var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _PICWebTable_activeRows_;
import { html, css, nothing } from 'lit';
import { property, query, queryAll, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { loadPage, SortTable, arrayDecoder, stringDecoder, booleanDecoder, customElement, fetchApiData, PIC, PICBase, PICCheckBox, PICAction, numberDecoder, objectDecoder, doSubmit, fetchContextData, toUserLocaleFloat, Util, stringDecoderLC, parseFormat, Position, parseLocaleNumber, } from '../Internals';
import { ifDefined } from 'lit/directives/if-defined.js';
/**
 * Handles throttling of user events
 * @param func function to throttle
 * @param timeout function will only be called once per `time` ms
 */
export const _throttle = (bind, func, timeout, preventDefault) => {
    const throttleFunc = (event) => {
        var _a;
        if (preventDefault)
            event.preventDefault();
        bind[func.name].bind(bind);
        const expireTimeCurr = (_a = __throttledFunctions.get(func)) !== null && _a !== void 0 ? _a : 0;
        const currTime = new Date().getTime();
        if (currTime > expireTimeCurr) {
            bind[func.name](event); //call to function happens here
            const expireTimeNew = new Date();
            expireTimeNew.setTime(currTime + timeout);
            __throttledFunctions.set(func, expireTimeNew.getTime());
        }
        else {
            //do nothing
        }
    };
    return throttleFunc;
};
const __throttledFunctions = new Map();
/**
 * Creates a function that will run after `timeout`ms.
 * If called before the timeout it will not trigger.
 * @param func
 * @param timeout
 * @param options
 * @returns
 */
export const _debounce = (func, timeout, options) => {
    const debounceFunc = (event) => {
        if (options === null || options === void 0 ? void 0 : options.preventDefault)
            event.preventDefault();
        if (options === null || options === void 0 ? void 0 : options.bind)
            options.bind[func.name].bind(options.bind);
        if (__debouncedFunctions.has(func)) {
            clearTimeout(__debouncedFunctions.get(func));
        }
        const timeoutFunc = () => {
            __debouncedFunctions.delete(func);
            if (options === null || options === void 0 ? void 0 : options.bind)
                options.bind[func.name](event);
            else
                func();
        };
        const timeoutId = window.setTimeout(timeoutFunc, timeout);
        __debouncedFunctions.set(func, timeoutId);
    };
    return debounceFunc;
};
const __debouncedFunctions = new Map();
const checkboxHeader = {
    type: 'checkbox',
    text: '',
    value: ':checkbox',
    internalIndex: -1,
    properties: {
        width: 'max-content'
    }
};
const treeHeader = {
    type: 'tree',
    text: '',
    value: ':tree',
    internalIndex: -2,
    properties: {
        width: 'max-content'
    }
};
const removalHeader = {
    type: 'removal',
    text: '',
    value: ':removal',
    internalIndex: -3,
    properties: {
        width: 'max-content'
    }
};
const moveHeader = {
    type: 'move',
    text: '',
    value: ':move',
    internalIndex: -3,
    properties: {
        width: 'max-content'
    }
};
const langObj = {
    engb: {
        startsWith: 'Starts with',
        contains: 'Contains',
        equals: 'Equals',
        notequals: 'Does not equal',
        notContains: 'Does not contain',
        notStartsWith: 'Does not start with',
        custom: 'Custom',
        greaterThan: 'Greater than',
        lessThan: 'Less than',
        between: 'Between...',
        and: 'and',
        select: 'Select...',
        selectAll: 'Select all',
        blank: '(Blank)',
        endsWith: 'Ends with'
    },
    dadk: {
        startsWith: 'Starter med',
        contains: 'Indeholder',
        equals: 'Lig med',
        notequals: 'Ikke lig med',
        notContains: 'Indeholder ikke',
        notStartsWith: 'Starter ikke med',
        custom: 'Brugerdefineret',
        greaterThan: 'Større end',
        lessThan: 'Mindre end',
        between: 'Mellem..',
        and: 'og',
        select: 'Vælg...',
        selectAll: 'Vælg alle',
        blank: '(Tom)',
        endsWith: 'Slutter med'
    },
    dede: {
        startsWith: 'Beginnt mit',
        contains: 'Enthält',
        equals: 'Ist gleich',
        notequals: 'Ist nicht gleich',
        notContains: 'Enthält nicht',
        notStartsWith: 'Beginnt nicht mit',
        custom: 'Benutzerdefiniert',
        greaterThan: 'Größer als',
        lessThan: 'Weniger als',
        between: 'Zwischen...',
        and: 'und',
        select: 'Auswählen...',
        selectAll: 'Alle auswählen',
        blank: '(Leer)',
        endsWith: 'Endet mit'
    }
};
let PICWebTable = class PICWebTable extends PICBase {
    get currentPageOffset() {
        //for when you apply a filter that reduces the maxPage amount below the currentPage amount - could be placed elsewhere
        if (this.currentPage > this.maxPages)
            this.currentPage = 1;
        return this.rowsPerPage * (this.currentPage - 1);
    }
    get maxPages() {
        return Math.ceil(this.visibleData.length / this.rowsPerPage);
    }
    get boundCanvasId() {
        var _a, _b;
        return (_b = (_a = this.boundCanvas) === null || _a === void 0 ? void 0 : _a.split(':')[0]) !== null && _b !== void 0 ? _b : '';
    }
    get boundCanvasColor() {
        var _a, _b;
        return (_b = (_a = this.boundCanvas) === null || _a === void 0 ? void 0 : _a.split(':')[1]) !== null && _b !== void 0 ? _b : 'green';
    }
    set alternateLineColor(iColor) {
        this._alternateLineColor = undefined;
        this._BGColor = 'var(--pic-surfaceColor, white)';
        if ((iColor === null || iColor === void 0 ? void 0 : iColor.trim()) === 'true') {
            this._alternateLineColor = 'var(--pic-tableAlternateRowColor, #f0f5fc)';
            this._BGColor = 'var(--pic-surfaceColor, white)';
        }
        else {
            try {
                let colorArray = iColor === null || iColor === void 0 ? void 0 : iColor.split(',');
                if (colorArray && colorArray.length > 1) {
                    this._alternateLineColor = colorArray[0];
                    this._BGColor = colorArray[1];
                }
            }
            catch (_a) {
            }
        }
        if (this._alternateLineColor)
            this.style.setProperty('--table-row-alternate-bgcolor', this._alternateLineColor);
        else
            this.style.removeProperty('--table-row-alternate-bgcolor');
        this.style.setProperty('--table-row-cell-bgcolor', this._BGColor);
        this.requestUpdate();
    }
    get alternateLineColor() {
        return this._alternateLineColor;
    }
    set scrollToRow(iRow) {
        if (iRow) {
            if (Array.isArray(iRow)) {
                this.scrollToRow_ = iRow;
            }
            else if (typeof iRow == 'number') {
                this.scrollToRow_ = [iRow, this.getRowIdxFromInternal(iRow)];
            }
            else {
                let tmpRow = this.visibleData.find(row => { var _a; return ((_a = row.__properties__) === null || _a === void 0 ? void 0 : _a.__submitValue__) == iRow; });
                this.scrollToRow_ = tmpRow ? [tmpRow.internalIndex, this.getRowIdxFromInternal(tmpRow.internalIndex)] : undefined;
            }
        }
        else {
            if (typeof iRow != 'number' && typeof iRow != 'string')
                this.scrollToRow_ = iRow;
        }
    }
    get scrollToRow() {
        return this.scrollToRow_;
    }
    //TODO: why is this not a function, is this even needed, what about the plain old visible headers?
    get actuallyVisibleHeaders() {
        let renderArr = this.visibleHeaders;
        if (this.saveToggledColumns && PIC.getInstance.storage.getItem('webTableToggledFilters-' + this.id_, 'internal')) { //changed from session for testing purposes
            let isSame = true;
            let savedHdrs = JSON.parse(PIC.getInstance.storage.getItem('webTableToggledFilters-' + this.id_, 'internal'), this.reviver);
            savedHdrs.forEach((hdr) => {
                var _a, _b, _c, _d;
                let candidate = renderArr.find((h) => h.value === hdr.value);
                if (!candidate) {
                    isSame = false;
                }
                else {
                    candidate.hidden = (_a = hdr.hidden) !== null && _a !== void 0 ? _a : false;
                    if ((((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.width) || ((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.minWidth)) && !((_d = candidate.properties) === null || _d === void 0 ? void 0 : _d.width)) {
                        if (!candidate.properties)
                            candidate.properties = {};
                        candidate.properties.width = hdr.properties.minWidth ? `${hdr.properties.minWidth}px` : hdr.properties.width;
                    }
                }
            });
            if (isSame) {
                renderArr.forEach((hdr) => {
                    if (!savedHdrs.find((h) => h.value === hdr.value))
                        hdr.hidden = true;
                });
                this.visibleHeaders = renderArr;
            }
        }
        let filteredArr = renderArr.filter(header => !header.hidden);
        return filteredArr;
    }
    set refreshTimer(iMilis) {
        if (iMilis) {
            this._refreshTimer = iMilis > 10000 ? iMilis : 10000;
        }
        else {
            this._refreshTimer = undefined;
        }
    }
    get refreshTimer() {
        return this._refreshTimer;
    }
    set height(iHeight) {
        let heightParser = '80vh';
        if (typeof iHeight == 'number' || iHeight.match(/^[0-9]*$/g))
            heightParser = iHeight + 'vh';
        else
            heightParser = iHeight;
        this._height = heightParser;
        this.requestUpdate();
    }
    get height() {
        return this._height;
    }
    /**
     * The main data setting of the table.
     * Add internal indexes to lists, set grid style and separate data properties
     */
    set data(data) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let hasHeaders = !!(data === null || data === void 0 ? void 0 : data.headers);
        let hasData = !!(data === null || data === void 0 ? void 0 : data.data);
        // let hasHidden: boolean = this.invisibleHeaders.length > 0
        const prvCtxHolder = this.data['contextholder'] || []; //to save this when using csv type
        if (!hasHeaders || !hasData || (data === null || data === void 0 ? void 0 : data.headers.length) == 0 && (data === null || data === void 0 ? void 0 : data.data.length) == 0) {
            console.warn('Faulty Data', data);
            this.visibleHeaders = [];
            this.visibleData = [];
            this.hasHeaderFilter = false;
            this.lastUpdated = new Date();
            if (this.dataType === 'CSV')
                this.responseTabledata = { headers: [], data: [], contextholder: prvCtxHolder };
            else
                this.responseTabledata = { headers: [], data: [] };
            return;
        }
        if (this.dataType === 'CSV' && prvCtxHolder.length > 0)
            data = Object.assign(Object.assign({}, data), { contextholder: prvCtxHolder });
        if (((_a = this.visibleHeaders[0]) === null || _a === void 0 ? void 0 : _a.value) === ':checkbox')
            this.visibleHeaders.shift();
        if (((_b = this.visibleHeaders[this.visibleHeaders.length - 1]) === null || _b === void 0 ? void 0 : _b.value) === ':removal')
            this.visibleHeaders.pop(); // this needs testing
        if (!this.keepSort) { //Allow sort to stay if used by fx commands
            this.sortTable = new SortTable(this.hasAttribute('maxSortSize') ? Number(this.getAttribute('maxSortSize')) : 2);
        }
        else
            this.checkedForSortColumn = false;
        //TODO: Check if filters are applied multiple times?
        for (let idx = 0; idx < data.headers.length; idx++) {
            let h = data.headers[idx];
            if (h.hidden == undefined)
                h.hidden = false;
            if (h.filter)
                this.activeFilters.filters.set(h.value, { value: '', filterType: h.filter });
            //Predefined filter
            if ((_c = h.properties) === null || _c === void 0 ? void 0 : _c.filterValues) {
                let jSonFilterValues = JSON.parse(h.properties.filterValues);
                let fType = jSonFilterValues.type;
                this.addActiveFilter(jSonFilterValues.value, h, fType);
            }
        }
        if (this.dataType === 'CSV' && typeof data.data === 'string') {
            data.data = data.data.replace(/'/g, '"');
            data = this.parseCsv2(data.data, data.headers, (_d = data.contextholder) !== null && _d !== void 0 ? _d : []);
        }
        data = data;
        //set initial checkmark if done from backend
        //happens here initially, then gets updated later so a reset doesn't overwrite everything again
        //TODO: check if this needs to be run for commands
        if (this.checkbox) {
            data.data.forEach(row => {
                var _a;
                if (Boolean((_a = row === null || row === void 0 ? void 0 : row.__properties__) === null || _a === void 0 ? void 0 : _a['checked'])) {
                    row.checked = true;
                }
            });
        }
        this.visibleHeaders = structuredClone(data.headers);
        this.visibleData = structuredClone(data.data);
        if (this.refreshTimer && this.toggledHeaders) {
            if (this.toggledHeaders != this.visibleHeaders)
                this.visibleHeaders = this.toggledHeaders;
        }
        this.hasHeaderFilter = !!data.headers.find(h => h.filter);
        if (this.tree && ((_e = this.visibleHeaders[1]) === null || _e === void 0 ? void 0 : _e.value) !== ':tree')
            this.visibleHeaders.unshift(treeHeader);
        if (this.checkbox && ((_f = this.visibleHeaders[0]) === null || _f === void 0 ? void 0 : _f.value) !== ':checkbox')
            this.visibleHeaders.unshift(checkboxHeader);
        if (this.addRemoveRows && !this.readOnly && ((_g = this.visibleHeaders[this.visibleHeaders.length - 1]) === null || _g === void 0 ? void 0 : _g.value) !== ':removal') {
            removalHeader.internalIndex = data.headers[data.headers.length - 1].internalIndex - 1;
            this.visibleHeaders.push(removalHeader);
        }
        if (this.dragndrop && !this.addRemoveRows && ((_h = this.visibleHeaders[this.visibleHeaders.length - 1]) === null || _h === void 0 ? void 0 : _h.value) !== ':move') {
            moveHeader.internalIndex = data.headers[data.headers.length - 1].internalIndex - 1;
            this.visibleHeaders.push(moveHeader);
        }
        PIC.getInstance.msg(data);
        this.responseTabledata = data;
        if ((this.activeFilters.filters.size > 0 || this.activeFilters.main) && this.hasUpdated) {
            this.triggerInput = true;
        }
        this.lastUpdated = new Date();
        //Moved this trigger to here from firstUpdated
        if (this.boundCanvas && this.checkbox)
            this.sendCheckedRowsToBoundCanvas();
        this.requestUpdate();
        if (!this.pagination)
            this.useLazyRender = this.data.data.length >= 1000; // TODO change this to 500 ?
        if (this.useLazyRender) {
            this.rowsPerPage = 100;
            this.lazyPage = [0, 99];
            if (PIC.getInstance.events.lazyTables.indexOf(this) == -1)
                PIC.getInstance.events.lazyTables.push(this);
        }
    }
    ;
    setDataAndKeepSort(data) {
        this.keepSort = true;
        this.data = data;
        this.keepSort = false;
    }
    parseCsv2(csvInput, headers, contextHolder) {
        const tableData = { data: [], headers: headers, contextholder: contextHolder };
        const options = {};
        options.newLine = csvInput.indexOf('\r\n') > -1 ? '\r\n' : '\n';
        const res = this.parseCSV(csvInput, options);
        for (let rows = 0; rows < res.data.length; rows++) {
            const row = res.data[rows];
            const tRow = this.parseCsvRow2TableRow(row, rows, headers);
            tableData.data.push(tRow);
        }
        return tableData;
    }
    //Other places need to call this function, if dataType is csv
    setCSVData(csvInput) {
        const tableData = { data: [], headers: this.data.headers };
        if (csvInput.indexOf('\n') === -1) { //if only header, then return
            this.data = tableData;
            return;
        }
        const options = {};
        options.newLine = csvInput.indexOf('\r\n') > -1 ? '\r\n' : '\n';
        const res = this.parseCSV(csvInput, options);
        for (let rows = 1; rows < res.data.length; rows++) {
            const row = res.data[rows];
            const tRow = this.parseCsvRow2TableRow(row, rows, this.data.headers);
            tableData.data.push(tRow);
        }
        this.data = tableData;
    }
    /* Used via commands, should be merged with setCSVData function */
    parseCsvResult2TableData(data) {
        const tableData = { data: [], headers: this.data.headers };
        for (let rows = 1; rows < data.data.length; rows++) {
            const row = data.data[rows];
            const tRow = this.parseCsvRow2TableRow(row, rows, this.data.headers);
            tableData.data.push(tRow);
        }
        return tableData;
    }
    parseCsvRow2TableRow(row, rowIndex, headers) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let tRow = { internalIndex: rowIndex, __properties__: {} };
        if (row[0] != null) {
            const [submitValue, ctxKey, isChecked, geometry, geometryColor, hiddenFilterValue, rowBGColor, rowClickEvent] = (row[0] + '').split('¨');
            if (submitValue != null)
                tRow.__properties__['__submitValue__'] = submitValue;
            if (ctxKey != null)
                tRow.__properties__['__contextId__'] = ctxKey;
            if (isChecked)
                tRow.checked = true;
            if (geometry != null)
                tRow.__properties__['geometry'] = geometry;
            if (geometryColor != null)
                tRow.__properties__['geometry_color'] = geometryColor;
            if (hiddenFilterValue != null) {
                try {
                    tRow.__properties__['hiddenFilterValue'] = JSON.parse(hiddenFilterValue);
                }
                catch (e) { }
            }
            if (rowBGColor != null)
                tRow.__properties__['bgcolor'] = rowBGColor;
            if (rowClickEvent != null)
                tRow.__properties__['__clickEvent__'] = rowClickEvent;
        }
        for (let col = 0; col < row.length - 1; col++) {
            const hdrName = (_a = headers[col]) === null || _a === void 0 ? void 0 : _a.value; //no *-header
            const cell = row[col + 1]; //skip *-value
            if (typeof cell === 'string' && cell.indexOf('¨') > -1) {
                const [cellValue, bgColor, color, cellClickEvent, sortValue] = cell.split('¨');
                tRow[hdrName] = {
                    value: cellValue !== null && cellValue !== void 0 ? cellValue : '',
                    type: (_c = (_b = headers[col]) === null || _b === void 0 ? void 0 : _b.type) !== null && _c !== void 0 ? _c : 'string',
                    properties: {
                        bgcolor: bgColor,
                        color: color,
                        type: (_f = (_e = (_d = headers[col]) === null || _d === void 0 ? void 0 : _d.properties) === null || _e === void 0 ? void 0 : _e.type) !== null && _f !== void 0 ? _f : 'string',
                        ['__clickEvent__']: cellClickEvent,
                    },
                    sortValue: sortValue
                };
            }
            else {
                tRow[hdrName] = { value: cell, type: (_h = (_g = headers[col]) === null || _g === void 0 ? void 0 : _g.type) !== null && _h !== void 0 ? _h : 'string' };
            }
        }
        return tRow;
    }
    //v2 parse CSV format from OpenEdge "Export" statement
    parseCSV(data, options) {
        //TODO: saveField(), saveRow(), errorHandling
        var _a, _b;
        const delimiter = ';';
        const quoteChar = (_a = options === null || options === void 0 ? void 0 : options.quoteChar) !== null && _a !== void 0 ? _a : '"';
        const escapeChar = '"';
        const quotes = true; //except for numbers, booleans, dates and null (?)
        const header = true;
        const nullChar = '?';
        const newLine = (_b = options === null || options === void 0 ? void 0 : options.newLine) !== null && _b !== void 0 ? _b : '\r\n'; ///\r?\n/
        const newLineLen = newLine.length;
        const quoteEscape = /""/g;
        const ESCAPE_TIMER = 2500;
        const TIME_NOW = new Date().getTime();
        const resData = {
            data: []
        };
        let cursor = 0, quoteCursor = 0;
        let nxtDelimiter = data.indexOf(delimiter, cursor);
        let nxtNewline = data.indexOf(newLine, cursor);
        let nxtQuote;
        //save field, save row
        let row = [];
        while (true) {
            if (cursor >= data.length)
                break;
            //Field opening quote
            if (data[cursor] === quoteChar) {
                quoteCursor = cursor + 1;
                while (true) {
                    nxtQuote = data.indexOf(quoteChar, quoteCursor);
                    //We reached the end
                    if (nxtQuote === -1) {
                        let field = data.substring(cursor + 1, quoteCursor - 1).replace(quoteEscape, '"');
                        row.push(field);
                        if ((nxtNewline < nxtDelimiter && nxtNewline !== -1) || (nxtNewline === -1 && nxtDelimiter === -1)) {
                            resData.data.push(row);
                            row = [];
                        }
                        cursor = quoteCursor + 1;
                        nxtDelimiter = data.indexOf(delimiter, cursor);
                        nxtNewline = data.indexOf(newLine, cursor);
                        break;
                    }
                    //Check if nxtQuote and nxtChar are both quotes
                    if (data[nxtQuote + 1] === quoteChar) {
                        if (nxtQuote - quoteCursor > 2)
                            quoteCursor = nxtQuote; //fixes off by one offset error ()
                        quoteCursor += 2;
                        continue; //not quite, needs to save the nxtQuote/quoteSearch index for reuse
                    }
                    //End of row --in here?
                    if (data[nxtQuote + 1] === newLine[0] || data[nxtQuote + 1] === undefined) {
                        let field = data.substring(cursor + 1, nxtQuote).replace(quoteEscape, '"');
                        row.push(field);
                        resData.data.push(row);
                        row = [];
                        cursor = nxtQuote + 1 + newLineLen;
                        nxtDelimiter = data.indexOf(delimiter, cursor);
                        nxtNewline = data.indexOf(newLine, cursor);
                        nxtQuote = data.indexOf(quoteChar, cursor);
                        break;
                    }
                    //Field closing quote
                    if (data[nxtQuote + 1] === delimiter) {
                        let field = data.substring(cursor + 1, nxtQuote).replace(quoteEscape, '"');
                        row.push(field);
                        cursor = nxtQuote + 2;
                        nxtDelimiter = data.indexOf(delimiter, cursor);
                        nxtNewline = data.indexOf(newLine, cursor);
                        break;
                    }
                }
                continue;
            }
            //Null from Progress is represented by a single unquoted '?'
            if (data[cursor] === nullChar) {
                row.push(null);
                cursor += 2;
                nxtDelimiter = data.indexOf(delimiter, cursor);
                nxtNewline = data.indexOf(newLine, cursor);
                continue;
            }
            //Numbers are unquoted too
            const numTest = Number(data[cursor]);
            if (!isNaN(numTest) || data[cursor] === '-') {
                // End of field: first of delimiter, newline, or end of data
                const idx2use = nxtDelimiter === -1
                    ? (nxtNewline === -1 ? data.length : nxtNewline)
                    : (nxtNewline === -1 ? nxtDelimiter : Math.min(nxtDelimiter, nxtNewline));
                let field = data.substring(cursor, idx2use);
                const numField = Number(field);
                //assume date, yyyy/mm/dd if < 1950 && > 2050, else yy/mm/dd, for some reason...
                //better to use string in progress
                if (isNaN(numField) && field.indexOf('/') > -1) { //(data[cursor + 2] === '/' || data[cursor + 4] === '/')
                    field = field.replaceAll('/', '');
                    const year = field.length === 6 ? Number(field.substring(0, 2)) : Number(field.substring(0, 4));
                    let yearVal = year + '';
                    if (year < 50)
                        yearVal = '20' + year;
                    else if (year > 50 && year < 100)
                        yearVal = '19' + year;
                    const dateVal = year + field.substring(field.length === 6 ? 2 : 4);
                    row.push(dateVal);
                }
                else
                    row.push(Number(field)); //errHandling?
                const atEndOfRow = nxtNewline !== -1 && (nxtDelimiter === -1 || nxtNewline < nxtDelimiter);
                const atEndOfData = nxtNewline === -1 && nxtDelimiter === -1;
                if (atEndOfRow || atEndOfData) {
                    resData.data.push(row);
                    row = [];
                }
                if (nxtNewline === -1) {
                    break;
                }
                cursor = atEndOfRow ? nxtNewline + newLineLen : nxtDelimiter + 1;
                nxtDelimiter = data.indexOf(delimiter, cursor);
                nxtNewline = data.indexOf(newLine, cursor);
                continue;
            }
            //Booleans are also unquoted yes/no
            if (data[cursor] === 'Y' || data[cursor] === 'N' || data[cursor] === 'y' || data[cursor] === 'n') {
                let idx2use = nxtDelimiter < nxtNewline ? nxtDelimiter : nxtNewline; //fix unquoted end fields (newline)
                if (idx2use === -1)
                    idx2use = data.length;
                let field = data.substring(cursor, idx2use);
                row.push(field === 'yes');
                if (nxtNewline < nxtDelimiter || nxtNewline === -1) {
                    resData.data.push(row);
                    row = [];
                }
                if (nxtDelimiter === -1 || nxtNewline === -1) {
                    break;
                }
                cursor = Math.min(nxtDelimiter, nxtNewline) + 1;
                nxtDelimiter = data.indexOf(delimiter, cursor);
                nxtNewline = data.indexOf(newLine, cursor);
                continue;
            }
            //No more lines / end of data -> stop
            if (nxtNewline === -1 && nxtDelimiter === -1) {
                break;
            }
        }
        return resData;
    }
    resetToOriginalData() {
        let origClone = structuredClone(this.data.data);
        if (this.checkbox) {
        }
    }
    /**
     * Get submit values for while table > every row that has been checked by a checkbox.
     * Rows must have a submitValue property to be eligible or '*'.
     * @returns checked values as comma separated string. Returns the empty string if no checkbox header or no checked rows.
     */
    get submitValue() {
        var _a;
        if (!this.visibleHeaders[0])
            return '';
        if (((_a = this.visibleHeaders[0]) === null || _a === void 0 ? void 0 : _a.value) !== ':checkbox')
            return this.selectedRowValues; //if no checkbox, return selected ... ?
        return this.visibleData.filter(data => data.checked).map(data => {
            var _a, _b;
            return ((_a = data.__properties__) === null || _a === void 0 ? void 0 : _a.__submitValue__) ? (_b = data.__properties__) === null || _b === void 0 ? void 0 : _b.__submitValue__ : '';
        }).join('^').replace(/,*$/, '');
    }
    /**
     * Returns a string formatted like row~cell\x03cell^...
     * To return the value of all cells, when you have set the table to be editable
     */
    get inputValues() {
        if (this.editable) { //if editable, return all values of cells
            return this.visibleData.map(data => {
                var _a, _b, _c, _d, _e, _f, _g, _h;
                let submitline = ((_b = (_a = data.__properties__) === null || _a === void 0 ? void 0 : _a.__submitValue__) !== null && _b !== void 0 ? _b : '') + '~';
                for (let i = 0; i < this.actuallyVisibleHeaders.length; i++) {
                    let cell = data[this.actuallyVisibleHeaders[i].value];
                    if (cell != null) {
                        let isValidCellType = ((_c = cell.properties) === null || _c === void 0 ? void 0 : _c.type) == 'date' || ((_d = cell.properties) === null || _d === void 0 ? void 0 : _d.type) == 'datetime' || ((_e = cell.properties) === null || _e === void 0 ? void 0 : _e.type) == 'input'
                            || ((_f = cell.properties) === null || _f === void 0 ? void 0 : _f.type) == 'number' || ((_g = cell.properties) === null || _g === void 0 ? void 0 : _g.type) == 'string' || ((_h = cell.properties) === null || _h === void 0 ? void 0 : _h.type) == 'time'
                            || (typeof cell.properties === 'undefined' && (cell.type === 'string' || cell.type === 'number'));
                        if (isValidCellType) {
                            submitline += cell.value;
                        }
                        if (i !== this.actuallyVisibleHeaders.length - 1)
                            submitline += String.fromCharCode(3);
                    }
                }
                return submitline;
            }).join('^');
        }
        return null;
    }
    /**
     * @returns rows that are currently selected / highlighted / active. Different from submitvalues where they have to be checked
     */
    get selectedRowValues() {
        return this.activeRows.map(row => {
            var _a, _b;
            const data = this.visibleData.find(item => item.internalIndex === row[0]);
            if (!data)
                return '';
            return ((_a = data.__properties__) === null || _a === void 0 ? void 0 : _a.__submitValue__) ? (_b = data.__properties__) === null || _b === void 0 ? void 0 : _b.__submitValue__ : '';
        }).join('^').replace(/,*$/, '');
    }
    get data() {
        return this.responseTabledata;
    }
    /**
     * Set just the headers of a table. Use to init a table before data has arrived.
     */
    set dataHeaders(value) {
        this.data = { headers: value, data: this.visibleData };
    }
    /**
     * Set just the data of the table. Use to add/refresh/load data after table has been initialised
     */
    set dataDataset(value) {
        if (this.visibleHeaders.length === 0)
            throw new Error('Cannot add data to table without header');
        this.data = { headers: this.visibleHeaders, data: value };
    }
    /**
     * Load data into the table,
     * @param src TableData object or url that returns a TableData json struct
     * @returns
     */
    async loadData(src) {
        if (typeof src === 'object') {
            this.responseTabledata = src; //data or tabledata?
        }
        else if (typeof src === 'string') {
            this.keepSort = true;
            let options = {
                // sessionId: PIC.sessionCode,
                systemId: PIC.systemId,
                errorReturn: true,
                formatOutput: 'json',
                postBody: {
                    mode: 'getData'
                },
            };
            if (this.dataType === 'CSV')
                options.formatOutput = 'text';
            if (this.useRest) {
                options.path = src;
                options.method = 'GET';
                delete options.postBody;
                delete options.systemId;
                options.restVersion = 'v1';
                src = '';
                if (options.path.indexOf('=#') > -1) {
                    options.path = await this.getQueryParmsWithFields(options.path);
                }
            }
            else if (src.indexOf('=#') > -1) {
                src = await this.getQueryParmsWithFields(src);
            }
            this.isLoading = true;
            let resetVisibility = false;
            if (this.filterRowIsVisible && this.visibleData.length === 0) {
                resetVisibility = true;
                this.filterRowIsVisible = false;
            }
            let overlay = null;
            if (this.visibleData.length > 0) {
                overlay = document.createElement('div');
                overlay.classList.add('pic-loadingicon');
                overlay.style.position = 'absolute';
                overlay.style.top = '0';
                overlay.style.left = '0';
                overlay.style.width = '100%';
                overlay.style.height = '100%';
                this.gridBody.appendChild(overlay);
            }
            if (this.isConnected) {
                fetchApiData(src, options).then(data => {
                    if (!!data[this.dataRootName]) {
                        this.data = data[this.dataRootName];
                    }
                    else if (this.dataType === 'CSV') {
                        this.setCSVData(data);
                    }
                    else
                        this.data = data;
                    if (this.refreshTimer) {
                        PIC.getInstance.setTimeout(this.getData.bind(this, true), this.refreshTimer);
                    }
                    this.isLoading = false;
                    if (overlay)
                        overlay.remove();
                    if (resetVisibility) {
                        this.filterRowIsVisible = true;
                    }
                    if (this.filterRowIsVisible) {
                        if (this.activeFilters.filters.size > 0) {
                            this.dispatchEvent(new CustomEvent('loadsavedfilters', { detail: { value: 'filters' } }));
                        }
                    }
                    if (this.activeFilters.main.length > 0) {
                        this.dispatchEvent(new CustomEvent('loadsavedfilters', { detail: { value: 'main' } }));
                    }
                    if (!this.autowidth) {
                        this.setHdrMinWidths().then(() => {
                            if (this.autoFitTable && !this._autoFitHeight) {
                                this.handleAutoFit(true);
                            }
                            else
                                this.handleAutoFit();
                        });
                    }
                    else {
                        if (this.autoFitTable && !this._autoFitHeight) {
                            this.updateComplete.then(() => this.handleAutoFit(true));
                        }
                        else {
                            this.updateComplete.then(() => this.handleAutoFit());
                        }
                    }
                }).catch(err => {
                    var _a, _b;
                    console.log(err);
                    this.dataLoadError = true;
                    let backupHeaders = (_b = (_a = this.data) === null || _a === void 0 ? void 0 : _a.headers) !== null && _b !== void 0 ? _b : undefined;
                    this.data = { headers: backupHeaders !== null && backupHeaders !== void 0 ? backupHeaders : [], data: [] };
                    this.isLoading = false;
                    if (overlay)
                        overlay.remove();
                    if (resetVisibility) {
                        this.filterRowIsVisible = true;
                    }
                    this.requestUpdate();
                    if (this.autoFitTable && !this._autoFitHeight) {
                        this.handleAutoFit(true);
                    }
                    else
                        this.handleAutoFit();
                    if (!this.autowidth)
                        this.setHdrMinWidths();
                })
                    .finally(() => {
                    this.keepSort = false;
                });
            }
            else {
                return Promise.reject('table is disconnected');
            }
        }
        else {
            this.isLoading = false;
            throw new Error(`Error. Trying to load data that is neither tabledata nor a url`);
        }
        return true;
    }
    async waitForFieldReady(fieldId, timeoutMs = 500, pollIntervalMs = 20) {
        const normalizedFieldId = fieldId.toLowerCase();
        const timeoutAt = Date.now() + timeoutMs;
        while (Date.now() < timeoutAt) {
            const fieldRef = PIC.getInstance.picInputs.get(normalizedFieldId);
            if (fieldRef) {
                try {
                    await fieldRef.updateComplete;
                }
                catch (error) {
                    PIC.getInstance.msg('PICWebTable waitForFieldReady updateComplete failed', normalizedFieldId, error);
                }
                await new Promise(resolve => requestAnimationFrame(() => resolve()));
                return fieldRef;
            }
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
        }
        return null;
    }
    async getQueryParmsWithFields(url) {
        var _a;
        if (url.indexOf('?') < 0)
            return url;
        const queryPath = url.substring(url.indexOf('?'));
        const queryParams = new URLSearchParams(queryPath);
        let encodedParams = '';
        for (const [key, value] of queryParams.entries()) {
            if (value.startsWith('#')) {
                const fieldId = value.substring(1);
                const field = await this.waitForFieldReady(fieldId);
                let fldVal = '';
                if (field) {
                    try {
                        fldVal = (_a = field.submitValue) !== null && _a !== void 0 ? _a : '';
                        fldVal = escape(fldVal);
                    }
                    catch (error) {
                        PIC.getInstance.msg('PICWebTable getQueryParmsWithFields submitValue failed', fieldId, error);
                    }
                }
                else {
                    PIC.getInstance.msg('PICWebTable getQueryParmsWithFields timed out waiting for field', fieldId);
                }
                queryParams.set(key, fldVal);
                if (fldVal.length > 0)
                    encodedParams += key + '=' + fldVal + '&';
            }
        }
        if (encodedParams.length > 0)
            encodedParams = encodedParams.substring(0, encodedParams.length - 1);
        return url.substring(0, url.indexOf('?')) + '?' + encodedParams;
    }
    addDebouncedFunction(xfunc, timeout) {
        if (this.debouncedFunctions.has(xfunc.name))
            clearTimeout(this.debouncedFunctions.get(xfunc.name));
        const timeoutFunc = () => {
            this.debouncedFunctions.delete(xfunc.name);
            xfunc();
        };
        const timeoutID = window.setTimeout(timeoutFunc, timeout);
        this.debouncedFunctions.set(xfunc.name, timeoutID);
    }
    constructor() {
        super();
        this.responseTabledata = { headers: [], data: [] };
        /* Viewed */
        this.visibleHeaders = [];
        this.invisibleHeaders = [];
        this.orderHelper = [];
        this.visibleData = [];
        this.tableTitle = 'Table Title';
        this.lastUpdated = new Date();
        this.filterRowIsVisible = false;
        this.hasHeaderFilter = false; //false in no headers has the 'filterable' property
        this.activeFilters = { main: '', filters: new Map() };
        this.checkedItems = [];
        this.debouncedFunctions = new Map();
        this.eventListeners = [];
        this.dataLoadError = false;
        this.triggerInput = false;
        this.savedFiltersApplied = false;
        this.mousePosition = [];
        this.clickedSide = 'test';
        this.toggledHeaders = null;
        this.rowAdded = false;
        this.checkedForSortColumn = false;
        this.forceScroll = false;
        this.scrolledToBottom = false;
        this.useLazyRender = false;
        this.scrollDiff = 10;
        this.scrollThumb = { height: 10, minimum: false };
        this.computedDataHeight = { data: 0, header: 0 };
        this.cellCompactMode = 'cozy';
        this.selectedHeader = null;
        this.settingsOpen = false;
        this._autoFitHeight = undefined;
        this._autoFitWidth = undefined;
        this.doDeferredLazyScrollUpdate = false; // Ensures lazy scrollbar length is recalculated + re-rendered after final initial render ( when inlineData + autofittable)
        this._scrollLeft = 0;
        //active rows
        _PICWebTable_activeRows_.set(this, []);
        this.activeEndRow = null;
        this.checkbox = false;
        this.tree = false;
        this.dragndrop = false;
        this.filter = 'Single';
        this.infofooter = true;
        this.sumfooter = true;
        this.hideUpdateTimestamp = false;
        this.hideRowCount = false;
        this.infoheader = true;
        this.sort = true;
        this.src = '';
        this.allowNewRows = false;
        this.groups = 'Group 1, Group 2, Group 3';
        this.search = true;
        this.compactMode = false;
        this.rowDensity = null;
        this.compactHeaderMode = false;
        this.highVisibilityHeaders = false;
        this.export = ['Excel', 'PDF', 'CSV', 'CSVC'];
        this.dataRootName = 'Booking';
        this.saveFilters = false;
        this.autowidth = false;
        this.selectionMode = 'multi';
        this.filterEnabled = true;
        this.showRowSelectedCount = true;
        this.useCheckAsMarked = false;
        this.saveToggledColumns = false;
        this.useRest = false;
        this.resizeColumns = true;
        this.editable = false;
        this.readOnly = false;
        this.addRemoveRows = false;
        this.valuesFilters = '';
        this.filterType = '';
        this.filterEnable = '';
        this.dataType = 'standard'; //standard being the table.cls format
        this.allowDblclick = false;
        this.hideheadertooltip = false;
        this.pagination = false;
        this.whitespace = false;
        this.autoFitTable = false;
        this.isLookUp = false;
        this.minheight = '';
        this.rowsPerPage = 100;
        this.currentPage = 1;
        this.lazyPage = [0, 0];
        this.scrollToRow_ = null;
        this._height = '80vh';
        this.keepSort = false;
        this.isFirefox = undefined;
        this.heightParent = null;
        this.resizeObserved = false;
        this.widthObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentRect.width > 100) {
                    this.resizeObserved = true;
                    if (this.saveFilters) {
                        let ref = this;
                        let timer = setTimeout(() => {
                            ref.requestUpdate();
                            clearTimeout(timer);
                        }, 1000);
                    }
                    else
                        this.requestUpdate();
                }
            }
        });
        this.thumbTop = 0;
        this.tableTop = undefined;
        this.handleDragThumbMove = (e) => {
            var _a, _b, _c;
            if (this.tableTop === undefined)
                this.tableTop = (_a = this.gridTable) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top;
            let totalHeaderHeight = this.getTotalHeaderHeight();
            let adjustedTableTop = this.tableTop + totalHeaderHeight;
            let top = e.clientY - adjustedTableTop;
            let thumb = this.scrollThumb.minimum ? 10 : this.scrollThumb.height;
            // Constrain to valid range: 0 to (clientHeight - totalHeaderHeight - thumb)
            if (e.clientY <= adjustedTableTop)
                top = 0;
            if (e.clientY >= (this.tableTop + ((_b = this.gridTable) === null || _b === void 0 ? void 0 : _b.clientHeight)) - thumb)
                top = (((_c = this.gridTable) === null || _c === void 0 ? void 0 : _c.clientHeight) - totalHeaderHeight) - thumb;
            this.thumbTop = top;
            this.lazyThumb.style.top = this.thumbTop + 'px';
        };
        this.handleDragThumbDrop = (e) => {
            let totalHeaderHeight = this.getTotalHeaderHeight();
            let scrollLength = this.gridTable.clientHeight - totalHeaderHeight;
            if (this.scrollThumb.minimum)
                scrollLength -= (10 - this.scrollThumb.height);
            let fraction = this.thumbTop / scrollLength;
            let start = fraction * this.visibleData.length;
            let shownRows = Math.round((this.gridTable.clientHeight - totalHeaderHeight) / this.computedDataHeight.data);
            let topMostVisibleRow = Math.round(start);
            this.lazyPage[0] = Math.round(topMostVisibleRow + (shownRows / 2) - 50); // finds the middle of the visible rows, and then goes up half the rows per page so that the visible rows are centered
            this.lazyPage[1] = this.lazyPage[0] + 99;
            this.forceScroll = true;
            if (this.lazyPage[0] < 0) {
                this.lazyPage[0] = 0;
                this.lazyPage[1] = this.lazyPage[0] + 99;
            }
            if (this.lazyPage[1] > this.visibleData.length - 1) {
                this.lazyPage[1] = this.visibleData.length - 1;
                this.lazyPage[0] = this.lazyPage[1] - 99;
            }
            this.scrollDiff = topMostVisibleRow - this.lazyPage[0];
            if (this.lazyPage[1] >= this.visibleData.length - 1)
                this.scrolledToBottom = true;
            this.tableTop = undefined;
            window.removeEventListener('mousemove', this.handleDragThumbMove);
            window.removeEventListener('mouseup', this.handleDragThumbDrop);
            this.requestUpdate();
        };
        this.thumbLeftToDragPoint = 0;
        this.handleHDragMove = (e) => {
            let xCoord = Math.max(0, Math.min(this.gridTable.scrollWidth - this.gridTable.clientWidth, e.clientX - this.thumbLeftToDragPoint));
            let yCoord = this.gridTable.scrollTop;
            this.gridTable.scrollTo(xCoord, yCoord);
        };
        this.handleHDragDrop = (e) => {
            this.ffHScrollThumb.classList.remove('dragging');
            window.removeEventListener('mousemove', this.handleHDragMove);
            window.removeEventListener('mouseup', this.handleHDragDrop);
        };
        this.resizeDragHandler = (e) => {
            if (this.overlay) {
                let oldWidth = parseInt(getComputedStyle(this.overlay).width);
                this.overlay.style.width = (oldWidth - (this.overlay.getBoundingClientRect().right - e.clientX)).toString() + 'px';
            }
        };
        this.resizeDropHandler = (e) => {
            var _a;
            let newWidth = parseInt(getComputedStyle(this.overlay).width);
            let headerIndex = (_a = this.overlay) === null || _a === void 0 ? void 0 : _a.dataset.headerIndex;
            if (headerIndex) {
                let header = this.actuallyVisibleHeaders.find(h => { var _a; return ((_a = h.internalIndex) === null || _a === void 0 ? void 0 : _a.toString()) === headerIndex; });
                if (header) {
                    if (!header.properties)
                        header.properties = {};
                    header.properties.width = `${newWidth}px`;
                }
            }
            this.deleteResizeOverlay();
            window.removeEventListener('mousemove', this.resizeDragHandler);
            window.removeEventListener('mouseup', this.resizeDropHandler);
            this.requestUpdate();
        };
        this.dragging = false;
        // doubleclick as multiselectiong and 1 click is only makring one row
        this.clickCount = 0;
    }
    /* *********** LIFECYCLE HOOKS ******************/
    connectedCallback() {
        var _a;
        super.connectedCallback();
        PIC.getInstance.msg('CONNECTED CALLBACK');
        // this.parseFeatures()
        this.lastUpdated = new Date();
        if (this.hasAttribute('name'))
            this.tableTitle = (_a = this.getAttribute('name')) !== null && _a !== void 0 ? _a : ''; //default table title
        this.sortTable = new SortTable(this.hasAttribute('maxSortSize') ? Number(this.getAttribute('maxSortSize')) : 2);
        if (this.saveFilters) {
            if (PIC.getInstance.storage.getItem('webTable-SavedFilters-' + this.id_, 'internal')) {
                this.activeFilters = JSON.parse(PIC.getInstance.storage.getItem('webTable-SavedFilters-' + this.id_, 'internal'), this.reviver);
            }
            this.filterRowIsVisible = true;
        }
        this.setAttribute('tabIndex', '0');
        this.addListeners();
        this.checkAndConsumeInlineData();
        this.getData();
        window.PIC.addInput(this.id_, this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        PIC.getInstance.msg('DISCONNECTED CALLBACK CALLED');
        this.refreshTimer = undefined;
        window.PIC.removeInput(this.id_);
        this.removeListeners();
    }
    setScrollSize() {
        var _a, _b, _c, _d, _e;
        let cellHeight = (_c = (_b = (_a = this.gridBody.querySelectorAll('.grid-row')[0]) === null || _a === void 0 ? void 0 : _a.querySelector('.grid-cell')) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) !== null && _c !== void 0 ? _c : 0;
        if (cellHeight < 0)
            cellHeight += 1;
        if (this.gridTableHeader)
            this.computedDataHeight.header = (_e = (_d = this.gridTableHeader[0]) === null || _d === void 0 ? void 0 : _d.offsetHeight) !== null && _e !== void 0 ? _e : 0;
        this.computedDataHeight.data = cellHeight;
        let totalHeaderHeight = this.getTotalHeaderHeight();
        let percentage = (this.gridTable.clientHeight - totalHeaderHeight) / (cellHeight * this.visibleData.length);
        this.scrollThumb.height = this.gridTable.clientHeight * percentage;
        if (this.scrollThumb.height < 10)
            this.scrollThumb.minimum = true;
        else
            this.scrollThumb.minimum = false;
    }
    resetAutoFitHeight() {
        this._autoFitHeight = undefined;
    }
    async handleAutoFit(doAutoHeight = false) {
        await this.updateComplete.then(() => {
            let ref = this;
            let timer = setTimeout(() => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
                let modalSettingsElm = ((_b = (_a = ref.heightParent) === null || _a === void 0 ? void 0 : _a.nextElementSibling) === null || _b === void 0 ? void 0 : _b.id) === '__modalsettings__' ? (_c = ref.heightParent) === null || _c === void 0 ? void 0 : _c.nextElementSibling : null;
                let modalBody = undefined;
                let modalHeightProp = undefined;
                let modalWidthProp = undefined;
                if (ref.heightParent && modalSettingsElm) {
                    let modalSettings = JSON.parse((modalSettingsElm === null || modalSettingsElm === void 0 ? void 0 : modalSettingsElm.value).replace(new RegExp("'", 'g'), '"'));
                    let subForm = (((_d = ref.heightParent.parentElement) === null || _d === void 0 ? void 0 : _d.nodeName) === 'FORM' && ((_e = ref.heightParent.parentElement) === null || _e === void 0 ? void 0 : _e.id) === 'PICSubForm') ? ref.heightParent.parentElement : null;
                    if (modalSettings && 'height' in modalSettings && modalSettings['height'].length > 0)
                        modalHeightProp = modalSettings['height'];
                    if (modalSettings && 'width' in modalSettings && modalSettings['width'].length > 0)
                        modalWidthProp = modalSettings['width'];
                    if (subForm) {
                        let sessionCode = (_f = subForm.querySelector('input[name=SessionCode]')) === null || _f === void 0 ? void 0 : _f.value;
                        if (sessionCode && sessionCode.length > 0)
                            modalBody = (_l = (_k = (_j = (_h = (_g = PIC.getInstance.PICWinMap.get(sessionCode)) === null || _g === void 0 ? void 0 : _g.modal) === null || _h === void 0 ? void 0 : _h.shadowRoot) === null || _j === void 0 ? void 0 : _j.querySelector('.modal-body')) === null || _k === void 0 ? void 0 : _k.getBoundingClientRect()) !== null && _l !== void 0 ? _l : undefined;
                    }
                }
                let tableTop = Math.max(ref.getBoundingClientRect().top, 0);
                let tableLeft = Math.max(ref.getBoundingClientRect().left, 0);
                let footerHeight = (_p = (_o = (_m = ref.shadowRoot) === null || _m === void 0 ? void 0 : _m.querySelector('.info-footer')) === null || _o === void 0 ? void 0 : _o.getBoundingClientRect().height) !== null && _p !== void 0 ? _p : 0;
                let headerHeight = (_s = (_r = (_q = ref.shadowRoot) === null || _q === void 0 ? void 0 : _q.querySelector('.info-header')) === null || _r === void 0 ? void 0 : _r.getBoundingClientRect().height) !== null && _s !== void 0 ? _s : 0;
                if (modalBody) {
                    modalHeightProp = modalBody.height + 'px';
                    modalWidthProp = (modalBody.width - 28) + 'px';
                    tableTop = tableTop - modalBody.top;
                    tableLeft = tableLeft - modalBody.left;
                }
                let tabParent = (_u = (_t = ref.heightParent) === null || _t === void 0 ? void 0 : _t.classList) === null || _u === void 0 ? void 0 : _u.contains('tabBody');
                if (doAutoHeight) {
                    let availableHeight = modalHeightProp ? modalHeightProp : ((_v = ref.heightParent) === null || _v === void 0 ? void 0 : _v.getBoundingClientRect().height) ? ((_w = ref.heightParent) === null || _w === void 0 ? void 0 : _w.getBoundingClientRect().height) + 'px' : window.innerHeight + 'px';
                    ref._autoFitHeight = `calc(${availableHeight} - ${(tableTop + footerHeight + headerHeight + (tabParent ? 16 : 28))}px)`; // added 16/28px to account for padding on body (top and bottom)
                }
                let availableWidth = tabParent ? (ref.heightParent.getBoundingClientRect().width - 16) + 'px' :
                    modalWidthProp ? modalWidthProp :
                        window.innerWidth - (tableLeft + 14) + 'px';
                ref._autoFitWidth = availableWidth;
                ref.requestUpdate();
                clearTimeout(timer);
                if (ref.doDeferredLazyScrollUpdate) {
                    ref.doDeferredLazyScrollUpdate = false;
                    ref.updateComplete.then(() => ref.requestUpdate());
                }
            }, 10);
        });
    }
    updated() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (this.triggerInput) {
            this.headerFilters.forEach(elm => {
                elm.dispatchEvent(new Event('input'));
            });
            this.triggerInput = false;
        }
        if (this.useLazyRender) {
            if (this.isFirefox === undefined)
                this.isFirefox = (_b = (_a = PIC.getInstance.browser) === null || _a === void 0 ? void 0 : _a.includes('Firefox')) !== null && _b !== void 0 ? _b : false;
            if (this.isFirefox)
                (_c = this.gridTable) === null || _c === void 0 ? void 0 : _c.classList.add('firefox');
            (_d = this.gridTable) === null || _d === void 0 ? void 0 : _d.classList.add('hasLazyScrollbar');
            this.setScrollSize();
            if (this.forceScroll) {
                let iIndex = this.visibleData[this.lazyPage[0] + this.scrollDiff].internalIndex;
                if (this.scrolledToBottom)
                    iIndex = this.visibleData[this.visibleData.length - 1].internalIndex;
                const row = (_e = this.shadowRoot) === null || _e === void 0 ? void 0 : _e.querySelector(`.grid-row.row-${iIndex} > *`);
                let tableTop = this.gridTableHeader.length > 0 ? this.gridTable.offsetTop + this.getTotalHeaderHeight() : this.gridTable.offsetTop;
                if (row) {
                    if (this.scrolledToBottom) {
                        row.scrollIntoView(false);
                        this.scrolledToBottom = false;
                    }
                    else
                        this.gridTable.scrollTo({
                            top: row.offsetTop - tableTop
                        });
                }
                this.forceScroll = false;
            }
        }
        else
            (_f = this.gridTable) === null || _f === void 0 ? void 0 : _f.classList.remove('hasLazyScrollbar');
        if (this.resizeObserved) {
            this.resizeObserved = false;
            this.widthObserver.disconnect();
        }
        let widthHeaders = this.actuallyVisibleHeaders.filter(hdr => { var _a; return (_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.width; });
        if (widthHeaders.length > 0 && this.filterRowIsVisible)
            this.calcFilterWidthsFromHeaders(widthHeaders);
        if (this.scrollToRow) {
            const row = (_g = this.shadowRoot) === null || _g === void 0 ? void 0 : _g.querySelector(`.grid-row.row-${this.scrollToRow[0]} > *`);
            if (row) {
                this.isVisible(row).then(isVisible => {
                    if (!isVisible) {
                        this.gridTable.scrollTo({
                            top: row.offsetTop - this.gridTable.offsetTop - 35,
                            behavior: 'smooth'
                        });
                    }
                });
            }
            this.scrollToRow = null;
        }
        if (this.savedFiltersApplied && this.filterRowIsVisible && this.saveFilters) {
            let filterButton = (_h = this.shadowRoot) === null || _h === void 0 ? void 0 : _h.querySelector('.tableFiltersButton');
            if (filterButton)
                filterButton.classList.add('selected');
        }
        if (!this.checkedForSortColumn) {
            if (this.gridTable) {
                const newSortArray = new Array(this.data.headers.length);
                this.actuallyVisibleHeaders.forEach(hdr => {
                    var _a;
                    if ((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.columnsort) {
                        const colSort = JSON.parse(hdr.properties.columnsort);
                        newSortArray[colSort.seqno - 1] = [hdr.value, colSort.direction];
                    }
                });
                //reverse the array to get the correct sort order
                newSortArray.filter(x => x !== undefined).reverse().forEach(x => this.sortTable.addSort(x[0], x[1]));
                this.visibleHeaders.forEach(h => {
                    let sortItem = this.sortTable.find(h.value);
                    if (sortItem != null)
                        h.currSort = sortItem.sortDir;
                    else
                        h.currSort = '';
                });
                this.sortDataBySortTable();
                this.checkedForSortColumn = true;
            }
        }
        if (this.addRemoveRows && this.editable && this.boundCanvas && this.rowAdded) {
            this.rowAdded = false;
            this.sendEditableFieldsToBoundCanvas();
        }
        else
            return;
    }
    firstUpdated(_changedProperties) {
        if (this.editable && this.boundCanvas)
            this.sendEditableFieldsToBoundCanvas();
        if (this.boundCanvas && this.checkbox)
            this.sendCheckedRowsToBoundCanvas();
        this.heightParent = this.getParentForHeight(this);
        if (this.compactHeaderMode)
            this.style.setProperty('--filter-row-top', '1.8rem');
    }
    getParentForHeight(child) {
        let parent = child.parentElement;
        if (parent) {
            if (child.classList.contains('tabBody') && parent.classList.contains('body'))
                return parent; // if in tab
            if (['modal-body', 'pic-mainbody'].includes(parent.classList[0]))
                return parent; // if in main view or modal/getans
            let grandparent = this.getParentForHeight(parent);
            if (grandparent)
                return grandparent;
        }
        return null;
    }
    //util function
    isVisible(domElement) {
        return new Promise(resolve => {
            const o = new IntersectionObserver(([entry]) => {
                resolve(entry.intersectionRatio === 1);
                o.disconnect();
            });
            o.observe(domElement);
        });
    }
    removeListeners() {
        for (const [type, event] of this.eventListeners) {
            this.removeEventListener(type, event);
        }
        this.eventListeners = [];
    }
    addListeners() {
        //make the err lines go away by adding this file to tsconfig
        // only for use in omni search field
        const searchContains = (a, val) => {
            for (const [key, data] of Object.entries(a)) {
                if (typeof data !== 'object')
                    continue;
                try {
                    if (data.value.toString().toLowerCase().indexOf(val.toLowerCase()) > -1)
                        return true;
                }
                catch (_a) { }
            }
            return false;
        };
        // For usage in the single colum search
        const startsWith = (a, val, hdr) => {
            var _a, _b, _c;
            let cell = a[hdr.value];
            if (cell && typeof cell === 'object') {
                let hasMatch = false;
                let curTxt = cell.value.toString();
                if (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date')
                    curTxt = PIC.getInstance.userConfig.formatDate(cell.value);
                if (((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type) === 'datetime')
                    curTxt = PIC.getInstance.userConfig.formatDateTime(new Date(cell.value));
                if (((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.type) === 'time')
                    curTxt = PIC.getInstance.userConfig.formatTime(cell.value);
                val.split('|').forEach(option => {
                    if (curTxt.toLowerCase().startsWith(option.toLowerCase()))
                        hasMatch = true;
                });
                return hasMatch;
            }
            return false;
        };
        const endsWith = (a, val, hdr) => {
            var _a, _b, _c;
            let cell = a[hdr.value];
            if (cell && typeof cell === 'object') {
                let hasMatch = false;
                let curTxt = cell.value.toString();
                if (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date')
                    curTxt = PIC.getInstance.userConfig.formatDate(cell.value);
                if (((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type) === 'datetime')
                    curTxt = PIC.getInstance.userConfig.formatDateTime(new Date(cell.value));
                if (((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.type) === 'time')
                    curTxt = PIC.getInstance.userConfig.formatTime(cell.value);
                val.split('|').forEach(option => {
                    if (curTxt.toLowerCase().endsWith(option.toLowerCase()))
                        hasMatch = true;
                });
                return hasMatch;
            }
            return false;
        };
        const contains = (a, val, hdr) => {
            var _a, _b, _c;
            let cell = a[hdr.value];
            if (cell && typeof cell === 'object') {
                let hasMatch = false;
                let curTxt = cell.value.toString();
                if (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date')
                    curTxt = PIC.getInstance.userConfig.formatDate(cell.value);
                if (((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type) === 'datetime')
                    curTxt = PIC.getInstance.userConfig.formatDateTime(new Date(cell.value));
                if (((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.type) === 'time')
                    curTxt = PIC.getInstance.userConfig.formatTime(cell.value);
                if (val.includes('&')) {
                    hasMatch = true;
                    val.split('&').forEach(option => {
                        if (curTxt.toLowerCase().indexOf(option.toLowerCase()) === -1)
                            hasMatch = false;
                    });
                }
                else {
                    val.split('|').forEach(option => {
                        if (curTxt.toLowerCase().indexOf(option.toLowerCase()) > -1)
                            hasMatch = true;
                    });
                }
                return hasMatch;
            }
            return false;
        };
        const equals = (a, val, hdr) => {
            var _a, _b, _c;
            let cell = a[hdr.value];
            if (cell && typeof cell === 'object') {
                let curTxt = cell.value.toString();
                if (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date')
                    curTxt = PIC.getInstance.userConfig.formatDate(cell.value);
                if (((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type) === 'datetime')
                    curTxt = PIC.getInstance.userConfig.formatDateTime(new Date(cell.value));
                if (((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.type) === 'time')
                    curTxt = PIC.getInstance.userConfig.formatTime(cell.value);
                if ((val === '*' && curTxt.length > 0) || (val === '!*' && curTxt.length === 0))
                    return true; // all or nothing
                else {
                    let hasMatch = false;
                    val.split('|').forEach(option => {
                        if (option.length > 0 && curTxt.toLowerCase() === option.toLowerCase())
                            hasMatch = true;
                        else if (option === '!*' && curTxt.length === 0)
                            hasMatch = true; // checking for blanks
                    });
                    return hasMatch;
                }
            }
            return false;
        };
        const notEquals = (a, val, hdr) => {
            var _a, _b, _c;
            let cell = a[hdr.value];
            if (cell && typeof cell === 'object') {
                let curTxt = cell.value.toString();
                if (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date')
                    curTxt = PIC.getInstance.userConfig.formatDate(cell.value);
                if (((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type) === 'datetime')
                    curTxt = PIC.getInstance.userConfig.formatDateTime(new Date(cell.value));
                if (((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.type) === 'time')
                    curTxt = PIC.getInstance.userConfig.formatTime(cell.value);
                if (val === '*')
                    return curTxt.length === 0;
                else {
                    let hasMatch = false;
                    val.split('|').forEach(option => {
                        if (curTxt.toLowerCase() === option.toLowerCase())
                            hasMatch = true;
                    });
                    return !hasMatch;
                }
            }
            return false;
        };
        const between = (a, val, hdr) => {
            var _a, _b, _c, _d, _e, _f;
            let range = val.split(' to ');
            if (range.length !== 2)
                return false;
            let cell = a[hdr.value];
            if (cell && typeof cell === 'object') {
                let parsedC = typeof cell.value === 'number' ? cell.value : parseLocaleNumber(cell.value, PIC.getInstance.userConfig.numberLang);
                let parsedRangeA = parseLocaleNumber((range[0]), PIC.getInstance.userConfig.numberLang);
                let parsedRangeB = parseLocaleNumber((range[1]), PIC.getInstance.userConfig.numberLang);
                let cVal = !isNaN(parsedC) ? parsedC : undefined;
                let rangeA = !isNaN(parsedRangeA) ? parsedRangeA : undefined;
                let rangeB = !isNaN(parsedRangeB) ? parsedRangeB : undefined;
                if (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date' || ((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type) === 'datetime') { // converts the date vals to numbers for comparison
                    let cDate = undefined;
                    if (String(cell.value).length === 8)
                        cDate = new Date(String(cell.value).substring(0, 4) + '-' + String(cell.value).substring(4, 6) + '-' + String(cell.value).substring(6));
                    if (String(cell.value).length === 6)
                        cDate = new Date(String(cell.value).substring(0, 2) + '-' + String(cell.value).substring(2, 4) + '-' + String(cell.value).substring(4));
                    if (((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.type) === 'datetime')
                        cDate = new Date(String(cell.value));
                    if (cDate !== undefined)
                        cVal = cDate.valueOf();
                    rangeA = this.getNumberFromDateString(range[0], ((_d = hdr.properties) === null || _d === void 0 ? void 0 : _d.type) === 'date' ? 'date' : 'datetime');
                    rangeB = this.getNumberFromDateString(range[1], ((_e = hdr.properties) === null || _e === void 0 ? void 0 : _e.type) === 'date' ? 'date' : 'datetime');
                }
                if (((_f = hdr.properties) === null || _f === void 0 ? void 0 : _f.type) === 'time') { // converts the timevals to seconds for comparison
                    let start = range[0].split(':'), end = range[1].split(':');
                    if (start.length > 0)
                        rangeA = Number(start[0]) * 3600 + (!isNaN(Number(start[1])) ? Number(start[1]) * 60 : 0) + (!isNaN(Number(start[2])) ? Number(start[2]) : 0);
                    if (end.length > 0)
                        rangeB = Number(end[0]) * 3600 + (!isNaN(Number(end[1])) ? Number(end[1]) * 60 : 0) + (!isNaN(Number(end[2])) ? Number(end[2]) : 0);
                }
                if (cVal !== undefined && rangeA !== undefined && rangeB !== undefined) {
                    if (rangeA <= rangeB)
                        return cVal >= rangeA && cVal <= rangeB;
                    else
                        return cVal <= rangeA && cVal >= rangeB;
                }
            }
            return false;
        };
        const greaterOrLessThan = (a, val, hdr, mode) => {
            var _a, _b, _c, _d, _e;
            let cell = a[hdr.value];
            if (cell && typeof cell === 'object') {
                let parsedC = typeof cell.value === 'number' ? cell.value : parseLocaleNumber(cell.value, PIC.getInstance.userConfig.numberLang);
                let parsedF = parseLocaleNumber((val), PIC.getInstance.userConfig.numberLang);
                let cVal = !isNaN(parsedC) ? parsedC : undefined;
                let fVal = !isNaN(parsedF) ? parsedF : undefined;
                if (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date' || ((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type) === 'datetime') {
                    let cDate = undefined;
                    if (String(cell.value).length === 8)
                        cDate = new Date(String(cell.value).substring(0, 4) + '-' + String(cell.value).substring(4, 6) + '-' + String(cell.value).substring(6));
                    if (String(cell.value).length === 6)
                        cDate = new Date(String(cell.value).substring(0, 2) + '-' + String(cell.value).substring(2, 4) + '-' + String(cell.value).substring(4));
                    if (((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.type) === 'datetime')
                        cDate = new Date(String(cell.value));
                    if (cDate !== undefined)
                        cVal = cDate.valueOf();
                    fVal = this.getNumberFromDateString(val, ((_d = hdr.properties) === null || _d === void 0 ? void 0 : _d.type) === 'date' ? 'date' : 'datetime');
                }
                if (((_e = hdr.properties) === null || _e === void 0 ? void 0 : _e.type) === 'time') {
                    let arr = val.split(':');
                    if (arr.length > 0)
                        fVal = Number(arr[0]) * 3600 + (!isNaN(Number(arr[1])) ? Number(arr[1]) * 60 : 0) + (!isNaN(Number(arr[2])) ? Number(arr[2]) : 0);
                }
                if (fVal !== undefined && cVal !== undefined)
                    return mode === 'greater' ? cVal > fVal : cVal < fVal;
            }
            return false;
        };
        const custom = (a, val, hdr) => {
            var _a, _b, _c, _d, _e, _f, _g;
            let cellMatches = false;
            let cell = a[hdr.value];
            if (cell && typeof cell === 'object') {
                let curTxt = cell.value.toString();
                if (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date')
                    curTxt = PIC.getInstance.userConfig.formatDate(cell.value);
                if (((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type) === 'datetime')
                    curTxt = PIC.getInstance.userConfig.formatDateTime(new Date(cell.value));
                if (((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.type) === 'time')
                    curTxt = PIC.getInstance.userConfig.formatTime(cell.value);
                val.split('|').forEach(option => {
                    if (curTxt.toLowerCase().startsWith(option.toLowerCase()))
                        cellMatches = true;
                });
                if (cellMatches)
                    return true;
            }
            let hiddenValue = (_e = (_d = a[hdr.value]) === null || _d === void 0 ? void 0 : _d.properties) === null || _e === void 0 ? void 0 : _e.hiddenFilterValue;
            if (!hiddenValue)
                hiddenValue = (_f = a.__properties__) === null || _f === void 0 ? void 0 : _f.hiddenFilterValue;
            //@ts-ignore
            if (hiddenValue)
                hiddenValue = atob(hiddenValue);
            if (!Array.isArray(hiddenValue) && typeof hiddenValue === 'string')
                hiddenValue = JSON.parse(hiddenValue);
            let rawFilter = (_g = hdr.properties) === null || _g === void 0 ? void 0 : _g.customFilter;
            //@ts-ignore 
            if (rawFilter)
                rawFilter = atob(rawFilter);
            if (!Array.isArray(rawFilter) && typeof rawFilter === 'string')
                rawFilter = JSON.parse(rawFilter);
            if (Array.isArray(rawFilter)) {
                let found = rawFilter.findIndex((item) => item === null || item === void 0 ? void 0 : item.filterName);
                if (found >= 0)
                    rawFilter.splice(found, 1);
            }
            if (rawFilter && Array.isArray(rawFilter)) {
                let rex = rawFilter.map(curFilter => new RegExp(`${curFilter.replace('${inputValue}', val)}`, 'gi'));
                let hiddenMatches = rex.map(curRex => {
                    if (hiddenValue && Array.isArray(hiddenValue)) {
                        return hiddenValue.map(curHiddenValue => {
                            return curHiddenValue.toLowerCase().match(curRex);
                        }).filter(x => x !== null).length > 0 ? true : null;
                    }
                    return null;
                }).filter(x => x !== null && x !== undefined).length > 0;
                return hiddenMatches;
            }
            return false;
        };
        const filterChangeEvent = (event) => {
            var _a;
            const { header, value: filterValue, resetVisibleData, handleBlank } = event.detail;
            if ((filterValue === '' && !handleBlank) || resetVisibleData) { //a filter has been deleted
                this.visibleData = structuredClone(this.data.data);
                if (this.activeFilters.main !== '') {
                    this.visibleData = this.visibleData.filter(tdata => searchContains(tdata, this.activeFilters.main.toLowerCase()));
                }
                let tbr = [];
                for (const [key, value] of this.activeFilters.filters.entries()) {
                    let hdr = this.data.headers.find(h => h.value == key);
                    if (value.value === '' && !handleBlank) {
                        tbr.push(key);
                        continue;
                    }
                    if (typeof hdr === 'undefined')
                        continue;
                    this.visibleData = this.visibleData.filter(tdata => {
                        switch (value.filterType) {
                            case 'startsWith':
                            case true:
                            case '':
                                return startsWith(tdata, value.value, hdr);
                            case 'endsWith':
                                return endsWith(tdata, value.value, hdr);
                            case 'contains':
                                return contains(tdata, value.value, hdr);
                            case 'notStartsWith':
                                return !startsWith(tdata, value.value, hdr);
                            case 'notContains':
                                return !contains(tdata, value.value, hdr);
                            case 'equals':
                                if (value.value === '' && handleBlank)
                                    return notEquals(tdata, '*', hdr);
                                return equals(tdata, value.value, hdr);
                            case 'notequals':
                                if (value.value === '' && handleBlank)
                                    return equals(tdata, '*', hdr);
                                return notEquals(tdata, value.value, hdr);
                            case 'between':
                                return between(tdata, value.value, hdr);
                            case 'greaterThan':
                                return greaterOrLessThan(tdata, value.value, hdr, 'greater');
                            case 'lessThan':
                                return greaterOrLessThan(tdata, value.value, hdr, 'less');
                            case 'custom':
                                return custom(tdata, value.value, hdr);
                            case 'select':
                                return equals(tdata, value.value, hdr);
                            default:
                                return false;
                        }
                    });
                }
                for (const key of tbr) { //remove empty values from array to clear visual filter list
                    this.activeFilters.filters.delete(key);
                }
            }
            else { //add to a filter
                let filterDecider = (_a = this.activeFilters.filters.get(header.value)) === null || _a === void 0 ? void 0 : _a.filterType;
                if (filterDecider === 'equals') {
                    if (filterValue === '' && handleBlank)
                        this.visibleData = this.visibleData.filter((vData) => notEquals(vData, '*', header));
                    else
                        this.visibleData = this.visibleData.filter((vData) => equals(vData, filterValue, header));
                }
                else if (filterDecider === 'notequals') {
                    if (filterValue === '' && handleBlank)
                        this.visibleData = this.visibleData.filter((vData) => equals(vData, '*', header));
                    else
                        this.visibleData = this.visibleData.filter((vData) => notEquals(vData, filterValue, header));
                }
                else if (filterDecider === 'notContains') {
                    this.visibleData = this.visibleData.filter((vData) => !contains(vData, filterValue, header));
                }
                else if (filterDecider === 'notStartsWith') {
                    this.visibleData = this.visibleData.filter((vData) => !startsWith(vData, filterValue, header));
                }
                else if (filterDecider === 'contains') {
                    this.visibleData = this.visibleData.filter((vData) => contains(vData, filterValue, header));
                }
                else if (filterDecider === 'between') {
                    this.visibleData = this.visibleData.filter((vData) => between(vData, filterValue, header));
                }
                else if (filterDecider === 'greaterThan') {
                    this.visibleData = this.visibleData.filter((vData) => greaterOrLessThan(vData, filterValue, header, 'greater'));
                }
                else if (filterDecider === 'lessThan') {
                    this.visibleData = this.visibleData.filter((vData) => greaterOrLessThan(vData, filterValue, header, 'less'));
                }
                else if (filterDecider === 'custom') {
                    this.visibleData = this.visibleData.filter((vData) => custom(vData, filterValue, header));
                }
                else if (filterDecider === 'select') {
                    this.visibleData = this.visibleData.filter((vData) => equals(vData, filterValue, header));
                }
                else if (filterDecider === 'endsWith') {
                    this.visibleData = this.visibleData.filter((vData) => endsWith(vData, filterValue, header));
                }
                else {
                    this.visibleData = this.visibleData.filter((vData) => startsWith(vData, filterValue, header));
                }
            }
            //Update twice to fix filter/selected rows not updating with new rowcount
            //this.requestUpdate().then(() => this.requestUpdate()); //no longer returns a Promise??
            // this.requestUpdate()
            if (this.saveFilters) {
                PIC.getInstance.storage.setItem('webTable-SavedFilters-' + this.id_, JSON.stringify(this.activeFilters, this.replacer), 'internal');
                // sessionStorage.setItem('webTable-SavedFilters-' + this.id_, JSON.stringify(this.activeFilters, replacer));
            }
            if (this.visibleData.length >= 1000 && !this.pagination) {
                this.useLazyRender = true;
                this.lazyPage = [0, 99];
                if (PIC.getInstance.events.lazyTables.indexOf(this) == -1)
                    PIC.getInstance.events.lazyTables.push(this);
            }
            else {
                this.useLazyRender = false;
                if (PIC.getInstance.events.lazyTables.indexOf(this) != -1)
                    PIC.getInstance.events.lazyTables.splice(PIC.getInstance.events.lazyTables.indexOf(this), 1);
            }
            setTimeout(() => this.requestUpdate(), 50);
        };
        this.addEventListener('filterchange', filterChangeEvent);
        this.eventListeners.push(['filterchange', filterChangeEvent]);
        const loadSavedFilters = (event) => {
            let detail = event.detail;
            if (detail.value === 'filters') {
                for (const [key, value] of this.activeFilters.filters.entries()) {
                    let hdr = this.data.headers.find(h => h.value == key);
                    if (!hdr)
                        continue;
                    if (value.filterType === 'equals') {
                        if (value.value === '')
                            this.visibleData = this.visibleData.filter((vData) => notEquals(vData, '*', hdr));
                        else
                            this.visibleData = this.visibleData.filter((vData) => equals(vData, value.value, hdr));
                    }
                    else if (value.filterType === 'notequals') {
                        if (value.value === '')
                            this.visibleData = this.visibleData.filter((vData) => equals(vData, '*', hdr));
                        else
                            this.visibleData = this.visibleData.filter((vData) => notEquals(vData, value.value, hdr));
                    }
                    else if (value.filterType === 'notContains') {
                        this.visibleData = this.visibleData.filter((vData) => !contains(vData, value.value, hdr));
                    }
                    else if (value.filterType === 'notStartsWith') {
                        this.visibleData = this.visibleData.filter((vData) => !startsWith(vData, value.value, hdr));
                    }
                    else if (value.filterType === 'contains') {
                        this.visibleData = this.visibleData.filter((vData) => contains(vData, value.value, hdr));
                    }
                    else if (value.filterType === 'custom') {
                        this.visibleData = this.visibleData.filter((vData) => custom(vData, value.value, hdr));
                    }
                    else if (value.filterType === 'between') {
                        this.visibleData = this.visibleData.filter((vData) => between(vData, value.value, hdr));
                    }
                    else if (value.filterType === 'greaterThan') {
                        this.visibleData = this.visibleData.filter((vData) => greaterOrLessThan(vData, value.value, hdr, 'greater'));
                    }
                    else if (value.filterType === 'lessThan') {
                        this.visibleData = this.visibleData.filter((vData) => greaterOrLessThan(vData, value.value, hdr, 'less'));
                    }
                    else if (value.filterType === 'select') {
                        this.visibleData = this.visibleData.filter((vData) => equals(vData, value.value, hdr));
                    }
                    else if (value.filterType === 'endsWith') {
                        this.visibleData = this.visibleData.filter((vData) => endsWith(vData, value.value, hdr));
                    }
                    else {
                        this.visibleData = this.visibleData.filter((vData) => startsWith(vData, value.value, hdr));
                    }
                }
            }
            else if (detail.value === 'main' && this.activeFilters.main.length > 0) {
                this.visibleData = this.visibleData.filter(vData => searchContains(vData, this.activeFilters.main.toLowerCase()));
            }
            if (this.visibleData.length >= 1000 && !this.pagination) {
                this.useLazyRender = true;
                this.lazyPage = [0, 99];
                if (PIC.getInstance.events.lazyTables.indexOf(this) == -1)
                    PIC.getInstance.events.lazyTables.push(this);
            }
            else {
                this.useLazyRender = false;
                if (PIC.getInstance.events.lazyTables.indexOf(this) != -1)
                    PIC.getInstance.events.lazyTables.splice(PIC.getInstance.events.lazyTables.indexOf(this), 1);
            }
            this.requestUpdate();
            this.updateComplete.then(() => this.applySavedFilterVals());
        };
        this.addEventListener('loadsavedfilters', loadSavedFilters);
        this.eventListeners.push(['loadsavedfilters', loadSavedFilters]);
        const filterReset = (event) => {
            var _a, _b;
            let detail = event.detail;
            if (detail.value === 'filters') {
                if (this.saveFilters) {
                    PIC.getInstance.storage.removeItem('webTable-SavedFilters-' + this.id_, 'internal');
                    // sessionStorage.removeItem('webTable-SavedFilters-' + this.id_);
                }
                // if(this.saveToggledColumns){
                //     PIC.getInstance.storage.removeItem('webTableSavedFilters-' + this.id_, 'internal');
                // }
                this.activeFilters.filters.clear();
                this.visibleData = structuredClone(this.data.data);
                if (this.activeFilters.main.length > 0) {
                    this.visibleData = this.visibleData.filter(a => {
                        for (const [key, data] of Object.entries(a)) {
                            if (typeof data !== 'object')
                                continue;
                            try {
                                if (data.value.toString().toLowerCase().indexOf(this.activeFilters.main.toLowerCase()) > -1)
                                    return true;
                            }
                            catch (_a) { }
                        }
                    });
                }
            }
            else {
                //
            }
            if (this.visibleData.length >= 1000 && !this.pagination) { //TODO is it necessary here??
                this.useLazyRender = true;
                this.lazyPage = [0, 99];
                if (PIC.getInstance.events.lazyTables.indexOf(this) == -1)
                    PIC.getInstance.events.lazyTables.push(this);
            }
            else {
                this.useLazyRender = false;
                if (PIC.getInstance.events.lazyTables.indexOf(this) != -1)
                    PIC.getInstance.events.lazyTables.splice(PIC.getInstance.events.lazyTables.indexOf(this), 1);
            }
            this.selectedHeader = null;
            (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('#secondaryFilterContainer')) === null || _b === void 0 ? void 0 : _b.classList.remove('visible');
            this.requestUpdate();
        };
        this.addEventListener('filterreset', filterReset);
        this.eventListeners.push(['filterreset', filterReset]);
        if (this.selectionMode == 'multi' && this.allowDblclick) {
            this.addEventListener('click', this.handleTableClick);
            this.eventListeners.push(['click', this.handleTableClick]);
        }
        else {
            this.addEventListener('click', Util._throttle(this, this.handleTableClick, 1000));
            this.eventListeners.push(['click', Util._throttle(this, this.handleTableClick, 1000)]);
        }
        if (this.dragndrop || (this.editable && !this.readOnly)) {
            this.addEventListener('mousedown', this.handleDragTableRow);
            this.eventListeners.push(['mousedown', this.handleDragTableRow]);
        }
        this.addEventListener('contextmenu', this.handleContextMenu);
        this.eventListeners.push(['contextmenu', this.handleContextMenu]);
        if (this.dragndrop || (this.editable && !this.readOnly)) {
            this.addEventListener('mouseup', this.handleDragTableRow);
            this.eventListeners.push(['mouseup', this.handleDragTableRow]);
        }
        this.addEventListener('blur', this.handleTableBlur);
        this.eventListeners.push(['blur', this.handleTableBlur]);
        //Command
        this.addEventListener('addrows', this.handleCMDAddRows);
        this.eventListeners.push(['addrows', this.handleCMDAddRows]);
        this.addEventListener('insertrows', this.handleCMDInsertRows);
        this.eventListeners.push(['insertrows', this.handleCMDInsertRows]);
        this.addEventListener('deleterows', this.handleCMDDeleteRows);
        this.eventListeners.push(['deleterows', this.handleCMDDeleteRows]);
        this.addEventListener('refresh', this.handleCMDRefresh);
        this.eventListeners.push(['refresh', this.handleCMDRefresh]);
    }
    handleTableBlur(event) {
        this.selectedHeader = null;
        this.requestUpdate();
    }
    handleCMDRefresh(event) {
        this.getData(true);
    }
    /** Returns the number of milliseconds since January 1, 1970, 00:00:00 UTC for the given date string.*/
    getNumberFromDateString(date, type) {
        var _a;
        let retDate = undefined;
        let format = parseFormat(type === 'date' ? PIC.getInstance.userConfig.dateFormat : PIC.getInstance.userConfig.dateTimeFormat.split(' ')[0]);
        let dateParts = date.split(format.sep);
        let timeParts = (_a = date.split(' ')[1]) === null || _a === void 0 ? void 0 : _a.split(':');
        if (dateParts.length === 3) {
            retDate = new Date(dateParts[format.posArr.indexOf(Position.Year)] + '-' + dateParts[format.posArr.indexOf(Position.Month)] + '-' + dateParts[format.posArr.indexOf(Position.Day)]);
        }
        if (timeParts && retDate !== undefined) {
            retDate.setHours(Number(timeParts[0]), (!isNaN(Number(timeParts[1])) ? Number(timeParts[1]) : 0), (!isNaN(Number(timeParts[2])) ? Number(timeParts[2]) : 0));
        }
        return retDate === null || retDate === void 0 ? void 0 : retDate.valueOf();
    }
    handleCMDDeleteRows(event) {
        let cmd = event.detail;
        if (cmd.data)
            try {
                const cmdData = cmd.data;
                let keys;
                if (cmdData.rowKey.length > 0) {
                    keys = cmdData.rowKey.split(',');
                }
                else if (cmdData.rowIdx.length > 0) {
                    keys = cmdData.rowIdx.split(',').map(x => Number(x));
                }
                else {
                    throw new Error("Invalid key for deletion: " + cmd.data);
                }
                if (keys[0] == null || keys[0] === '' || (typeof keys[0] === 'number' && keys[0] < 1)) {
                    throw new Error("Invalid key for deletion: " + cmd.data);
                }
                this.deleteRows(keys);
                this.invalidateTooltips();
                this.clearActiveRows();
                this.setDataAndKeepSort(this.data);
                this.requestUpdate();
            }
            catch (e) {
                console.error(e);
            }
    }
    deleteRows(keys) {
        var _a, _b;
        if (typeof keys[0] === 'string') {
            //there can be multiple records with the same key
            for (let i = this.data.data.length - 1; i >= 0; i--) {
                //this.data.data[i].__properties__?.__submitValue__ === key
                const key = (_b = (_a = this.data.data[i].__properties__) === null || _a === void 0 ? void 0 : _a.__submitValue__) !== null && _b !== void 0 ? _b : '';
                if (key.length > 0 && keys.indexOf(key) > -1) {
                    this.data.data.splice(i, 1);
                }
            }
            redoInternalIdx(this.data.data);
        }
        else {
            for (const key of keys) {
                const rowToDelete = this.data.data.findIndex(row => row.internalIndex === key);
                if (rowToDelete > -1) {
                    this.data.data.splice(rowToDelete, 1);
                }
            }
            redoInternalIdx(this.data.data);
        }
        //Fix internal index after deletion
        function redoInternalIdx(data) {
            for (let i = 0; i < data.length; i++) {
                data[i].internalIndex = i + 1;
            }
        }
    }
    handleCMDInsertRows(event) {
        let cmd = event.detail;
        if (cmd.data)
            try {
                const rowData = cmd.data;
                const key = rowData.rowKey || rowData.rowIdx;
                const replaceRow = rowData.replaceRow;
                let newRows;
                if (key == null || key === '' || (typeof key === 'number' && (key < 0 || key > this.data.data.length))) {
                    throw new Error("Invalid key for replacement: " + key);
                }
                if (typeof rowData.data === 'string') { //CSV
                    const csvData = rowData.data.replaceAll("'", '"');
                    const options = {};
                    options.newLine = csvData.indexOf('\r\n') > -1 ? '\r\n' : '\n';
                    const parseData = this.parseCSV(csvData, options);
                    newRows = this.parseCsvResult2TableData(parseData).data;
                }
                else {
                    newRows = rowData.data;
                }
                this.insertRows(newRows, key, replaceRow);
                this.invalidateTooltips();
                this.clearActiveRows();
                this.setDataAndKeepSort(this.data);
                this.requestUpdate();
            }
            catch (e) {
                //Trigger alert "could not insert row" ?
                console.error(e);
            }
    }
    insertRows(newRows, key, replaceRow) {
        var _a, _b;
        if (replaceRow && newRows.length > 1) {
            throw new Error("Only allowed to replace one row pr command");
        }
        let insertionRowIdx = -1;
        let insertionRow;
        if (typeof key === 'string') {
            insertionRow = (_a = this.data.data.find(row => { var _a; return ((_a = row.__properties__) === null || _a === void 0 ? void 0 : _a.__submitValue__) === key; })) !== null && _a !== void 0 ? _a : null;
            insertionRowIdx = this.data.data.findIndex(row => { var _a; return ((_a = row.__properties__) === null || _a === void 0 ? void 0 : _a.__submitValue__) === key; });
        }
        else {
            insertionRow = (_b = this.data.data.find(row => row.internalIndex === key)) !== null && _b !== void 0 ? _b : null;
            insertionRowIdx = this.data.data.findIndex(row => row.internalIndex === key);
            //reuse above to not use to finds
            //this.data.data[key] ?? null
        }
        if (insertionRow == null) {
            throw new Error("Invalid insertionRow: " + key);
        }
        if (replaceRow) { //just one row to replace
            let intIdx = insertionRow.internalIndex;
            newRows[0].internalIndex = intIdx;
            this.data.data.splice(insertionRowIdx, 1, newRows[0]);
            //insertionRow = newRows[0]
            //insertionRow.internalIndex = intIdx
        }
        else { //move up all current records, give the previous ids to the new rows, then insert
            let intIdx = insertionRow.internalIndex;
            for (let i = 0; i < this.data.data.length; i++) {
                if (this.data.data[i].internalIndex < intIdx)
                    continue;
                this.data.data[i].internalIndex += newRows.length;
            }
            for (let i = 0; i < newRows.length; i++) {
                newRows[i].internalIndex = intIdx + i;
            }
            this.data.data.splice(insertionRowIdx, 0, ...newRows);
        }
    }
    handleCMDAddRows(event) {
        var _a, _b, _c, _d;
        let cmd = event.detail;
        if (cmd.data)
            try {
                const rowData = cmd.data;
                //use row input data, other last, if empty then "first" - if user input, move all indexes by data length
                const userRow = (_b = (_a = rowData.row) !== null && _a !== void 0 ? _a : this.data.data.length) !== null && _b !== void 0 ? _b : 0;
                let intRowIdx = (_d = (_c = this.data.data.slice(-1)[0]) === null || _c === void 0 ? void 0 : _c.internalIndex) !== null && _d !== void 0 ? _d : 0;
                let isInsert = false;
                if (typeof rowData.row !== 'undefined' && rowData.row < this.data.data.length) {
                    isInsert = true;
                    intRowIdx = this.data.data[rowData.row].internalIndex;
                    for (let i = 0; i < this.data.data.length; i++) {
                        if (i < rowData.row)
                            continue;
                        let iRow = this.data.data[i];
                        iRow.internalIndex += rowData.data.length;
                    }
                }
                const insertRow = userRow;
                //if adding at the end, ++idx, if inserting idx++
                if (isInsert)
                    rowData.data.forEach(row => {
                        row.internalIndex = intRowIdx++;
                    });
                else
                    rowData.data.forEach(row => {
                        row.internalIndex = ++intRowIdx;
                    });
                this.data.data.splice(insertRow, 0, ...rowData.data); //insert new rows into array without deleting
                this.invalidateTooltips();
                this.clearActiveRows();
                this.setDataAndKeepSort(this.data);
                this.requestUpdate();
            }
            catch (error) {
                console.error(error); //purely to trigger mail?
            }
    }
    //Call after rows has been modified by commands so tooltips are not in wrong places 
    // no recreate because they already exists)
    //or just remove as part of splice??????
    invalidateTooltips(row) {
        var _a, _b, _c, _d;
        if (typeof row === 'number' && row > 0) {
            const spansWithTooltips = (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`.grid-row row-${row} span[has-tooltip]`)) !== null && _b !== void 0 ? _b : [];
            for (const span of spansWithTooltips) {
                span.removeAttribute('has-tooltip');
                const tooltip = span.nextElementSibling;
                if (tooltip && tooltip.nodeName === 'PIC-TOOLTIP')
                    tooltip.remove();
            }
        }
        else {
            const spansWithTooltips = (_d = (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelectorAll(`.grid-row span[has-tooltip]`)) !== null && _d !== void 0 ? _d : [];
            for (const span of spansWithTooltips) {
                span.removeAttribute('has-tooltip');
                const tooltip = span.nextElementSibling;
                if (tooltip && tooltip.nodeName === 'PIC-TOOLTIP')
                    tooltip.remove();
            }
        }
    }
    clearActiveRows() {
        this.activeBaseRow = null;
        this.activeEndRow = null;
        this.activeRows = [];
    }
    /**
     * Add all static style here.
     * For pr instance style, fx number of columns add those in render() method.
     */
    static get styles() {
        const mainStyle = css `
			:host {
                width: 100%;
				display: block;
                --table-width: 100%;
                /* --pic-tableMediumBgColor: #d5e3f6; */
                /* --pic-tableMediumColor: #000000;  */
        		--table-row-odd-color: var(--pic-blackOverlayActiveColor);
        		--table-row-even-color: var(--pic-surfaceColor);
				--table-row-active-color: var(--pic-primaryActiveColor); //^pic- {  }
                --table-row-hover-bgcolor: rgba(247, 249, 252, 0.8);
                --table-row-hover-color: var(--pic-onSurfaceHighColor);
                --table-header-cell-bgcolor: var(--pic-tableMediumBgColor, var(--pic-primaryColor)); 
                --table-header-cell-color: var(--pic-tableMediumColor, var(--pic-onPrimaryHighColor));
                --table-row-cell-bgcolor: var(--pic-surfaceColor);
                --table-row-cell-color: var(--pic-onSurfaceHighColor);
                --table-footer-cell-bgcolor: var(--pic-tableMediumBgColor, var(--pic-primaryColor));
                --table-footer-cell-color: var(--pic-tableMediumColor, var(--pic-onPrimaryHighColor));
                --table-info-header-bgcolor: var(--pic-backgroundColor); 
                --table-info-footer-bgcolor: var(--pic-tableMediumBgColor, var(--pic-primaryColor));
                --table-info-footer-color: var(--pic-tableMediumColor, var(--pic-onPrimaryHighColor));
                --table-status-new-color: var(--pic-statusNewColor, #aee1e5);
                --table-status-ready-color: var(--pic-statusReadyColor, #cef6c3);
                --table-status-departed-color: var(--pic-statusDepartedColor, #efefa7);
                --table-status-done-color: var(--pic-statusDoneColor, #e8e9ea);
                --table-status-settled-color: var(--pic-statusSettledColor, #d8c7f4);
                --table-status-canceled-color: var(--pic-statusCanceledColor, #ffcad0);
                --table-status-invalid-color: var(--pic-statusInvalidColor, #F9C694);
				outline: 0;
                --table-row-cell-border-color: var(--table-row-border-color, var(--pic-outline, #E9EDF5));
                --table-row-cell-border-width: 1px 0 0 0;
                --table-info-header-inactive-icon-color: var(--pic-primaryMediumColor);
                --table-info-header-active-icon-color: var(--pic-primaryColor);
                --table-info-header-icon-bgcolor-hover: var(--pic-primaryHoverColor);
                --table-info-header-icon-bgcolor-focus: var(--pic-primaryFocusColor);
                --filter-row-top: 2.2rem;
                --scrollbar-button-height: 2.23rem;
                --lazy-header-strip-top: 2.2rem;
                --lazy-header-strip-left: 0;
            }
			.grid-table { 
        		display: grid; 
        		max-height: var(--table-height, 80vh);
        		overflow: auto;
                outline: 0;
                max-width: var(--table-width);
    		}
            .grid-table.pic-loadingicon { 
                width: 100%;
                width: -webkit-fill-available;
                height: 15rem;
            }
            .grid-table.hasLazyScrollbar::-webkit-scrollbar-thumb,
            .grid-table.hasLazyScrollbar::-webkit-scrollbar-track {
                visibility: hidden;
            }
            .grid-table::-webkit-scrollbar-thumb:horizontal,
            .grid-table::-webkit-scrollbar-track:horizontal { 
                visibility: visible;
            }
            .grid-table.hasLazyScrollbar.firefox{
                scrollbar-width: 6px;
                scrollbar-color: transparent transparent;
                width: calc(100% - 6px);
            }
            .tableFullHeight:has(.grid-table.hasLazyScrollbar.firefox)::after {
                content: '';
                position: absolute;
                top: var(--lazy-header-strip-top);
                left: var(--lazy-header-strip-left);
                width: 6px;
                height: var(--lazy-header-strip-height, 4.4rem);
                background: var(--table-header-cell-bgcolor);
                pointer-events: none;
                z-index: 1;
            }

            .grid-table::-moz-scrollbar-button:single-button:vertical {
                scrollbar-width: 6px;
                height: var(--scrollbar-button-height);
                background-color: var(--table-header-cell-bgcolor);
            }
            .grid-table::-webkit-scrollbar-button:single-button:vertical {
                scrollbar-width: 6px;
                height: var(--scrollbar-button-height);
                background-color: var(--table-header-cell-bgcolor);
            }

            .grid-table::-webkit-scrollbar-button:single-button:vertical:increment {
                display: none;
            }
            .grid-table::-webkit-scrollbar-corner {
                background-color: var(--pic-backgroundColor);
            }

            .grid-header {display:contents;}
            .grid-filter-row {display:contents;}
			.grid-header .grid-cell {
                border-top: 0 !important;
				display: inline-flex;
				align-items: center;
				background-color: var(--table-header-cell-bgcolor);
                color: var(--table-header-cell-color);
				/* backdrop-filter: blur(8px); */
				/* padding: 6px; */
				position: sticky;
				top: 0;
				text-align: left;
				height: 2.2rem;
                z-index: 2;
				min-width: 1.5rem;
			}
            .grid-header .grid-cell pic-checkbox { /* otherwise checkbox has same color as header and is invisible */
                --pic-checkboxColor: var(--table-header-cell-color) !important;
            }
            .grid-header .grid-cell span{
                color: var(--table-header-cell-color);
                -webkit-user-select: none;
                -ms-user-select: none;
                user-select: none;
            }
			.grid-header .grid-cell.grid-sortable { padding-left: 11px; cursor: pointer; }
			.grid-header > .grid-sortable::before, .grid-header > .grid-sortable::after  {
				border: 4px solid transparent;
				content: "";
				display: block;
				height: 0;
				left: 2px;
				top: 50%;
				position: absolute;
				width: 0;
			}
			.grid-header > .grid-sortable::before {
				border-bottom-color: var(--pic-onSurfaceMediumColor, #666);
    			margin-top: -8px;
			}
			.grid-header > .grid-sortable.grid-sortable-ascending::before { border-bottom-color: cornflowerblue; }
			.grid-header > .grid-sortable::after {
				border-top-color: var(--pic-onSurfaceMediumColor, #666);
    			margin-top: 3px;
			}
			.grid-header > .grid-sortable.grid-sortable-descending::after { border-top-color: cornflowerblue; }
            .header-resize-handle {
                position: absolute;
                right: 0;
                top: 0;
                bottom: 0;
                width: 8px;
                cursor: col-resize;
                z-index: 1;
            }
            .header-resize-handle:hover {
                background-color: var(--table-header-cell-color);
                opacity: 0.15;
            }
			
			.grid-footer {display:contents;}
			.grid-footer .grid-cell {
				border-top: 1px solid var(--pic-primaryColor, black);
				border-bottom: 1px solid var(--pic-outline, #E9EDF5);
                display: flex;
                align-items: center;
                justify-content: right;
                background-color: var(--table-footer-cell-bgcolor);
                color: var(--table-footer-cell-color);
				position: sticky;
				bottom: 0;
				min-height: 1.25rem;
				height: 1.25rem;
				max-height: 1.25rem;
                padding: 1rem 0;
			}

			.grid-checkbox { 
                cursor: pointer;
                overflow:hidden; 
				display: grid;
    			place-items: center;
                justify-content: center;
                align-items: center;
                padding: 0;
                line-height: unset; 
            }
			
			.grid-treeCaret::before {
                font-family: "Material Design Icons";
                font-size: x-large;
				//content: '\u{F0B27}';
				content: '\u{f0142}';
                color: var(--pic-onSurfaceMediumColor, #868FA0);
  				display: inline-block;
  				margin-right: 6px;
				transition-duration: 0.3s;
    			transition-property: transform;
			}
			
			.grid-treeCaret { 
				cursor: pointer; 
				user-select: none; 
				height: 100%;
				display: grid;
				place-items: center;
			}
	
			.grid-treeCaretDown::before { 
				//transform: rotate(90deg);
				content: '\u{f0140}';
			}
            .grid-remove {
                cursor: pointer; 
				user-select: none; 
				height: 100%;
				display: grid;
				place-items: center;
                padding: 0 2px;
            }
            .grid-remove::before {
                font-family: "Material Design Icons";
                font-size: large;
                content: '\u{f0159}'; 
                color: var(--pic-onSurfaceDisabledColor, #00000061);
            }
            .grid-remove:hover::before {
                color: var(--pic-statusErrorColor, #b00020)
            }
            .grid-move {
                padding: 0 2px;
                cursor: move;
            }
            .grid-move::before {
                font-family: "Material Design Icons";
                content: '\u{F01DD}';
                width: 16px;
                height: 16px;
                color: var(--pic-onSurfaceMediumColor);
            }
			.grid-body { 
                display: contents;
                font-variant-numeric: tabular-nums; 
            }
			/* ++cell-border */
			/* .grid-row:hover > .grid-cell { background-color: var(--table-row-hover-bgcolor); } */
			/*.grid-row:hover > .grid-cell { filter: brightness(88%) }*/

			.grid-row:hover > .grid-cell, .grid-row:hover div, .grid-row.alternate-bg:hover > .grid-cell, .grid-row.alternate-bg:hover {
                background-color: var(--elm-bg-hover-color, var(--pic-primaryHoverColor, #e0e0e0)); }
			.grid-row { display:contents; }
			.grid-cell { 
                display: inline-flex;
                align-items: center;
                justify-content: left;
                border-width: 1px 0 0 0; /* only top-border */
                border-style: solid;
                border-color: var(--table-row-border-color, var(--pic-outline, #E9EDF5));
                padding: var(--table-row-cell-padding, 6px 6px 5px 6px);
				min-height: 1rem; 
                background-color: var(--table-row-cell-bgcolor);
                color: var(--table-row-cell-color);
                /* white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis; */
				box-sizing: border-box;
			}
            .grid-cell.cell-borders{
                border-width: var(--table-row-cell-border-width);
                border-color: var(--table-row-cell-border-color);
            }
            .grid-row.row-1 .grid-cell:not(.cell-borders) { /* remove border from top row in body */
                border-top: 0;
            }
			.grid-cell.compact { 
				--table-row-cell-padding: 0px 3px 0px 5px;
                overflow: hidden;
			}
            .grid-cell.cozy {
                --table-row-cell-padding: 6px 3px 6px 5px;
            }
            .grid-cell.roomy {
                --table-row-cell-padding: 1em 3px 1em 5px;
            }
            .grid-cell.compactheader {
                height: 1.8rem;
            }
            .hideheadertooltip {
                display: none;
            } 
			.grid-cell > .hide-overflow {
				width: 100%;
				white-space: pre;
                overflow: hidden;
                text-overflow: ellipsis;
			}
			.grid-cell > .wrap-overflow {
				/* white-space: nowrap; */
                white-space: pre;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .grid-cellRight {
                text-align: right !important;
            }
            .grid-cell i.fa {
                font-size: 1.5em; /* slightly better icon size */
            }
            .grid-subrow {
                border: none;
            }
			.grid-row.highlight > .grid-cell {
				/* Compute inverse */
				background-color: black !important;
        		color: white !important;
			}	
			.grid-row.active-row > .grid-cell, .grid-row.alternate-bg.active-row > .grid-cell {
				background-color: var(--elm-bg-active-color, var(--elm-bg-hover-color, var(--table-row-active-color)));
			}
            .grid-row.active-row, .grid-row.alternate-bg.active-row {
                background-color: transparent;
            }
            .grid-cell  > input[type=number]{
                -moz-appearance: textfield;
            }
            .grid-cell > input::-webkit-outer-spin-button,
            .grid-cell > input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
            .icon-cell {
                display: block;
                height: 24px;
                width: 24px;
                /* background-color: var(--table-row-cell-bgcolor); */
                fill: var(--table-row-cell-color);
            }
            .new-row{
                border-width: 1px 0px;
                border-style: solid;
                border-color: var(--table-row-border-color, var(--pic-outline, #E9EDF5));
                display: flex;
                align-items: center;
                justify-content: center;
                /* color: #31A751bf; */
                color: #31A75199;
            }
            .new-row:hover{
                background-color: var(--elm-bg-hover-color, var(--pic-primaryHoverColor, #e0e0e0));
                cursor: pointer;
                color: var(--pic-statusOkColor);
            }
            .add-new-row::before {
                font-family: "Material Design Icons";
                content: '\u{f0417}';
                font-size: x-large;
                font-style: normal;
                color: inherit;
            }
            .editable:active {
                cursor: grabbing;
            }
            .editable:not(:active) .grid-move {
                cursor: grab;
            }
            /* .add-new-row is the i itself */
			/* The Header on top of the table - can take search bars and more */
			.info-header {
                background-color: var(--table-info-header-bgcolor);
				width: 100%;
				min-height: 3rem;
				box-sizing: border-box;
				display: grid;
				grid-template-columns: 1fr 8fr 0fr 0fr 0fr 0fr;
				place-items: center start;
			}
            .info-header.admin{
                /* grid-template-columns: 1fr 7fr 1fr 1fr 1fr 1fr 1fr; */
                grid-template-columns: 1fr 7fr 0fr 0fr 0fr 0fr 0fr;
            }
            .info-header .pic-button,
            .reset-columns-button {
                font-style: normal;
                font-weight: 600;
                font-size: 12px;
                line-height: 18px;
                letter-spacing: 0.03em;
                color: var(--pic-primaryColor, rgb(0, 109, 204));
                margin: 0px 6px;
                background-color: var(--pic-surfaceColor, white);
                border: none;
                text-transform: none;
                white-space: nowrap;
            }
			.info-footer {
				background-color: var(--table-info-footer-bgcolor);
				min-height: 3rem;
				height: 3rem;
				max-height: 3rem;
				box-sizing: border-box;
				display: flex;
				flex-direction: row;
				justify-content: space-between;
				align-items: center;
                padding: 0px 10px 0px 10px;
                color: var(--table-info-footer-color);
			}
			.info-footer b{
				/* color: #003767; */
				font-weight: 700;
			}
			.info-footer select {
				padding: 0px 10px 0px 10px;
                padding: 6px 12px;
                background: var(--pic-surfaceColor, #FFFFFF);
                color: var(--pic-onSurfaceHighColor);
                box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(70, 79, 96, 0.16);
                border-radius: 6px;
			}
            .info-footer .last-updated{
                margin-left: auto;
                margin-right: 3em;
            }
            .info-header select::before {
                font-family: "Material Design Icons";
                font-size: x-large;
                content: '\u{F0232}';
                position: absolute;
                left: 8px;
                top: 6px;
                color: var(--pic-onSurfaceMediumColor, #868FA0);
			}
            .info-header select {
                padding: 6px 12px;
                height: 32px;
                background: var(--pic-surfaceColor, #FFFFFF);
                color: var(--pic-onSurfaceHighColor);
                box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(70, 79, 96, 0.16);
                border-radius: 6px 0px 0px 6px;
                margin: 0px 1px;
			}
			.info-title {
				justify-self: start;
				padding: 0.25rem;
				font-size: 21px;
				font-weight: bold;
				min-width: 9rem;
			}
			.filter-single {
				grid-column: 1;
				position: relative;
                width: 100%;
                width: -moz-available;          
                width: -webkit-fill-available;  
                width: fill-available;
			}
			.filter-single::before {
                font-family: "Material Design Icons";
                font-size: x-large;
				content: '\u{F0349}';
                position: absolute;
                left: 8px;
                top: 6px;
                color: var(--pic-onSurfaceMediumColor, #868FA0);
			}
			.filter-single input {
                padding: 5px 3px;
                padding-left: 2rem;
                margin-left: 1.25%;
                background: var(--pic-backgroundColor, #FFFFFF);
                color: var(--pic-onSurfaceHighColor);
                box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.06), 0px 0px 0px 1px rgba(134, 143, 160, 0.16);
                border-radius: 6px;
                border: 1px solid var(--pic-outline, rgb(234, 236, 239));
                width: 100%;
                width: -moz-available;
                width: -webkit-fill-available;
                width: fill-available;
            }
			.grid-header-filters {
				display: contents
			}
			.filter-container {
				max-height: 200px;
				box-sizing: border-box;
				padding: 5px 5px 5px 0;
    			border-bottom: 2px solid var(--pic-outline, black);
				transition: max-height 0.5s ease 0s, visibility 0.2s ease 0s, padding 0.5s ease 0s, margin 0.5s ease 0s;
				position: sticky;
    			top: 37px; /* calc this properly */
    			background-color: var(--pic-surfaceColor, white);
			}
			.filter-hidden {
				visibility: hidden;
				max-height: 0;
				margin: 0;
				padding: 0;
			}
			.info-update {
				grid-column-start: 1;
				grid-column-end: 3;
			}
			.info-update::before {
				content: "";
				border-radius: 50%;
				width: 12px;
				height: 12px;
				background-color: var(--lastUpdateColor, green);
				display: inline-block;
				margin: 0 2px;
			}
			.info-rows {
				display: grid;
    			grid-template-columns: max-content max-content;
				white-space: break-spaces;
			}

			input[type="checkbox"] {
				width: 16px;
				height: 16px;
				background: var(--pic-surfaceColor, #FFFFFF);
				box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(70, 79, 96, 0.16);
				border-radius: 4px;
				appearance: unset;
			}

            input[type="checkbox"] {
                font-family: 'Montserrat';
                font-style: normal;
                font-weight: normal;
                font-size: 14px;
                line-height: 20px;
                letter-spacing: 0.25px;
                color: var(--pic-onSurfaceHighColor, #000000);
			}
			input[type="checkbox"]:hover {cursor: "pointer"}
			input[type="checkbox"]:checked::before {
				content: '\u{2713}';
				color: var(--pic-primaryColor, #003767);
                position: relative;
                bottom: 2px;
                left: 2px;
			}
			input[type="checkbox"]:checked {
    			border: 1px solid var(--pic-primaryColor, #003767);
                overflow: inherit;
			}

            button {
                cursor: pointer;
            }

            .filterselectcontainer {
                grid-column-start: 1;
            }

            .filterselectbutton::before {
                font-family: "Material Design Icons";
                content: '\u{F0232}';
                color: var(--pic-onSurfaceMediumColor, #868FA0);
			}

            .filterselectbutton {
                cursor: pointer;
                padding: 6px 12px;
                background: var(--pic-surfaceColor);
                box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(70, 79, 96, 0.16);
                border-radius: 6px;
                margin: 0px 1px;
                white-space: nowrap;
                color: var(--pic-onSurfaceHighColor);
            }
            .filterselectmenu {
                background: var(--pic-surfaceColor, #FFFFFF);
                color: var(--pic-onSurfaceHighColor);
                box-shadow: 0px 0px 0px 1px rgba(152, 161, 179, 0.1), 0px 15px 35px -5px rgba(17, 24, 38, 0.15), 0px 5px 15px rgba(0, 0, 0, 0.08);
                border-radius: 6px;
                z-index: 3;
                position: fixed;
                top: 35px;
                animation: 0.3s ease 0s 1 normal none running fadeIn;
                display: flex;
                flex-direction: column;
                flex-wrap: nowrap;
                align-items: stretch;
                max-height: 70vh;
                height: auto;
                overflow: auto;
            }
            .filterselectmenuitem:hover {
                background-color: var(--pic-primaryHoverColor)
            }
            .filterselectmenuitem {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                height: 32px;
                align-items: center;
                padding: 4px;
                transition: all 300ms ease-out
            }
            .filterselectmenuitemdragicon::before {
                font-family: "Material Design Icons";
                content: '\u{F01DD}';
                width: 16px;
                height: 16px;
                color: var(--pic-onSurfaceMediumColor);
                cursor: grab;
            }
            .borderTop{
                border-top: 2px solid var(--pic-primaryColor);
                /* margin-top: 36px; */
                /* height: calc(36px * 2); */
                padding-top: 40px;
                transition: all 300ms ease-in
            }
            .borderBottom{
                border-bottom: 2px solid var(--pic-primaryColor);
                padding-bottom: 40px;
                transition: all 300ms ease-in
            }
            .filterlabelcontainer {
                margin-left: 5px;
                grid-column-start: 2;
                display: inline-flex;
                gap: 4px;
                width: 100%;
                overflow: hidden;
            }
            .filter-row {
                display: inline-flex;
                align-items: center;
                background-color: var(--table-header-cell-bgcolor);
                position: sticky;
                top: var(--filter-row-top); /* should always be the header's height */
                height: 2.2rem;
				min-width: 1.5rem;
            }
            .filter-row > input {
                background-color: var(--pic-backgroundColor);
                color: var(--pic-onSurfaceHighColor);
                height: calc(2.2rem - 10px);
                min-height: 20px;
                padding: 0 16px 0 2px;
                border: 1px solid var(--pic-outline);
                border-radius: 2px;
                margin: 0 5px;
                width: calc(100% - 12px);
            }
            .filter-row > input:disabled {
                background-color: rgb(from var(--pic-backgroundColor) r g b / 0.5);
            }
            .filter-icon {
                color: var(--table-info-header-inactive-icon-color);
            }
            .filter-icon.active:not(.disabled),
            .filter-icon:active:not(.disabled) {
                color: var(--table-info-header-active-icon-color);
            }
            .filter-icon::before{
                font-family: "Material Design Icons";
                font-size: small;
                color: inherit;
                content: '\u{F0232}';
                position: absolute;
                right: 8px;
                top: 11px;
                cursor: pointer;
            }
            .filter-icon.disabled::before {
                /* --pic-blackOverlayFocusColor is a flat black overlay with no dark
                   variant, so it's nearly invisible on a dark input/page background -
                   the theme-aware disabled-ink token reads correctly in both modes. */
                color: var(--pic-onSurfaceDisabledColor);
                cursor: default;
            }
            .tableBtn {
                padding: 10px;
                border-radius: 50%;
                margin-left: 10px;
                cursor: pointer;
                color: var(--table-info-header-inactive-icon-color);
            }
            .tableBtn:hover{
                color: var(--table-info-header-active-icon-color);
                background-color: var(--table-info-header-icon-bgcolor-hover);
            }
            .tableBtn:focus {
                color: var(--table-info-header-active-icon-color);
                background-color: var(--table-info-header-icon-bgcolor-focus);
            }
            .tableBtn.selected{
                color: var(--table-info-header-active-icon-color);
            }
            .tableBtn::before{
                font-family: "Material Design Icons";
                font-size: x-large;
                color: inherit;
            }
            .tableNewButton{
                grid-column-start: 3;
            }
            .tableNewButton::before{
                content: '\u{F0415}';
            }
            .tableRefreshButton{
                grid-column-start: 4;
            }
            .tableRefreshButton::before{
                content: '\u{F0450}';
            }
            .tableFiltersButton{
                grid-column-start: 5;
            }
            .tableFiltersButton::before{
                /* content: '\u{F0236}'; // this is the stripey icon */
                content: '\u{F0232}'; // this is the funnel icon
            }
            .tableSettingsButton{
                grid-column-start: 7;
            }
            .tableSettingsButton::before{
                content: '\u{F0493}';
            }
            .tableExportButton{
                grid-column-start: 6;
            }
            .tableExportButton::before {
                content: '\u{F021D}';
            }
            #filterContainer, #secondaryFilterContainer, #selectContainer {
                background-color: var(--pic-surfaceColor, white);
                color: var(--pic-onSurfaceHighColor);
                position: fixed;
                top: 0;
                left: 0;
                z-index: 10;
                margin: 0 5px;
                box-shadow: 0px 0px 0px 1px rgba(152, 161, 179, 0.1), 0px 15px 35px -5px rgba(17, 24, 38, 0.15), 0px 5px 15px rgba(0, 0, 0, 0.08);
                border-radius: 6px;
                cursor: pointer
            }
            #filterContainer > div {
                padding: 10px 10px 10px 20px;
                user-select: none;
            }
            #filterContainer > div:hover {
                background-color: var(--pic-primaryHoverColor);
            }
            #filterContainer > div:focus {
                background-color: var(--pic-primaryHoverColor);
            }
            #filterContainer > div.selected {
                font-weight: bold;
            }
            #secondaryFilterContainer {
                padding: 10px;
            }
            #secondaryFilterContainer, #selectContainer {
                display: none;
            }
            #secondaryFilterContainer.visible, #selectContainer.visible {
                display: block;
            }
            #secondaryFilterContainer p {
                user-select: none;
            }

            #secondaryFilterContainer > input {
                background-color: var(--pic-surfaceColor, white);
                color: var(--pic-onSurfaceHighColor);
                border: 1px solid var(--pic-outline);
                border-radius: 2px;
            }
            #selectContainer {
                max-height: 400px;
                overflow-y: auto;
                cursor: default;
            }
            .select-checkbox {
                margin-top: -15px;
            }
            .select-item {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                height: 32px;
                align-items: center;
                padding: 4px;
            }
            .select-item label {
                user-select: none;
            }
            .select-item:focus-within {
                background-color: var(--pic-primaryHoverColor);
            }
            .select-item:hover {
                background-color: var(--pic-primaryHoverColor);
            }
            .settings-container {
                background-color: var(--pic-surfaceColor, white);
                color: var(--pic-onSurfaceHighColor);
                position: fixed;
                z-index: 10;
                margin: 0 5px;
                box-shadow: 0px 0px 0px 1px rgba(152, 161, 179, 0.1), 0px 15px 35px -5px rgba(17, 24, 38, 0.15), 0px 5px 15px rgba(0, 0, 0, 0.08);
                border-radius: 6px;
                cursor: pointer;
                animation: 0.3s ease 0s 1 normal none running fadeIn;
                display: flex;
                flex-direction: column;
                flex-wrap: nowrap;
                align-items: stretch;
            }
            .settingsmenuitem{
                /* display: inline; */
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                height: 32px;
                align-items: center;
                padding: 4px;
                /* transition: all 300ms ease-out */
            }
            .settingsmenuitem:hover{
                background-color: var(--pic-primaryHoverColor);
            }
            .settingsmenuitemdragicon{
                margin-left: 10px;
            }
            .settingsmenuitemdragicon::before{
                font-family: "Material Design Icons";
                content: '\u{F01DD}';
                width: 16px;
                height: 16px;
                color: var(--pic-onSurfaceMediumColor);
                cursor: grab;
            }
            .subTablesContainer {
                border-left: '1px solid black';
                display: grid;
                /* overflow-y: scroll; */
                /* overflow-x: hidden ; */
            }
            .subTablesContainer .grid-header .grid-cell {
                display: grid;
                /* height: 1rem; */
                height: auto;
            }
            .subTable {
                display: grid;
                /* grid-column-start: 3; */
                /* grid-column-end: 3; */
                border-left: '1px solid black'; 
            }
            .clickable {
                cursor: pointer;
            }
            
            .retrybtn::before{
                font-family: 'Material Design Icons';
                font-size: x-large;
                color: var(--pic-onSurfaceMediumColor, #aaa);
                content: '\u{F0450}';
            }
            .retrybtn {

            }
            .retrybtn:hover {
                cursor: pointer;
            }
            .filterButtonContainer{
                display: flex;
                justify-content: center;
            }
            pic-checkbox.col-toggle:not(.col-settings){
                margin-top: 15px;
            }
            pic-checkbox.filter-search{
                margin-top: -5px;
                margin-left: 10px;
            }
            .resize-overlay {
                background-color: var(--table-header-cell-color);
                opacity: 0.2;
                pointer-events: none;
            }
            .hovered-header {
                background-color: var(--table-header-cell-color);
                opacity: 0.2;
                pointer-events: none;
            }
            .inputCell {
                width: 100%;
                border: none;
                background-color: transparent;
                padding: 0;
            }
            #pagination-control {
                display: flex;
                align-items: baseline;
                gap: .4rem;
                margin-left: 1rem;
            }
            .lazy-scrollbar {
                position: absolute;
                border-radius: 6px;
                width: 6px;
                background-color: var(--pic-scrollbarTrackColor);
            }
            .lazy-scrollbar.thumb {
                position: relative;
                background-color: var(--pic-scrollbarPassiveColor);
            }
            .lazy-scrollbar.thumb:hover,
            .lazy-scrollbar.thumb:active {
                background-color: var(--pic-scrollbarActiveColor);
            }
            .lazy-hscroll {
                position: absolute;
                left: var(--lazy-header-strip-left);
                bottom: 0;
                width: var(--lazy-hscroll-width, 100%);
                height: 6px;
                border-radius: 6px;
                background-color: var(--pic-scrollbarTrackColor);
                cursor: default;
            }
            .lazy-hscroll .lazy-hscroll-thumb {
                position: absolute;
                height: 6px;
                border-radius: 6px;
                background-color: var(--pic-scrollbarPassiveColor);
                transition: left 0.10s ease;
            }
            .lazy-hscroll .lazy-hscroll-thumb:hover,
            .lazy-hscroll .lazy-hscroll-thumb:active,
            .lazy-hscroll .lazy-hscroll-thumb.dragging {
                background-color: var(--pic-scrollbarActiveColor);
            }
		`;
        return css `
        ${super.styles}
		${mainStyle}
		`;
    }
    get hdrColCount() {
        let cnt = 0;
        this.data.headers.forEach(hdr => {
            if (['subTable'].includes(hdr.type))
                return;
            cnt++;
        });
        return cnt;
    }
    getData(isRefresh = false) {
        this.dataLoadError = false;
        if (isRefresh) {
            if (this.contextMenu.display !== 'none' && this.refreshTimer) {
                PIC.getInstance.setTimeout(this.getData.bind(this, true), this.refreshTimer);
                return;
            }
        }
        if (this.src) {
            this.loadData(this.src)
                .then()
                .catch(error => {
                console.error(error);
                this.visibleData = [];
                this.requestUpdate();
            });
        }
    }
    checkAndConsumeInlineData() {
        let iData = this.getAttribute('datafromprogress');
        if (iData) {
            try {
                iData = atob(iData);
                let parsey = JSON.parse(iData);
                if (!!parsey[this.dataRootName]) {
                    this.data = parsey[this.dataRootName];
                }
                else
                    this.data = parsey;
                if (!this.src) {
                    if (!this.autowidth) {
                        this.setHdrMinWidths().then(() => {
                            if (this.autoFitTable && !this._autoFitHeight) {
                                this.handleAutoFit(true);
                            }
                            else
                                this.handleAutoFit();
                        });
                    }
                    else {
                        if (this.autoFitTable && !this._autoFitHeight) {
                            this.updateComplete.then(() => {
                                if (this.useLazyRender)
                                    this.doDeferredLazyScrollUpdate = true;
                                this.handleAutoFit(true);
                            });
                        }
                        else {
                            this.updateComplete.then(() => this.handleAutoFit());
                        }
                    }
                }
            }
            catch (_a) {
                PIC.getInstance.msg('parsing inline data has failed');
            }
        }
        this.removeAttribute('datafromprogress');
        let iPrintReq = this.getAttribute('printrequester');
        if (iPrintReq) {
            try {
                iPrintReq = atob(iPrintReq);
                this.printRequester = iPrintReq;
                this.export.push('PrintRequester');
                this.export.push('JobQueue');
            }
            catch (_b) {
                PIC.getInstance.msg('parsing inline printRequester has failed');
            }
        }
        else {
            let newArr = [];
            this.export.forEach((e) => { if (e !== 'PrintRequester' && e !== 'JobQueue')
                newArr.push(e); });
            this.export = newArr;
        }
        this.removeAttribute('printrequester');
    }
    addSubTable(iData, iRow) {
        let subTableHdr = {
            type: 'subTable',
            text: '',
            value: '__subTable__',
            // internalIndex: -1,
        };
        if (!this.data.headers.find(hdr => hdr.type == 'subTable' && hdr.value == '__subTable__'))
            this.data.headers.push(subTableHdr);
        iRow['__subTable__'] = iData;
    }
    getTableHeightStyle() {
        var _a, _b, _c, _d, _e, _f;
        let styles = [];
        if (this.autoFitTable && this._autoFitHeight !== undefined) {
            styles.push({ 'height': `${this._autoFitHeight}` });
            styles.push({ 'min-height': '10vh' });
        }
        else if (this.minheight !== '') {
            let parentHeight = (_b = (_a = this.heightParent) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.height;
            if (this.minheight.match(/%/g) && parentHeight)
                styles.push({ 'min-height': `${parentHeight * parseFloat(this.minheight) / 100}px` });
            else
                styles.push({ 'min-height': this.minheight });
            styles.push({ 'display': 'flex' });
            styles.push({ 'flex-direction': 'column' });
            styles.push({ 'justify-content': 'space-between' });
            styles.push({ 'background-color': 'var(--table-row-cell-bgcolor)' });
        }
        if (this.useLazyRender && this.isFirefox) {
            styles.push({ '--lazy-header-strip-height': `${this.getTotalHeaderHeight()}px` });
            let top = (_d = (_c = this.gridTableHeader[0]) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect()) === null || _d === void 0 ? void 0 : _d.top;
            let left = (_f = (_e = this.gridTable) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect()) === null || _f === void 0 ? void 0 : _f.right;
            if (top !== undefined)
                styles.push({ '--lazy-header-strip-top': `${top}px` });
            if (left !== undefined)
                styles.push({ '--lazy-header-strip-left': `${left}px` });
        }
        return styles.length > 0 ? styles.reduce((total, current) => total = Object.assign(Object.assign({}, total), current)) : {};
    }
    /* RENDER METHODS */
    getGridTableStyle() {
        var _a, _b, _c;
        let tmpStr = '';
        let parentHeight = (_b = (_a = this.heightParent) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.height;
        let autoHeight = (_c = this._autoFitHeight) !== null && _c !== void 0 ? _c : '100%';
        let parentWidth = this._autoFitWidth && this._autoFitWidth.length > 0 ? this._autoFitWidth : '100%';
        if (this.isLoading)
            parentWidth = '100%';
        let savedWidth = JSON.parse(PIC.getInstance.storage.getItem('resizedHeaders-' + this.id_, 'session'));
        this.actuallyVisibleHeaders.forEach(hdr => {
            var _a, _b, _c, _d;
            if (['subTable'].includes(hdr.type))
                return;
            let widthStr = 'minmax(15px, 1fr) '; // default width for regular headers
            let isSpecialHeader = [':checkbox', ':tree', ':move', ':removal'].includes(hdr.value) || (((_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.type) && ['faicon', 'icon', 'button'].includes((_b = hdr.properties) === null || _b === void 0 ? void 0 : _b.type)); // default width for special headers
            if (isSpecialHeader)
                widthStr = 'max-content ';
            if ((_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.width) { // specified width overrides default
                widthStr = 'minmax(' + hdr.properties.width + ', auto) ';
                if (hdr.properties.width === 'max-content' && isSpecialHeader)
                    widthStr = 'max-content ';
            }
            let sWidth = (_d = savedWidth === null || savedWidth === void 0 ? void 0 : savedWidth.find((element) => element.val === hdr.value)) === null || _d === void 0 ? void 0 : _d.width; // resized width overrides specified width and default width
            if (sWidth)
                widthStr = 'minmax(' + sWidth + ', 1fr) ';
            if (this.autowidth) { // autowidth overrides all other width settings - the user wants the table to fit in its container (i.e. no horizontal scroll)
                widthStr = 'minmax(15px, 1fr) ';
                if (isSpecialHeader)
                    widthStr = 'max-content ';
            }
            tmpStr += widthStr + ' ';
        });
        return { 'grid-template-columns': tmpStr, '--table-height': this.autoFitTable ? autoHeight : this.height.match(/%/g) && parentHeight ? `${parentHeight * parseFloat(this.height) / 100}px` : this.height, '--table-width': parentWidth };
    }
    async setHdrMinWidths() {
        await this.updateComplete.then(() => {
            var _a;
            let tableWidth = (_a = this.gridTable) === null || _a === void 0 ? void 0 : _a.clientWidth;
            this._autoFitWidth = tableWidth + 'px';
            let timer = PIC.getInstance.setTimeout(() => {
                var _a, _b;
                let anyOverflow = Array.from((_a = this.gridTableHeader) !== null && _a !== void 0 ? _a : []).some(cell => {
                    let span = cell.querySelector('span');
                    return span ? span.scrollWidth > span.clientWidth : false;
                });
                if (anyOverflow)
                    (_b = this.gridTableHeader) === null || _b === void 0 ? void 0 : _b.forEach(cell => {
                        let hdr = cell === null || cell === void 0 ? void 0 : cell.item;
                        if (hdr) {
                            let span = cell.querySelector('span');
                            if (span) {
                                let scrollWidth = span.scrollWidth;
                                if (scrollWidth > span.clientWidth)
                                    scrollWidth += 1;
                                if (!hdr.properties)
                                    hdr.properties = {};
                                let hdrWidth = hdr.properties.width && hdr.properties.width.split('px').length > 1 ? parseFloat(hdr.properties.width.split('px')[0]) : undefined;
                                if (hdrWidth)
                                    hdrWidth = Math.max(hdrWidth, 50);
                                let padding = hdr.properties.sortable ? 17 : 12; // adding 12px to account for padding (6px left and right). For sortable headers, instead add 17px because of the caret icons
                                let widthVal = hdrWidth ? `${hdrWidth}px` : hdr.properties.width ? hdr.properties.width : `${Math.max(50, scrollWidth + padding)}px`;
                                hdr.properties.width = widthVal;
                                let convertedWidth = this.cssWidthToPxs(widthVal, tableWidth, span);
                                if (convertedWidth)
                                    hdr.properties.minWidth = convertedWidth;
                            }
                        }
                    });
                clearTimeout(timer);
                this.requestUpdate();
            }, 10);
        });
    }
    cssWidthToPxs(width, parentWidth, spanElm) {
        let num = parseFloat(width);
        if (isNaN(num))
            return undefined;
        let unit = width.replace(/^-?\d*\.?\d+/, '').trim().toLowerCase();
        switch (unit) {
            case 'px':
                return num;
            case '%':
                return num * parentWidth / 100;
            case 'em':
                return num * parseFloat(window.getComputedStyle(spanElm).fontSize);
            case 'rem':
                return num * parseFloat(window.getComputedStyle(document.documentElement).fontSize);
            case 'vw':
                return num * window.innerWidth / 100;
            case 'vh':
                return num * window.innerHeight / 100;
            default:
                return undefined;
        }
    }
    getGridTableClass() {
        return {
            'pic-loadingicon': this.visibleData.length == 0 && this.isLoading && !this.dataLoadError,
            'grid-table': true
        };
    }
    renderDataLoadError() {
        if (this.dataLoadError) {
            return html `
            <div class="pic-body2">
                There was an issue fetching data
                <div class="retrybtn" @click=${() => {
                this.requestUpdate();
                this.getData();
            }}>Reload</div>
            </div>
            `;
        }
        else
            return html ``;
    }
    emptyRowsMsg() {
        if (this.visibleData.length == 0 && !this.isLoading && !this.dataLoadError) {
            return html `
            <div class="pic-body2">No rows found</div>
            `;
        }
    }
    render() {
        var _a, _b;
        return html `
            ${this.renderCSS(true)}
			${this.renderTableGroups()}
			${this.renderInfoHeader()}
            ${this.settingsOpen ? this.renderSettingsContainer() : ''}
            <div class="tableFullHeight ${this.minheight ? 'minheight' : ''}" style=${styleMap(this.getTableHeightStyle())} >
                <div 
                    id="grid-table-${this.id_}"
                    style=${styleMap(this.getGridTableStyle())} 
                    class=${classMap(this.getGridTableClass())}
                    tabindex="0"
                    @keydown=${(e) => {
            var _a, _b;
            if (e.target && e.target.classList.contains('filter-input'))
                return;
            if (e.key === 'Tab' && e.shiftKey && e.target === this.gridTable) {
                e.preventDefault();
                const enabled = this.getEnabledFilterRowInputs();
                if (this.filterRowIsVisible && enabled.length > 0) {
                    enabled[enabled.length - 1].focus();
                }
                else if (((_a = this.tableBtns) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                    (_b = this.tableBtns[this.tableBtns.length - 1]) === null || _b === void 0 ? void 0 : _b.focus();
                }
                return;
            }
            _throttle(this, this.handleRowKeyDown, 50, ['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft', ' ', 'Escape'].includes(e.key))(e);
        }}
                    @scroll=${(e) => {
            if (this.useLazyRender) {
                if (this.isFirefox)
                    Util._throttle(this, this.handleHorizontalScroll, 10)(e);
            }
        }}
                    @wheel=${(e) => {
            if (this.useLazyRender) {
                if (!e.shiftKey)
                    Util._throttle(this, this.handleScroll, 60)(e);
            }
        }}
                >
                    <div class="grid-header">${this.renderTableHeader()}</div>
                    ${this.filterRowIsVisible && this.filterEnabled ? html `<div class="grid-filter-row">${this.renderFilterRow()}</div>` : ''}
                    <div class=${this.editable && !this.readOnly ? "grid-body editable" : "grid-body"}>
                        ${this.renderDataLoadError()}
                        ${this.visibleData.length > 0
            ? this.pagination
                ? this.renderTableBody(this.visibleData, this.currentPageOffset)
                : this.renderTableBody(this.visibleData)
            : this.editable && !this.readOnly ? this.renderAddNewRow()
                : this.emptyRowsMsg()}
                    </div>
                </div>
                ${this.useLazyRender ? this.renderLazyScrollbar() : ''}
                ${this.useLazyRender && this.isFirefox && this.gridTable && (((_a = this.gridTable) === null || _a === void 0 ? void 0 : _a.scrollWidth) > ((_b = this.gridTable) === null || _b === void 0 ? void 0 : _b.clientWidth)) ? this.renderFirefoxHScroll() : ''}
                ${this.renderInfoFooter()}
            </div>
            <pic-context id="tableContext" mode="table"></pic-context>
            ${this.renderFilterContainer()}
		`;
    }
    renderInfoHeader() {
        if (!this.infoheader)
            return '';
        return html `
            <!-- TODO change the below so the check is for whether user is admin - maybe add that to GlobUserConfig? -->
			<div class="info-header admin" @keydown=${(e) => { if (e.key === 'Tab')
            this.handleInfoHeaderTab(e); }}>
				${(!this.filter || (this.filter && this.filter == 'All') || !this.search) ? '' : this.renderFilterSearchField(this.filter)}
                ${this.allowNewRows ? html `<div class="tableBtn tableNewButton" tabindex="0" @click=${this.handleNewButton} @keyup=${(e) => { if (e.key === 'Enter')
            this.handleNewButton(); }}></div>` : ''}
                ${this.src && typeof this.src === 'string' ? html `<div class="tableBtn tableRefreshButton" tabindex="0" @click=${() => this.getData(true)} @keyup=${(e) => { if (e.key === 'Enter')
            this.getData(true); }}></div>` : ''}
                ${this.filterEnabled ? html `<div class="tableBtn tableFiltersButton" tabindex="0" @click=${this.toggleFilters} @keyup=${(e) => { if (e.key === 'Enter')
            this.toggleFilters(); }}></div>` : ''}
                ${(this.export.length !== 0) ? this.renderExportButton() : ''}
                <!-- TODO change the below so that it checks for whether user is admin - maybe add that to GlobUserConfig? -->
                <div class=${"tableBtn tableSettingsButton" + (this.settingsOpen ? ' selected' : '')} tabindex="0" @click=${this.toggleSettings} @keydown=${this.toggleSettings} @blur=${this.toggleSettings}></div>
			</div>`;
    }
    handleInfoHeaderTab(e) {
        var _a, _b, _c;
        let targ = e.target;
        if (this.filterRowIsVisible) {
            if (this.tableBtns.length > 0) {
                let currentIndex = Array.from(this.tableBtns).indexOf(targ);
                if (!e.shiftKey) {
                    if (currentIndex === this.tableBtns.length - 1) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        //focus on the first filter row input element
                        (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.filter-row input`)) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                }
            }
        }
        else {
            if (!e.shiftKey) {
                if (this.tableBtns.length > 0) {
                    let currentIndex = Array.from(this.tableBtns).indexOf(targ);
                    if (currentIndex === this.tableBtns.length - 1) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        // Set active row and focus the grid table for keyboard navigation
                        this.handleRowKeyDown(e);
                        (_c = this.gridTable) === null || _c === void 0 ? void 0 : _c.focus();
                    }
                }
            }
        }
    }
    get activeDragRow() { return this.activeDragRow_; }
    set activeDragRow(rowIdx) {
        var _a, _b;
        if (rowIdx != this.activeDragRow_) {
            let oldRow = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.settingsmenuitem.item-${this.activeDragRow_}`);
            let newRow = (_b = this.shadowRoot) === null || _b === void 0 ? void 0 : _b.querySelector(`.settingsmenuitem.item-${rowIdx}`);
            oldRow === null || oldRow === void 0 ? void 0 : oldRow.classList.remove('borderTop');
            oldRow === null || oldRow === void 0 ? void 0 : oldRow.classList.remove('borderBottom');
            if (this.visibleHeaders.findIndex(hdr => hdr.internalIndex == rowIdx) > this.visibleHeaders.findIndex(hdr => hdr.internalIndex == this.activeDragRow_))
                newRow === null || newRow === void 0 ? void 0 : newRow.classList.add('borderBottom');
            else
                newRow === null || newRow === void 0 ? void 0 : newRow.classList.add('borderTop');
            this.activeDragRow_ = rowIdx;
        }
    }
    /** Returns sum of header height and filter row height - if filter row is visible */
    getTotalHeaderHeight() {
        return this.computedDataHeight.header + (this.filterRowIsVisible ? this.compactHeaderMode ? (this.computedDataHeight.header * (2.2 / 1.8)) : this.computedDataHeight.header : 0);
    }
    handleScroll(event) {
        // if scrolling up
        if (event.deltaY < 0 && (this.gridTable.scrollHeight - this.gridTable.clientHeight) / 3 >= this.gridTable.scrollTop) {
            if (this.lazyPage[0] > 0) {
                this.forceScroll = true;
                this.scrollDiff = (this.lazyPage[0] - 10) < 0 ? this.lazyPage[0] : 10;
                this.lazyPage[0] -= this.scrollDiff;
                this.lazyPage[1] -= this.scrollDiff;
                this.requestUpdate();
            }
        }
        // if scrolling down
        if (event.deltaY > 0 && (this.gridTable.scrollHeight - this.gridTable.clientHeight) / 3 * 2 <= this.gridTable.scrollTop) {
            if (this.lazyPage[1] <= this.visibleData.length - 1) {
                this.forceScroll = false;
                this.scrollDiff = (this.lazyPage[1] + this.scrollDiff) > this.visibleData.length - 1 ? this.visibleData.length - 1 - this.lazyPage[1] : 10;
                this.lazyPage[0] += this.scrollDiff;
                this.lazyPage[1] += this.scrollDiff;
                this.requestUpdate();
            }
        }
        // setting thumb position
        let tableClientRect = this.gridTable.getBoundingClientRect();
        if (tableClientRect === null || tableClientRect === void 0 ? void 0 : tableClientRect.top) {
            let totalHeaderHeight = this.getTotalHeaderHeight();
            for (let i = this.lazyPage[0]; i < this.lazyPage[1]; i++) {
                let row = this.gridBody.querySelector('.row-' + this.visibleData[i].internalIndex);
                let rCell = row === null || row === void 0 ? void 0 : row.querySelector('.grid-cell');
                let scrollLength = this.gridTable.clientHeight;
                if (this.scrollThumb.minimum)
                    scrollLength -= (10 - this.scrollThumb.height);
                if (rCell && rCell.getBoundingClientRect().top >= (tableClientRect.top + totalHeaderHeight)) {
                    let fractionFromTop = i / this.visibleData.length;
                    this.thumbTop = fractionFromTop * (scrollLength - totalHeaderHeight);
                    this.lazyThumb.style.top = this.thumbTop + 'px';
                    break;
                }
            }
        }
    }
    getScrollStyle() {
        var _a, _b, _c, _d;
        let tableClientRect = (_a = this.gridTable) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        let top = (_b = tableClientRect === null || tableClientRect === void 0 ? void 0 : tableClientRect.top) !== null && _b !== void 0 ? _b : 0;
        let left = (tableClientRect === null || tableClientRect === void 0 ? void 0 : tableClientRect.right) ? tableClientRect.right - 6 : 0;
        if (this.gridTable && this._autoFitWidth && this._autoFitWidth.split('px').length > 1)
            left = this.gridTable.offsetLeft + (parseFloat(this._autoFitWidth.split('px')[0]) - 6);
        if (this.heightParent && this.heightParent.scrollHeight > this.heightParent.clientHeight)
            left -= 6;
        if (this.isFirefox)
            left = (_c = tableClientRect === null || tableClientRect === void 0 ? void 0 : tableClientRect.right) !== null && _c !== void 0 ? _c : 0;
        let height = (_d = this.gridTable) === null || _d === void 0 ? void 0 : _d.clientHeight;
        height -= this.getTotalHeaderHeight();
        top += this.getTotalHeaderHeight();
        let styles = [];
        styles.push({ 'height': height + 'px' });
        styles.push({ 'left': left + 'px' });
        styles.push({ 'top': top + 'px' });
        return styles.length > 0 ? styles.reduce((total, current) => total = Object.assign(Object.assign({}, total), current)) : {};
    }
    renderLazyScrollbar() {
        return html `
            <div class="lazy-scrollbar track"
                style=${styleMap(this.getScrollStyle())}
                @click=${this.handleScrollClick}>
                <div class="lazy-scrollbar thumb"
                    style=${`height:${this.scrollThumb.minimum ? 10 : this.scrollThumb.height}px; top:${this.thumbTop}px`}
                    @mousedown=${this.handleDragThumb}>
                </div>
            </div>
        `;
    }
    /** Will scroll incrementally up/down a page in the direction of the click.  */
    handleScrollClick(e) {
        var _a, _b;
        if ((_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.classList) === null || _b === void 0 ? void 0 : _b.contains('thumb'))
            return;
        let totalHeaderHeight = this.getTotalHeaderHeight();
        let shownRows = Math.round((this.gridTable.clientHeight - totalHeaderHeight) / this.computedDataHeight.data);
        let margins = 100 - shownRows;
        let goUp = e.offsetY < this.thumbTop;
        this.scrollDiff = Math.round(margins / 2);
        this.forceScroll = true;
        if (goUp) {
            this.lazyPage[0] -= 100;
            if (this.lazyPage[0] <= 0) {
                this.lazyPage[0] = 0;
                this.scrollDiff = 0;
            }
            this.lazyPage[1] = this.lazyPage[0] + 99;
        }
        else {
            this.lazyPage[1] += 100;
            if (this.lazyPage[1] >= this.visibleData.length - 1) {
                this.lazyPage[1] = this.visibleData.length - 1;
                this.scrolledToBottom = true;
                this.scrollDiff = margins;
            }
            this.lazyPage[0] = this.lazyPage[1] - 99;
        }
        // setting thumb position
        let fractionFromTop = (this.lazyPage[0] + this.scrollDiff) / this.visibleData.length;
        let scrollLength = this.gridTable.clientHeight - totalHeaderHeight;
        if (this.scrollThumb.minimum)
            scrollLength -= (10 - this.scrollThumb.height);
        this.thumbTop = fractionFromTop * scrollLength;
        this.lazyThumb.style.top = this.thumbTop + 'px';
        this.requestUpdate();
    }
    handleDragThumb(e) {
        e.stopPropagation();
        window.removeEventListener('mousemove', this.handleDragThumbMove);
        window.removeEventListener('mouseup', this.handleDragThumbDrop);
        window.addEventListener('mousemove', this.handleDragThumbMove);
        window.addEventListener('mouseup', this.handleDragThumbDrop);
        e.preventDefault();
    }
    /** Repositions lazy scrollbar and filter containers when the window is scrolled */
    handleExternalScroll() {
        var _a, _b, _c, _d;
        let tblParentRect = (_a = this.parentElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        let thumbRect = (_b = this.lazyThumb) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (tblParentRect && thumbRect) {
            this.lazyThumb.style.visibility = 'visible';
            this.lazyThumb.style.pointerEvents = 'auto';
            if (thumbRect.top < tblParentRect.top || thumbRect.bottom > tblParentRect.bottom) {
                this.lazyThumb.style.visibility = 'hidden';
                this.lazyThumb.style.pointerEvents = 'none';
            }
            this.requestUpdate();
        }
        if (this.filterContainer.style.display === 'block') {
            let selectedFilterInput = (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector(`.filter-row[data-internal-index="${(_d = this.selectedHeader) === null || _d === void 0 ? void 0 : _d.internalIndex}"] input`);
            if (this.heightParent && selectedFilterInput && this.isInView(selectedFilterInput, this.heightParent)) {
                let fStyle = this.getFilterContainerStyle();
                this.filterContainer.style.top = fStyle.top;
                this.filterContainer.style.left = fStyle.left;
                this.filterContainer.style.right = fStyle.right;
            }
            else {
                this.toggleFilterContainer(new FocusEvent('blur'), this.selectedHeader);
            }
        }
        ;
        ['secondaryFilterContainer', 'selectContainer'].forEach(containerId => {
            var _a;
            let container = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById(containerId);
            if ((container === null || container === void 0 ? void 0 : container.style.display) === 'block') {
                if (this.heightParent && this.isInView(container, this.heightParent)) {
                    let sStyle = this.getSecondaryFilterContainerStyle(containerId === 'selectContainer');
                    container.style.top = sStyle.top;
                    if (sStyle.left)
                        container.style.left = sStyle.left;
                    if (sStyle.right)
                        container.style.right = sStyle.right;
                }
                else {
                    this.toggleFilterContainer(new FocusEvent('blur'), this.selectedHeader);
                }
            }
        });
    }
    isInView(element, container) {
        let elementRect = element.getBoundingClientRect();
        let containerRect = container.getBoundingClientRect();
        return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom && elementRect.left >= containerRect.left && elementRect.right <= containerRect.right;
    }
    /** Moves the scrollthumb on track, as response to sidescrolling */
    handleHorizontalScroll(e) {
        var _a;
        let newLeft = (_a = e.target) === null || _a === void 0 ? void 0 : _a.scrollLeft;
        if (this._scrollLeft === newLeft)
            return;
        this._scrollLeft = newLeft;
        let computedThumb = this.getHScrollThumbWidthAndLeft();
        if (computedThumb && this.ffHScrollThumb && this.ffHScroll) {
            this.ffHScrollThumb.style.left = computedThumb[1] + 'px';
            let valueNow = Math.round(e.target.scrollLeft / (this.gridTable.scrollWidth - this.gridTable.clientWidth) * 100);
            this.ffHScroll.setAttribute('aria-valuenow', valueNow.toString());
        }
    }
    getHScrollStyle() {
        var _a, _b, _c;
        const r = (_a = this.gridTable) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (!r)
            return {};
        return { top: r.bottom + 'px', left: r.left + 'px', width: ((_c = (_b = this.gridTable) === null || _b === void 0 ? void 0 : _b.clientWidth) !== null && _c !== void 0 ? _c : 0) + 'px', height: '6px' };
    }
    getHScrollThumbWidthAndLeft() {
        if (!this.gridTable)
            return undefined;
        let totalWidth = this.gridTable.scrollWidth;
        let trackWidth = this.gridTable.clientWidth;
        let thumbWidth = Math.max(10, (trackWidth / totalWidth) * trackWidth);
        let thumbLeft = (this.gridTable.scrollLeft / (totalWidth - trackWidth)) * (trackWidth - thumbWidth);
        return [thumbWidth, thumbLeft];
    }
    getHScrollThumbStyle() {
        let computedThumb = this.getHScrollThumbWidthAndLeft();
        if (computedThumb)
            return { width: computedThumb[0] + 'px', left: computedThumb[1] + 'px' };
        return {};
    }
    renderFirefoxHScroll() {
        let valueNow = 0;
        if (this.gridTable && this.gridTable.scrollWidth > this.gridTable.clientWidth) {
            valueNow = Math.round(this.gridTable.scrollLeft / (this.gridTable.scrollWidth - this.gridTable.clientWidth) * 100);
        }
        return html `
            <div class="lazy-hscroll"
                role="scrollbar"
                aria-label="Horizontal scroll"
                aria-controls="grid-table-${this.id_}"
                aria-orientation="horizontal"
                aria-valuenow="${valueNow}"
                aria-valuemin="0"
                aria-valuemax="100"
                style=${styleMap(this.getHScrollStyle())}
                @click=${this.handleHScrollClick}>
                <div class="lazy-hscroll-thumb" style=${styleMap(this.getHScrollThumbStyle())} @mousedown=${this.handleHDragThumb}></div>
            </div>
        `;
    }
    handleHScrollClick(e) {
        var _a, _b, _c, _d, _e;
        if ((_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.classList) === null || _b === void 0 ? void 0 : _b.contains('lazy-hscroll-thumb'))
            return;
        let thumbRect = (_c = this.ffHScrollThumb) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
        let clickedToLeft = e.clientX < ((_d = thumbRect === null || thumbRect === void 0 ? void 0 : thumbRect.left) !== null && _d !== void 0 ? _d : 0) + ((_e = thumbRect === null || thumbRect === void 0 ? void 0 : thumbRect.width) !== null && _e !== void 0 ? _e : 0) / 2;
        let xCoord = clickedToLeft ? Math.max(0, this.gridTable.scrollLeft - 50) : Math.min(this.gridTable.scrollLeft + 50, this.gridTable.scrollWidth - this.gridTable.clientWidth);
        let yCoord = this.gridTable.scrollTop;
        this.gridTable.scrollTo(xCoord, yCoord);
    }
    handleHDragThumb(e) {
        var _a, _b;
        e.stopPropagation();
        let thumbRect = (_a = this.ffHScrollThumb) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        this.ffHScrollThumb.classList.add('dragging');
        this.thumbLeftToDragPoint = e.clientX - ((_b = thumbRect === null || thumbRect === void 0 ? void 0 : thumbRect.left) !== null && _b !== void 0 ? _b : 0);
        window.removeEventListener('mousemove', this.handleHDragMove);
        window.removeEventListener('mouseup', this.handleHDragDrop);
        window.addEventListener('mousemove', this.handleHDragMove);
        window.addEventListener('mouseup', this.handleHDragDrop);
        e.preventDefault();
    }
    exportVisibleData(sepA, sepB, sepC, rowFilter) {
        let outTxt = '';
        sepC = sepC ? sepC : sepB;
        if (this.visibleHeaders && this.visibleData) {
            let rows = this.visibleData;
            if (rowFilter === 'marked') {
                rows = rows.filter(row => row.checked);
            }
            else if (rowFilter === 'selected') {
                const activeIdx = new Set(this.activeRows.map(row => row[0]));
                rows = rows.filter(row => activeIdx.has(row.internalIndex));
            }
            outTxt =
                this.visibleHeaders
                    .filter(hdr => !hdr.value.startsWith(':'))
                    .map(hdr => hdr.value)
                    .join(sepA) + sepC;
            outTxt += rows.map(row => this.visibleHeaders
                .filter(hdr => !hdr.value.startsWith(':'))
                .map(hdr => this.getExportCellValue(hdr, row[hdr.value]))
                .join(sepA)).join(sepB);
        }
        else {
            PIC.getInstance.msg('no data available');
        }
        return outTxt;
    }
    openClientDownloadLink(iBlob, fileName) {
        let link = document.createElement('a');
        link.href = window.URL.createObjectURL(iBlob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
    }
    async fetchPrint(URL, body, fileName) {
        await fetch(URL, {
            method: 'POST',
            headers: [
                ["Content-type", "text/plain;charset=UTF-8"],
                ["picsystem", PIC.systemId]
            ],
            body: body
        }).then(res => {
            if (res.ok) {
                return res.blob();
            }
            else {
            }
        }).then((iBlob) => {
            this.openClientDownloadLink(iBlob, fileName);
        }).catch(err => PIC.getInstance.msg('Download file has failed', err));
    }
    downloadFromPDFSrv(response) {
        let system = PIC.systemId;
        let notekey = response.split('-')[0];
        let username = response.split('-')[1];
        let timestamp = response.split('-')[2].split('.')[0];
        let filetype = response.split('-')[2].split('.')[1];
        let url = `/filestore/DownloadSrv?notekey=${notekey}&username=${username}&timestamp=${timestamp}&filetype=${filetype}&system=${system}`;
        this.fetchPrint(url, '', `Vognliste.${filetype}`);
    }
    /** Cells rendered as graphics (icons, buttons) have no meaningful text value, so export them as empty */
    getExportCellValue(header, cell) {
        var _a, _b, _c;
        if (!cell)
            return undefined;
        const graphicTypes = ['icon', 'faicon', 'button'];
        const cellType = (_b = (_a = header.properties) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : (_c = cell.properties) === null || _c === void 0 ? void 0 : _c.type;
        if (cellType && graphicTypes.includes(cellType))
            return '';
        return cell.value;
    }
    /** Context menu calls eventFnc with the click event, so only accept known filter values */
    toExportRowFilter(rowFilter) {
        return rowFilter === 'marked' || rowFilter === 'selected' ? rowFilter : undefined;
    }
    exportAsCSVComma(rowFilter) {
        PIC.getInstance.msg('exportAsCSVComma clicked');
        let fileName = "print.csv";
        let body = this.exportVisibleData(',', '\n', undefined, this.toExportRowFilter(rowFilter));
        this.openClientDownloadLink(new Blob([body]), fileName);
    }
    exportAsCSV(rowFilter) {
        PIC.getInstance.msg('exportAsCSV clicked');
        let fileName = "print.csv";
        let body = this.exportVisibleData(';', '\n', undefined, this.toExportRowFilter(rowFilter));
        this.openClientDownloadLink(new Blob([body]), fileName);
    }
    exportAsExcel(rowFilter) {
        PIC.getInstance.msg('exportAsExcel clicked');
        let url = "/print/HTML2Print";
        let fileName = "print.xlsx";
        let body = this.exportVisibleData(String.fromCharCode(2), String.fromCharCode(3), String.fromCharCode(1), this.toExportRowFilter(rowFilter));
        if (body.length > 1)
            this.fetchPrint(url, body, fileName);
    }
    exportAsPDF(rowFilter) {
        PIC.getInstance.msg('exportAsPDF clicked');
        let url = "/print/HTML2Print?asPDF=true";
        let fileName = "print.pdf";
        let body = this.exportVisibleData(String.fromCharCode(2), String.fromCharCode(3), String.fromCharCode(1), this.toExportRowFilter(rowFilter));
        if (body.length > 1)
            this.fetchPrint(url, body, fileName);
    }
    openPrintRequester() {
        let template = document.createElement('template');
        template.innerHTML = this.printRequester;
        if (template.content && template.content.firstChild) {
            let printReqNode = template.content.firstChild;
            let nodeId = printReqNode.id;
            let foundNode = document.querySelector('#' + nodeId);
            if (foundNode) {
                document.body.removeChild(foundNode);
            }
            document.body.appendChild(template.content.firstChild);
        }
    }
    openJobQueue() {
        let prog2run = 'util/bjobqueue';
        let params = '';
        loadPage(prog2run, params, { isNew: true });
    }
    handleNewButton(e) {
        let action = new PICAction();
        let optObj = {};
        Object.assign(optObj, { 'type': 'doSubmit' });
        Object.assign(optObj, { 'event': 'createNewTableItem' });
        action.actionOptions = optObj;
        action.triggerEvent(e);
    }
    toggleSettings(e) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        let target = e.target;
        if (e instanceof FocusEvent) {
            let focusReceiver = e.relatedTarget;
            if (focusReceiver && (focusReceiver.classList.contains('settings-container') || focusReceiver.classList.contains('col-toggle') || focusReceiver.classList.contains('reset-columns-button'))) {
                //do nothing
            }
            else {
                this.settingsOpen = false;
            }
        }
        if (e instanceof KeyboardEvent) {
            if (e.key === 'Enter') {
                if (target.classList.contains('tableSettingsButton'))
                    this.settingsOpen = !this.settingsOpen;
            }
            if (e.key === 'Escape') {
                this.settingsOpen = false;
                (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.tableSettingsButton')) === null || _b === void 0 ? void 0 : _b.focus();
            }
            if (e.key === 'Tab') {
                if (target.classList.contains('tableSettingsButton') && this.settingsOpen) {
                    if (e.shiftKey)
                        e.preventDefault();
                    e.stopImmediatePropagation();
                    let focusReceiver = e.shiftKey ? (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector('.reset-columns-button') : (_d = this.shadowRoot) === null || _d === void 0 ? void 0 : _d.querySelector('.col-toggle');
                    focusReceiver === null || focusReceiver === void 0 ? void 0 : focusReceiver.focus();
                }
                if (target.classList.contains('reset-columns-button') && !e.shiftKey) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    let focusReceiver = (_e = this.shadowRoot) === null || _e === void 0 ? void 0 : _e.querySelector('.tableSettingsButton');
                    focusReceiver === null || focusReceiver === void 0 ? void 0 : focusReceiver.focus();
                }
            }
            if (e.key === 'ArrowDown') {
                if (target.classList.contains('tableSettingsButton') && this.settingsOpen) {
                    (_g = (_f = this.shadowRoot) === null || _f === void 0 ? void 0 : _f.querySelector('.col-toggle')) === null || _g === void 0 ? void 0 : _g.focus();
                }
                if (target.classList.contains('col-toggle')) {
                    let lastSwitch = (_h = this.shadowRoot) === null || _h === void 0 ? void 0 : _h.querySelector('.settings-container .settingsmenuitem:last-of-type .col-toggle');
                    if (lastSwitch === target) {
                        (_k = (_j = this.shadowRoot) === null || _j === void 0 ? void 0 : _j.querySelector('.reset-columns-button')) === null || _k === void 0 ? void 0 : _k.focus();
                    }
                    else {
                        lastSwitch === null || lastSwitch === void 0 ? void 0 : lastSwitch.focus();
                    }
                }
                if (target.classList.contains('reset-columns-button')) {
                    (_m = (_l = this.shadowRoot) === null || _l === void 0 ? void 0 : _l.querySelector('.tableSettingsButton')) === null || _m === void 0 ? void 0 : _m.focus();
                }
            }
            if (e.key === 'ArrowUp') {
                if (target.classList.contains('tableSettingsButton') && this.settingsOpen) {
                    (_p = (_o = this.shadowRoot) === null || _o === void 0 ? void 0 : _o.querySelector('.settings-container .settingsmenuitem:last-of-type .col-toggle')) === null || _p === void 0 ? void 0 : _p.focus();
                }
                if (target.classList.contains('col-toggle')) {
                    let firstSwitch = (_q = this.shadowRoot) === null || _q === void 0 ? void 0 : _q.querySelector('.col-toggle');
                    if (firstSwitch === target) {
                        (_s = (_r = this.shadowRoot) === null || _r === void 0 ? void 0 : _r.querySelector('.tableSettingsButton')) === null || _s === void 0 ? void 0 : _s.focus();
                    }
                    else {
                        firstSwitch === null || firstSwitch === void 0 ? void 0 : firstSwitch.focus();
                    }
                }
                if (target.classList.contains('reset-columns-button')) {
                    (_u = (_t = this.shadowRoot) === null || _t === void 0 ? void 0 : _t.querySelector('.settings-container .settingsmenuitem:last-of-type .col-toggle')) === null || _u === void 0 ? void 0 : _u.focus();
                }
            }
        }
        if (e instanceof PointerEvent) {
            this.settingsOpen = !this.settingsOpen;
        }
        this.requestUpdate();
    }
    handleDragSettingsRow(e, rowIdx) {
        var _a, _b;
        let settingsItemOnList = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.settingsmenuitem.item-${rowIdx}`);
        let eventType = e.type;
        if (settingsItemOnList) {
            if (eventType == 'dragstart') {
                if (e.dataTransfer) {
                    e.dataTransfer.setData('text/plain', '' + rowIdx);
                    e.dataTransfer.setDragImage(settingsItemOnList, 0, 0);
                }
            }
            else if (eventType == 'dragover') {
                e.preventDefault();
                if (e.dataTransfer)
                    e.dataTransfer.dropEffect = 'move';
                this.activeDragRow = rowIdx;
            }
            else if (eventType == 'drop') {
                let testArr = this.visibleHeaders.concat(this.invisibleHeaders);
                let oldIdx = Number((_b = e.dataTransfer) === null || _b === void 0 ? void 0 : _b.getData('text'));
                let newIdx = Number(rowIdx);
                oldIdx = testArr.findIndex(hdr => hdr.internalIndex == oldIdx);
                newIdx = testArr.findIndex(hdr => hdr.internalIndex == newIdx);
                testArr.splice(newIdx, 0, testArr.splice(oldIdx, 1)[0]);
                this.visibleHeaders = testArr.filter(h => !h.hidden);
                this.invisibleHeaders = testArr.filter(h => h.hidden);
                this.toggledHeaders = testArr;
                this.activeDragRow = undefined;
                if (this.saveToggledColumns) {
                    PIC.getInstance.storage.setItem('webTableToggledFilters-' + this.id_, JSON.stringify(testArr, this.replacer), 'internal');
                }
                this.orderHelper = [];
                testArr.forEach((hdr) => this.orderHelper.push(hdr.internalIndex));
                this.requestUpdate();
            }
        }
    }
    handleDragSettingsContainer(e) {
        let eventType = e.type;
        if (eventType == 'dragover') {
            e.preventDefault();
            if (e.dataTransfer)
                e.dataTransfer.dropEffect = 'move';
        }
        else if (eventType == 'drop') {
            this.activeDragRow = undefined;
        }
    }
    renderSettingsContainer() {
        var _a, _b, _c, _d;
        let renderArr = this.visibleHeaders.concat(this.invisibleHeaders);
        let settingsBtnBCR = (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.tableBtn.tableSettingsButton')) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        let top = settingsBtnBCR ? (settingsBtnBCR === null || settingsBtnBCR === void 0 ? void 0 : settingsBtnBCR.bottom) + 5 + 'px' : '0';
        let right = window.innerWidth - ((_c = settingsBtnBCR === null || settingsBtnBCR === void 0 ? void 0 : settingsBtnBCR.right) !== null && _c !== void 0 ? _c : 0) + 'px';
        const containerStyle = {
            'top': top,
            'right': right,
        };
        if (this.orderHelper.length == 0) {
            for (let i = 0; i < renderArr.length; i++) {
                this.orderHelper.push((_d = renderArr[i].internalIndex) !== null && _d !== void 0 ? _d : 0);
            }
        }
        return html `
            <div 
                class="settings-container"
                tabindex="0"
                style=${styleMap(containerStyle)}
                @drop=${(e) => this.handleDragSettingsRow(e)}
                @dragover=${(e) => this.handleDragSettingsContainer(e)}
                @dragenter=${(e) => this.handleDragSettingsContainer(e)}
                @dragleave=${(e) => this.handleDragSettingsContainer(e)}
                @blur=${(e) => this.toggleSettings(e)}
            >
                ${repeat(renderArr, (hdr) => hdr.internalIndex, (hdr, idx) => {
            if (hdr.type === 'subTable')
                return;
            if (hdr.text.length > 0) {
                let columnToggleAction = new PICAction();
                columnToggleAction.type_ = 'internal';
                columnToggleAction.eventFnc = this.toggleColumn.bind(this, hdr, renderArr);
                return html `
                            <div 
                                class="settingsmenuitem item-${hdr.internalIndex} pic-body2"
                                @drop=${(e) => this.handleDragSettingsRow(e, hdr.internalIndex)}
                                @dragover=${(e) => this.handleDragSettingsRow(e, hdr.internalIndex)}
                                @dragenter=${(e) => this.handleDragSettingsRow(e, hdr.internalIndex)}
                                @dragleave=${(e) => this.handleDragSettingsRow(e, hdr.internalIndex)}
                                @dragstart=${(e) => this.handleDragSettingsRow(e, hdr.internalIndex)}
                            >   
                                <pic-checkbox 
                                    class="col-toggle col-settings"
                                    @blur=${(e) => this.toggleSettings(e)}
                                    @keydown=${(e) => this.toggleSettings(e)}
                                    .isinput__=${false}
                                    .eType=${'switch'}
                                    .density=${'low'}
                                    .width=${'40px'}
                                    .tooltip_=${'Visible'}
                                    .value=${hdr.hidden ? 'false' : 'true'}
                                    .clickAction=${columnToggleAction}
                                >
                                </pic-checkbox>
                                <label style="margin-right: auto; margin-left: 20px;">${hdr.text}</label>
                                <div class="settingsmenuitemdragicon" draggable="true"> </div>
                            </div>
                        `;
            }
        })}
                <button style="float: right;cursor: pointer;" class="pic-button reset-columns-button" type="button" @click=${this.resetColSettings} @keydown=${(e) => this.toggleSettings(e)}>Reset Columns</button>
            </div>
        `;
    }
    handleExportButtonClick(e) {
        let cont = document.querySelector("pic-context[context-x]");
        // = this.contextMenu;
        if (cont) {
            cont.shouldHide = false;
            let context_ = [];
            this.export.forEach(expType => {
                switch (expType) {
                    case 'PDF':
                        context_.push({
                            menuTxt: 'Export as PDF',
                            eventTxt: 'PDF',
                            eventFnc: this.exportAsPDF.bind(this)
                        });
                        break;
                    case 'Excel':
                        context_.push({
                            menuTxt: 'Export as Excel',
                            eventTxt: 'Excel',
                            eventFnc: this.exportAsExcel.bind(this)
                        });
                        break;
                    case 'CSV':
                        context_.push({
                            menuTxt: 'Export as CSV',
                            eventTxt: 'CSV',
                            eventFnc: this.exportAsCSV.bind(this)
                        });
                        break;
                    case 'CSVC':
                        context_.push({
                            menuTxt: 'Export as CSV (Comma separated)',
                            eventTxt: 'CSVC',
                            eventFnc: this.exportAsCSVComma.bind(this)
                        });
                        break;
                    case 'PrintRequester':
                        context_.push({
                            menuTxt: 'Open Print Request',
                            eventTxt: 'PrintRequester',
                            eventFnc: this.openPrintRequester.bind(this)
                        });
                        break;
                    case 'JobQueue':
                        context_.push({
                            menuTxt: 'Open Print Queue',
                            eventTxt: 'JobQueue',
                            eventFnc: this.openJobQueue.bind(this)
                        });
                        break;
                }
            });
            if (this.checkbox) {
                this.pushFilteredExportSubMenu(context_, 'marked', 'Export Marked Only', 'ExportMarkedOnly', 'Marked');
            }
            if (this.selectionMode !== 'none') {
                this.pushFilteredExportSubMenu(context_, 'selected', 'Export Selected Only', 'ExportSelectedOnly', 'Selected');
            }
            cont.data = {
                clickEvent: e,
                context: context_,
            };
        }
    }
    pushFilteredExportSubMenu(context_, rowFilter, menuTxt, eventTxt, eventSuffix) {
        let subContext_ = [];
        this.export.forEach(expType => {
            switch (expType) {
                case 'PDF':
                    subContext_.push({
                        menuTxt: 'Export as PDF',
                        eventTxt: 'PDF' + eventSuffix,
                        eventFnc: this.exportAsPDF.bind(this, rowFilter)
                    });
                    break;
                case 'Excel':
                    subContext_.push({
                        menuTxt: 'Export as Excel',
                        eventTxt: 'Excel' + eventSuffix,
                        eventFnc: this.exportAsExcel.bind(this, rowFilter)
                    });
                    break;
                case 'CSV':
                    subContext_.push({
                        menuTxt: 'Export as CSV',
                        eventTxt: 'CSV' + eventSuffix,
                        eventFnc: this.exportAsCSV.bind(this, rowFilter)
                    });
                    break;
                case 'CSVC':
                    subContext_.push({
                        menuTxt: 'Export as CSV (Comma separated)',
                        eventTxt: 'CSVC' + eventSuffix,
                        eventFnc: this.exportAsCSVComma.bind(this, rowFilter)
                    });
                    break;
            }
        });
        if (subContext_.length > 0) {
            context_.push({
                menuTxt: menuTxt,
                eventTxt: eventTxt,
                eventFncKey: 'subMenu',
                subContext: subContext_
            });
        }
    }
    renderExportButton() {
        return html `
            <div tabindex="0" class="tableBtn tableExportButton" @click=${this.handleExportButtonClick}></div>
        `;
    }
    toggleFilters() {
        var _a, _b;
        if (this.filterRowIsVisible) {
            this.dispatchEvent(new CustomEvent('filterreset', { detail: { value: 'filters' } }));
            this.setCheckValues('all');
        }
        let currentScrollFraction = 0;
        // Preserve the current scroll fraction before toggling (if using lazy render)
        if (this.useLazyRender && this.lazyThumb && this.gridTable) {
            let headerHeight = this.getTotalHeaderHeight();
            let scrollLength = this.gridTable.clientHeight - headerHeight;
            if (this.scrollThumb.minimum)
                scrollLength -= (10 - this.scrollThumb.height);
            currentScrollFraction = scrollLength > 0 ? this.thumbTop / scrollLength : 0;
            this.updateComplete.then(() => {
                headerHeight = this.getTotalHeaderHeight();
                scrollLength = this.gridTable.clientHeight - headerHeight;
                if (this.scrollThumb.minimum)
                    scrollLength -= (10 - this.scrollThumb.height);
                this.thumbTop = currentScrollFraction * scrollLength;
                this.lazyThumb.style.top = this.thumbTop + 'px';
            });
        }
        this.filterRowIsVisible = !this.filterRowIsVisible;
        this.filterRowIsVisible ? (_a = this.filtersToggleBtn) === null || _a === void 0 ? void 0 : _a.classList.add('selected') : (_b = this.filtersToggleBtn) === null || _b === void 0 ? void 0 : _b.classList.remove('selected');
        this.setScrollbarButtonHeight();
    }
    applySavedFilterVals() {
        this.activeFilters.filters.forEach((value, key) => {
            var _a;
            let header = this.visibleHeaders.find((h) => h.value === key);
            if (header) {
                let fType = value.filterType;
                let fVal = value.value;
                header.filter = fType;
                switch (fType) {
                    case 'between':
                        this.setRangeValues(fVal);
                        break;
                    case 'select':
                        this.setCheckValues(fVal);
                        break;
                    default: {
                        let input = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.filter-row[data-internal-index="${header.internalIndex}"] input`);
                        if (input) {
                            input.value = fVal;
                        }
                    }
                }
            }
        });
    }
    replacer(key, value) {
        if (value instanceof Map) {
            return {
                dataType: 'Map',
                value: Array.from(value.entries()),
            };
        }
        else {
            return value;
        }
    }
    reviver(key, value) {
        if (typeof value === 'object' && value !== null) {
            if (value.dataType === 'Map') {
                return new Map(value.value);
            }
            ;
        }
        ;
        return value;
    }
    handleSavedToggles() {
        if (this.saveToggledColumns && PIC.getInstance.storage.getItem('webTableToggledFilters-' + this.id_, 'internal')) {
            let isSame = true;
            let savedHdrs = JSON.parse(PIC.getInstance.storage.getItem('webTableToggledFilters-' + this.id_, 'internal'), this.reviver);
            savedHdrs.forEach((hdr) => {
                if (!this.visibleHeaders.find((h) => h.value === hdr.value)) {
                    isSame = false;
                }
            });
            if (isSame && this.visibleHeaders.length === savedHdrs.length)
                this.visibleHeaders = savedHdrs;
        }
    }
    toggleColumn(header, headerArr) {
        header.hidden = !header.hidden;
        this.toggledHeaders = headerArr;
        if (this.saveToggledColumns) {
            PIC.getInstance.storage.setItem('webTableToggledFilters-' + this.id_, JSON.stringify(headerArr, this.replacer), 'internal');
        }
        ;
        this.requestUpdate();
    }
    sortVisibleHeaders() {
        if (this.orderHelper.length > 0) {
            let newVisible = [];
            this.orderHelper.forEach(n => {
                let candidate = this.visibleHeaders.find(e => e.internalIndex == n);
                if (candidate)
                    newVisible.push(candidate);
            });
            this.visibleHeaders = newVisible;
        }
    }
    //TODO: cleanup, unused custom eventhandler, invisible headers should be empty, is the sort even needed data vs vissibleData
    resetColSettings() {
        var _a;
        if (this.toggledHeaders)
            this.toggledHeaders = null;
        let tmpArr = this.visibleHeaders.concat(this.invisibleHeaders);
        let menuItemToggle = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.col-toggle');
        if (this.saveToggledColumns)
            PIC.getInstance.storage.removeItem('webTableToggledFilters-' + this.id_, 'internal');
        if (this.resizeColumns)
            PIC.getInstance.storage.removeItem('resizedHeaders-' + this.id_, 'session');
        tmpArr.forEach(o => {
            if (o.hidden)
                o.hidden = false;
            if (this.resizeColumns) {
                if (o.properties) {
                    let oldProps = this.data.headers.find(hdr => hdr.value === o.value);
                    if (oldProps) {
                        if (oldProps.properties)
                            o.properties = structuredClone(oldProps.properties);
                        else
                            delete o.properties;
                    }
                }
            }
        });
        tmpArr.sort((a, b) => { return b.internalIndex - a.internalIndex; });
        this.orderHelper = [];
        tmpArr.forEach((o) => this.orderHelper.push(o.internalIndex));
        this.invisibleHeaders = [];
        this.visibleHeaders = tmpArr;
        menuItemToggle === null || menuItemToggle === void 0 ? void 0 : menuItemToggle.forEach((itm) => {
            itm.inputField.checked = true;
            itm.requestUpdate();
        });
        this.dispatchEvent(new CustomEvent('columnreset', { detail: { value: 'columns' } }));
        this.requestUpdate();
    }
    renderFilterSearchField(filter) {
        var _a;
        return html `
			<div class="info-filter filter-single">
				<input type="text" class='pic-body2' placeholder="Search" value=${(_a = this.activeFilters.main) !== null && _a !== void 0 ? _a : ''} @input=${(e) => {
            let targetVal = e.target.value;
            this.addActiveFilter(targetVal, null);
        }}/>
			</div>
		`;
    }
    setScrollbarButtonHeight() {
        let baseHeight = this.compactHeaderMode ? 1.85 : 2.23;
        let filterHeight = this.filterRowIsVisible ? 2.2 : 0;
        let totalHeight = baseHeight + filterHeight;
        this.style.setProperty('--scrollbar-button-height', `${totalHeight}rem`);
    }
    getHeaderClass(header) {
        var _a;
        this.setScrollbarButtonHeight();
        return {
            'compactheader': this.compactHeaderMode,
            'grid-cell': true,
            'grid-sortable': ((_a = header.properties) === null || _a === void 0 ? void 0 : _a.sortable) && this.sort ? true : false,
            ['grid-sortable-' + header.currSort]: header.currSort ? true : false,
            'grid-cellRight': this.visibleData.find(c => { var _a, _b; return ((_b = (_a = c[header.value]) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.type) == 'number'; }) ? true : false
        };
    }
    createResizeOverlay(parent) {
        var _a, _b, _c, _d;
        this.deleteResizeOverlay();
        let overlay = document.createElement("div");
        overlay.classList.add('resize-overlay');
        if (isNaN(parseInt(getComputedStyle(parent).zIndex)))
            overlay.style.zIndex = '15';
        else
            overlay.style.zIndex = (parseInt(getComputedStyle(parent).zIndex) + 5).toString();
        overlay.style.position = 'absolute';
        overlay.style.top = parent.getBoundingClientRect().top.toString() + 'px';
        overlay.style.height = getComputedStyle(this.gridTable).height;
        overlay.style.left = parent.getBoundingClientRect().left.toString() + 'px';
        let width = this.getTotalHeaderWidth(parent);
        overlay.style.width = width.toString() + 'px';
        let minWidth = (_c = (_b = (_a = this.visibleHeaders.find(hdr => hdr.internalIndex === parent.item.internalIndex)) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.minWidth) !== null && _c !== void 0 ? _c : width;
        overlay.style.minWidth = minWidth.toString() + 'px';
        overlay.dataset.headerIndex = (_d = parent.item.internalIndex) === null || _d === void 0 ? void 0 : _d.toString();
        this.gridTable.appendChild(overlay);
    }
    deleteResizeOverlay() {
        if (this.overlay)
            this.gridTable.removeChild(this.overlay);
    }
    getTotalHeaderWidth(element) {
        let compStyles = getComputedStyle(element);
        let padR = parseInt(compStyles.getPropertyValue("padding-right"));
        let padL = parseInt(compStyles.getPropertyValue("padding-left"));
        let computedTotalWidth = parseInt(compStyles.getPropertyValue("width")) + padR + padL;
        return computedTotalWidth;
    }
    resizeMouseDown(e, header) {
        let headerElement = Array.from(this.gridTableHeader).find(h => h.item.internalIndex === header.internalIndex);
        if (headerElement) {
            this.createResizeOverlay(headerElement);
            window.addEventListener('mousemove', this.resizeDragHandler);
            window.addEventListener('mouseup', this.resizeDropHandler);
        }
    }
    renderTableHeader() {
        return repeat(this.actuallyVisibleHeaders, (header) => header.internalIndex, (header, idx) => {
            return html `${this.renderHeaderCell(header)}`;
        });
    }
    renderCheckBoxHeaderCell(header) {
        let gridCheckBoxAction = new PICAction();
        gridCheckBoxAction.type_ = 'internal';
        gridCheckBoxAction.eventFnc = ((e) => this.toggleAllCheckboxes(e)).bind(this);
        let checkBoxElement = html `<div style="resize: none;" class="grid-checkbox">
            <pic-checkbox 
                id=${this.id + '-checkbox-toggle'}
                class='checkbox-toggle' 
                tabindex='-1'
                .isinput__=${false}
                .eType=${'default'} 
                .density=${'inline'}
                .minFitContent=${false}
                .width=${'40px'} 
                .clickAction=${gridCheckBoxAction}
                >
            </pic-checkbox>
        </div>`;
        return this.renderDefaultHeaderCell(header, checkBoxElement, () => false, () => false);
    }
    renderTreeHeaderCell(header) {
        let treeElement = html `
                    <div style="visibility: hidden; resize: none;" 
                        class="grid-treeCaret" 
                        @click=${(event) => this.toggleAllTrees(event)}
                    ></div>
                `;
        return this.renderDefaultHeaderCell(header, treeElement, () => false, () => false);
    }
    hideTooltip() {
        return {
            'hideheadertooltip': this.hideheadertooltip,
        };
    }
    renderDefaultHeaderCell(header, innerHtml, resizeHander, clickHandler, mousedownHandler) {
        var _a, _b, _c, _d;
        if (!clickHandler)
            clickHandler = (e) => {
                this.sortHeader(header);
            };
        if (!mousedownHandler)
            mousedownHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.resizeMouseDown(e, header);
            };
        if (!innerHtml)
            innerHtml = html `<span class=${classMap(this.classAdder(this.getGridCellSpanClass(header), 'pic-overline'))} style=${styleMap(this.getHeaderTextStyle())}>${header.text}</span>`;
        let tooltipPlacement = ['bottom'];
        return html `
            <div    style=${styleMap(this.getGridCellStyle(header, true))}
                    .item=${header} 
                    class=${classMap(this.getHeaderClass(header))}
                    @click=${clickHandler}
                >
                ${innerHtml}
              ${header.type === 'checkbox' ? nothing : html `<pic-tooltip class=${classMap(this.hideTooltip())} .allowedPlacements=${tooltipPlacement} .offset=${9}>${(_d = (_b = (_a = header.properties) === null || _a === void 0 ? void 0 : _a.toolTip) !== null && _b !== void 0 ? _b : (_c = header.properties) === null || _c === void 0 ? void 0 : _c.hdrtitle) !== null && _d !== void 0 ? _d : header.text}</pic-tooltip>`}
              ${this.resizeColumns && !this.autowidth && ![':checkbox', ':tree', ':move', ':removal'].includes(header.value)
            ? html `<div class="header-resize-handle" @mousedown=${mousedownHandler} title="Drag to resize column"></div>`
            : nothing}
            </div> 
            `;
    }
    renderHeaderCell(header) {
        switch (header.type) {
            case 'checkbox':
                return this.renderCheckBoxHeaderCell(header);
            case 'tree':
                return this.renderTreeHeaderCell(header);
            case 'subTable':
                break;
            case 'string':
            case 'number':
            case 'modal':
            default:
                return this.renderDefaultHeaderCell(header);
        }
    }
    loadSubTableData(target, rowItemData) {
        if (target.classList.contains('grid-treeCaretDown')) {
            target.classList.toggle('grid-treeCaretDown');
            rowItemData.treeToggeled = false;
        }
        else {
            target.classList.toggle('grid-treeCaretDown');
            rowItemData.treeToggeled = true;
            /* check for subtabels */
            let subTabels = this.data.headers.filter(hdr => hdr.type == 'subTable')
                .map(subTable => rowItemData[subTable.value])
                .filter(subTable => !!subTable);
            if (subTabels && subTabels.length > 0) {
                //Do no fetch from API
            }
            else {
                let options = {
                    // sessionId: PIC.sessionCode,
                    systemId: PIC.systemId,
                    postBody: {
                        mode: 'getRowSubData',
                        item: rowItemData
                    },
                };
                fetchApiData(this.src, options)
                    .then((data) => {
                    PIC.getInstance.msg('API returned subtable data', data);
                    this.addSubTable(data, rowItemData);
                    this.requestUpdate();
                }).catch(() => target.classList.toggle('grid-treeCaretDown'));
            }
        }
        this.requestUpdate();
    }
    getTreeCarretClass() {
        return { '.grid-treeCaret': true, '.grid-treeCaretDown': true };
    }
    toggleTree(event) {
        let target = (event.target);
        let parentRow = target.closest('.grid-row');
        this.loadSubTableData(target, parentRow === null || parentRow === void 0 ? void 0 : parentRow.item);
    }
    toggleAllTrees(event) {
        let target = (event.target);
        target.classList.toggle('grid-treeCaretDown');
        this.requestUpdate();
    }
    toggleAllCheckboxes(event) {
        let target = (event.target);
        if (target && target.type == 'checkbox') {
            if (target.checked) {
                this._checkboxAll.forEach(node => {
                    node.setChecked(true);
                    let datanode = node.closest('.grid-row');
                    this.setCheckedRow(datanode.item, true);
                });
            }
            else {
                this._checkboxAll.forEach(node => {
                    node.setChecked(false);
                    let datanode = node.closest('.grid-row');
                    this.setCheckedRow(datanode.item, false);
                });
            }
            if (this.boundCanvas)
                this.sendCheckedRowsToBoundCanvas();
        }
        this.requestUpdate();
    }
    handleSelectAction() {
    }
    addNewRow() {
        var _a;
        let newRow = structuredClone(this.visibleData[this.visibleData.length - 1]);
        if (this.visibleData.length === 0) {
            if (this.data.data.length > 0)
                newRow = structuredClone(this.data.data[this.data.data.length - 1]);
            else {
                newRow = { internalIndex: 1 };
                this.visibleHeaders.forEach(hdr => {
                    let hVal = hdr.value;
                    let hObj = {};
                    Object.defineProperty(hObj, hVal, {
                        value: undefined
                    });
                    Object.assign(newRow, hObj);
                });
            }
        }
        newRow.internalIndex = this.visibleData.length + 1;
        if ((_a = newRow.__properties__) === null || _a === void 0 ? void 0 : _a.__submitValue__)
            newRow.__properties__.__submitValue__ = '';
        this.visibleHeaders.forEach(hdr => {
            if (hdr.type === 'string')
                newRow[hdr.value].value = '';
            if (hdr.type === 'number')
                newRow[hdr.value].value = 0;
            if (hdr.type === 'boolean')
                newRow[hdr.value].value = false;
        });
        this.visibleData.push(newRow);
        this.rowAdded = true;
        this.requestUpdate();
    }
    deleteRow(row) {
        this.visibleData.splice(this.visibleData.indexOf(row), 1);
        this.requestUpdate();
        if (this.boundCanvas) {
            this.sendEditableFieldsToBoundCanvas();
        }
    }
    handleDragTableRow(e) {
        let moveStart = e.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('grid-move'); });
        let dragRow = e.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('grid-row'); });
        if (e.type === 'mousedown') {
            if (dragRow && moveStart)
                this.draggedRow = dragRow.item.internalIndex;
        }
        if (e.type === 'mouseup') {
            if (dragRow && this.draggedRow) {
                let testArr = this.visibleData;
                let oldIdx = this.draggedRow;
                let newIdx = dragRow.item.internalIndex;
                if (oldIdx !== newIdx) {
                    oldIdx = testArr.findIndex(r => r.internalIndex === oldIdx);
                    newIdx = testArr.findIndex(r => r.internalIndex === newIdx);
                    testArr.splice(newIdx, 0, testArr.splice(oldIdx, 1)[0]);
                    this.visibleData = testArr;
                    this.draggedRow = undefined;
                    this.dragging = true;
                    this.requestUpdate();
                    if (this.boundCanvas && !this.readOnly) {
                        this.sendEditableFieldsToBoundCanvas();
                    }
                }
            }
        }
    }
    //Checks if no active row set, if not then sets one. Fixes selection issues via tab. And escape
    checkAndSetActiveRow(e) {
        if (e)
            return;
        if (this.visibleData && this.visibleData.length > 0 && !this.activeBaseRow && !this.activeEndRow && this.activeRows.length == 0) {
            const row = [this.visibleData[0].internalIndex, 0];
            this.activeBaseRow = row;
            this.activeEndRow = row;
            this.activeRows = [row];
        }
    }
    handleFocusOnTable(shiftKey = false) {
        var _a, _b, _c;
        if (shiftKey) {
            this.checkAndSetActiveRow();
            (_a = this.gridTable) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            if (!this.infoheader) {
                this.checkAndSetActiveRow();
                (_b = this.gridTable) === null || _b === void 0 ? void 0 : _b.focus();
            }
            else {
                if (this.headerFilterMain) {
                    this.headerFilterMain.focus();
                }
                else {
                    (_c = this.tableBtns[0]) === null || _c === void 0 ? void 0 : _c.focus();
                }
            }
        }
    }
    handleCellTextCopy(cellText) {
        navigator.clipboard.writeText(cellText);
    }
    getCellTextCopyContext(cellText) {
        return {
            menuTxt: 'Copy To Clipboard',
            eventTxt: cellText !== null && cellText !== void 0 ? cellText : '',
            eventFncKey: 'apiRequest',
            eventFnc: this.handleCellTextCopy.bind(this, cellText)
        };
    }
    handleInputUpdate(e, header, cell) {
        let target = e.target;
        if (!target.classList.contains('inputCell'))
            return;
        cell.value = target.value; //overwrite initial value in data, so it can be sent to the backend
        if (this.boundCanvas && !this.readOnly) {
            this.sendEditableFieldsToBoundCanvas();
        }
    }
    handleTableClick(event) {
        var _a, _b, _c;
        let filterRow = event.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('filter-row'); });
        let filterContainer = event.composedPath().find(x => x.id === 'filterContainer');
        let selectContainer = event.composedPath().find(x => x.id === 'selectContainer');
        let secondaryFilterContainer = event.composedPath().find(x => x.id === 'secondaryFilterContainer');
        if (this.filterRowIsVisible) {
            if (!filterContainer && !selectContainer && !secondaryFilterContainer && !filterRow) {
                this.selectedHeader = null;
                this.requestUpdate();
            }
        }
        if (this.dragging) {
            this.dragging = false;
            return;
        }
        if (this.allowDblclick && this.selectionMode == 'multi') {
            this.clickCount++;
        }
        // don't fire events while selecting rows.
        if ((event.shiftKey || event.ctrlKey) && this.selectionMode == 'multi')
            return;
        let clickedRow = event.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('grid-row'); });
        let clickedCell = event.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('grid-cell'); });
        let clickedNewRow = event.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('new-row'); });
        if (this.isLookUp && (clickedRow || clickedCell)) {
            let action = new PICAction();
            action.actionOptions = {
                type: 'doSubmit',
                event: 'pullback'
            };
            action.parms_ += ',selectedvalues=' + this.selectedRowValues;
            action.trigger_ = 'click';
            action.triggerEvent(event);
            return;
        }
        if (clickedRow && clickedCell) {
            let clickEventOptions;
            if ((_b = (_a = clickedCell.item) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.__clickEvent__) {
                let clickEvent = clickedCell.item.properties.__clickEvent__;
                if (clickEvent.indexOf('{') > -1) {
                    clickEventOptions = clickEvent;
                }
                else {
                    clickEventOptions = JSON.stringify({
                        type: 'doSubmit',
                        event: clickEvent
                    });
                }
            }
            else if (((_c = clickedRow.item.__properties__) === null || _c === void 0 ? void 0 : _c.__clickEvent__) && !handledByCell(clickedCell)) {
                let clickEvent = clickedRow.item.__properties__.__clickEvent__;
                if (clickEvent.indexOf('{') > -1) {
                    clickEventOptions = clickEvent;
                }
                else {
                    clickEventOptions = JSON.stringify({
                        type: 'doSubmit',
                        event: clickEvent
                    });
                }
            }
            else if (this.data.submitEvent) {
                clickEventOptions = "{'type': 'doSubmit', 'event': '" + this.data.submitEvent + '"}';
            }
            if (clickEventOptions) {
                let action = new PICAction();
                action.actionOptions = objectDecoder(clickEventOptions);
                if (this.allowDblclick && this.selectionMode == 'multi') {
                    this.clickTimeout = setTimeout(() => {
                        if (this.allowDblclick && this.selectionMode == 'multi') {
                            if (this.clickCount > 1) {
                                action.parms_ += ',selectedvalues=' + this.selectedRowValues;
                                action.triggerEvent(event);
                                clearTimeout(this.clickTimeout);
                                this.clickCount = 0;
                            }
                            else {
                                clearTimeout(this.clickTimeout);
                                this.clickCount = 0;
                            }
                        }
                    }, 400);
                }
                else {
                    action.parms_ += ',selectedvalues=' + this.selectedRowValues;
                    action.triggerEvent(event);
                }
            }
            else if (this.editable) {
                let removeCell = event.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('grid-remove'); });
                if (removeCell)
                    return;
                let idx = clickedRow.item.internalIndex;
                let rowIdx = this.getRowIdxFromInternal(idx);
                let row = [this.visibleData[rowIdx].internalIndex, rowIdx];
                if (!this.activeRows.find(r => JSON.stringify(r) == JSON.stringify(row)))
                    this.handleRowClick(event, idx);
            }
        }
        else if (clickedNewRow)
            this.addNewRow();
        //Checks if cell is checkboxcell, could be expanded
        function handledByCell(iCell) {
            if (!iCell) {
                return false;
            }
            else if (iCell.firstElementChild) {
                if ((iCell.firstElementChild instanceof PICCheckBox))
                    return true;
            }
            return false;
        }
    }
    handleContextMenu(event) {
        var _a, _b, _c, _d, _e, _f, _g;
        let filterRow = event.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('filter-row'); });
        let filterContainer = event.composedPath().find(x => x.id === 'filterContainer');
        let selectContainer = event.composedPath().find(x => x.id === 'selectContainer');
        let secondaryFilterContainer = event.composedPath().find(x => x.id === 'secondaryFilterContainer');
        if (this.filterRowIsVisible) {
            if (!filterContainer && !selectContainer && !secondaryFilterContainer && !filterRow) {
                this.selectedHeader = null;
                this.requestUpdate();
            }
        }
        this.contextMenu.hide();
        if (this.isLookUp) {
            return;
        }
        // Will make right click select row
        let clickedRow = event.composedPath().find(x => { var _a; return (_a = x.classList) === null || _a === void 0 ? void 0 : _a.contains('grid-row'); }) || undefined;
        let cellText = (_a = event.composedPath()[0]) === null || _a === void 0 ? void 0 : _a.innerText;
        if (clickedRow) {
            const idx = clickedRow.item.internalIndex;
            const ctxKey = (_c = (_b = clickedRow.item.__properties__) === null || _b === void 0 ? void 0 : _b.__contextId__) !== null && _c !== void 0 ? _c : '__default__';
            const ctxApi = (_e = (_d = clickedRow.item.__properties__) === null || _d === void 0 ? void 0 : _d.__contextApi__) !== null && _e !== void 0 ? _e : '';
            const ctxClipboard = (_g = (_f = clickedRow.item.__properties__) === null || _f === void 0 ? void 0 : _f.__contextCopy__) !== null && _g !== void 0 ? _g : '';
            const rowIdx = this.getRowIdxFromInternal(idx);
            const row = [this.visibleData[rowIdx].internalIndex, rowIdx];
            if (!this.activeRows.find(r => JSON.stringify(r) == JSON.stringify(row)))
                this.handleRowClick(event, idx);
            if (ctxApi) {
                event.preventDefault();
                const contextData = {
                    clickEvent: event,
                    contextPromise: fetchContextData(ctxApi, this.selectedRowValues)
                };
                if (ctxClipboard)
                    contextData.postPromiseContext = [this.getCellTextCopyContext(cellText)];
                this.contextMenu.data = contextData;
                //openContext(event, {fetchPath: ctxApi, fetchParm: this.selectedRowValues});
            }
            else if (this.data.contextholder) {
                event.preventDefault();
                let ctx;
                if (ctxKey.indexOf(',') > -1) {
                    let ctxKeys = ctxKey.split(',');
                    let filteredContexts = this.data.contextholder.filter(ctx => ctxKeys.includes(ctx.key));
                    ctx = { key: ctxKey, context: filteredContexts.flatMap(ctx => ctx.context) };
                }
                else
                    ctx = this.data.contextholder.filter(ctx => ctx.key == ctxKey)[0];
                if (!ctx)
                    ctx = this.data.contextholder.filter(ctx => ctx.key == '__default__')[0];
                if (!ctx)
                    ctx = { key: 'null', context: [] };
                if (!ctx.context)
                    ctx.context = [];
                //Add copy cell if needed
                ctx.context = ctx.context.filter(x => x.menuTxt != 'Copy To Clipboard');
                ctx.context.push(this.getCellTextCopyContext(cellText));
                this.contextMenu.data = {
                    clickEvent: event,
                    selectedValues: this.selectedRowValues,
                    context: ctx.context,
                    isNew: true
                };
            }
        }
    }
    handleRowKeyDown(event) {
        var _a, _b, _c, _d;
        if (this.selectionMode === 'none') {
            event.preventDefault();
            return true;
        }
        if (event.key === 'Shift' || event.key === 'Control')
            return;
        let currRow, row, target, parentRow;
        this.checkAndSetActiveRow();
        if (this.contextMenu.display !== 'none') {
            this.contextMenu.hide();
        }
        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                this.isMovingUp = true;
                if (this.activeBaseRow === null) {
                    const row = this.visibleData[0];
                    this.activeBaseRow = [row.internalIndex, 0];
                    this.activeRows = [this.activeBaseRow];
                    this.scrollToRow = this.activeBaseRow;
                }
                else {
                    if (event.shiftKey && this.selectionMode == 'multi') {
                        if (this.activeEndRow === null)
                            this.activeEndRow = this.activeBaseRow;
                        this.activeEndRow = this.findPrevRow(this.activeEndRow);
                        const rowsToFill = this.findRowsBetween(this.activeBaseRow, this.activeEndRow);
                        this.activeRows = rowsToFill;
                        this.scrollToRow = this.findPrevRow(this.activeEndRow);
                    }
                    else {
                        this.activeRows = [this.findPrevRow(this.activeEndRow)]; //should be (?? baseRow)... but doesn't seem to be working
                        this.activeBaseRow = this.activeRows[0];
                        this.activeEndRow = this.activeRows[0];
                        this.scrollToRow = this.activeBaseRow;
                    }
                }
                this.requestUpdate();
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.isMovingUp = false;
                if (this.activeBaseRow === null) {
                    const row = this.visibleData[0];
                    this.activeBaseRow = [row.internalIndex, 0];
                    this.activeRows = [this.activeBaseRow];
                    this.scrollToRow = this.activeEndRow;
                }
                else {
                    if (event.shiftKey && this.selectionMode == 'multi') {
                        if (this.activeEndRow === null)
                            this.activeEndRow = this.activeBaseRow;
                        this.activeEndRow = this.findNextRow(this.activeEndRow);
                        const rowsToFill = this.findRowsBetween(this.activeBaseRow, this.activeEndRow);
                        this.activeRows = rowsToFill;
                        this.scrollToRow = this.findNextRow(this.activeEndRow);
                    }
                    else {
                        this.activeRows = [this.findNextRow(this.activeEndRow)];
                        this.activeBaseRow = this.activeRows[0];
                        this.activeEndRow = this.activeRows[0];
                        this.scrollToRow = this.activeEndRow;
                    }
                }
                this.requestUpdate();
                break;
            case 'ArrowRight':
                if (!this.tree)
                    return;
                currRow = (_a = this.activeEndRow) !== null && _a !== void 0 ? _a : this.activeBaseRow;
                if (!currRow)
                    return;
                row = (_b = this.shadowRoot) === null || _b === void 0 ? void 0 : _b.querySelector(`.grid-row.row-${currRow[0]}`);
                target = row === null || row === void 0 ? void 0 : row.querySelector('.grid-treeCaret');
                parentRow = row === null || row === void 0 ? void 0 : row.closest('.grid-row');
                PIC.getInstance.msg('Right', row, target, parentRow);
                this.loadSubTableData(target, parentRow.item);
                break;
            case 'ArrowLeft': //TODO toggle functionality, currently both Left & Right opens and closes the tree
                if (!this.tree)
                    return;
                currRow = (_c = this.activeEndRow) !== null && _c !== void 0 ? _c : this.activeBaseRow;
                if (!currRow)
                    return;
                row = (_d = this.shadowRoot) === null || _d === void 0 ? void 0 : _d.querySelector(`.grid-row.row-${currRow[0]}`);
                target = row === null || row === void 0 ? void 0 : row.querySelector('.grid-treeCaret');
                parentRow = row === null || row === void 0 ? void 0 : row.closest('.grid-row');
                PIC.getInstance.msg('Left', row, target, parentRow);
                this.loadSubTableData(target, parentRow.item);
                break;
            case ' ':
                if (event.ctrlKey) {
                    //this.dispatchEvent(new CustomEvent('contextmenu')) //no bueno
                    // let x = new PointerEvent('pointerdown', {
                    // 	clientX: 0,
                    // 	clientY: 0
                    // })
                    // this.handleContextMenu(x)
                }
                else
                    this.setCheckbox(this.activeRows, null);
                this.scrollToRow = this.activeBaseRow;
                this.requestUpdate();
                break;
            case 'Escape':
                this.activeBaseRow = null;
                this.activeEndRow = null;
                this.activeRows = [];
                this.blur();
                break;
            case 'a':
                if (event.ctrlKey && this.selectionMode == 'multi') {
                    event.preventDefault();
                    this.activeBaseRow = [this.visibleData[0].internalIndex, 0];
                    this.activeEndRow = [this.visibleData[this.visibleData.length - 1].internalIndex, this.visibleData.length - 1];
                    this.activeRows = this.findRowsBetween(this.activeBaseRow, this.activeEndRow);
                    this.requestUpdate();
                }
                break;
            case 'c':
                if (event.ctrlKey) {
                    event.preventDefault();
                    if (!this.editable)
                        this.copyRowsToClipboard(this.activeRows);
                    else {
                        // only copy the current cell
                        let targetCell = event.composedPath().find(x => { var _a; return (_a = x === null || x === void 0 ? void 0 : x.classList) === null || _a === void 0 ? void 0 : _a.contains('grid-cell'); });
                        let inputCell = targetCell === null || targetCell === void 0 ? void 0 : targetCell.querySelector('input.inputCell');
                        if (inputCell) {
                            // copy inputCell.value to clipboard
                            navigator.clipboard.writeText(inputCell.value);
                        }
                    }
                }
                break;
            case 'Enter':
                if (this.isLookUp) {
                    let action = new PICAction();
                    action.actionOptions = {
                        type: 'doSubmit',
                        event: 'pullback'
                    };
                    action.parms_ += ',selectedvalues=' + this.selectedRowValues;
                    action.trigger_ = 'default';
                    action.triggerEvent(event);
                }
                else
                    return true;
                break;
            default:
                // PIC.getInstance.msg('DefaultKeyLogged', event.key)
                return true;
                break;
        }
    }
    handleRowClick(event, idx) {
        const rowIdx = this.getRowIdxFromInternal(idx);
        const row = [this.visibleData[rowIdx].internalIndex, rowIdx];
        if (event.shiftKey && this.selectionMode == 'multi') {
            event.preventDefault();
            if (this.activeBaseRow == null)
                this.activeBaseRow = row;
            if (this.activeEndRow == null)
                this.activeEndRow = row;
            const rowsToFill = this.findRowsBetween(this.activeBaseRow, row);
            PIC.getInstance.msg('ShiftRowClick', rowsToFill);
            this.activeRows = rowsToFill;
        }
        else if (event.ctrlKey && this.selectionMode == 'multi') {
            event.preventDefault();
            PIC.getInstance.msg('CtrlRowClick', row, this.activeRows.findIndex(roww => roww[0] === row[0]));
            if (this.activeBaseRow == null) {
                this.activeBaseRow = row;
                this.activeEndRow = row;
                this.activeRows = [row];
            }
            else {
                const activeIdx = this.activeRows.findIndex(item => item[0] === row[0]);
                if (activeIdx > -1) { //if exists remove
                    this.removeActiveRow(activeIdx);
                    if (this.activeBaseRow[0] === row[0]) {
                        if (this.activeRows.length > 0)
                            this.activeBaseRow = this.activeRows[0];
                        else {
                            this.activeBaseRow = null;
                            this.activeEndRow = null;
                        }
                    }
                }
                else { //add to current active rows
                    this.activeRows = this.activeRows.concat([row]);
                }
            }
        }
        else if (this.selectionMode === 'none') {
            event.preventDefault();
            return true;
        }
        else {
            PIC.getInstance.msg('SingleRowClick', row);
            this.activeBaseRow = row;
            this.activeEndRow = row;
            this.activeRows = [row];
            //TODO implement left click handling here!      
            //only do actions on leftclick
            /*if(event.button == 0){
                console.log(event.composedPath().find(x => (x as HTMLElement).classList?.contains('grid-row')));
                console.log(row);
            }*/
        }
    }
    copyRowsToClipboard(rows) {
        var _a, _b;
        const propsToSkip = ['internalindex', '__properties__', 'checked'];
        const result = [];
        /* add headers to output, can be readded later, good for csv output
        const headerResult = []
        for (let header of this.visibleHeaders) { //this.data.headers
            if (header.type === 'checkbox') continue
            headerResult.push(header.text)
        }
        result.push(headerResult.join(';'))
        */
        for (let row of rows) {
            const rowResult = [];
            let dataRow = this.visibleData.find(d => d.internalIndex === row[0]); //or this.data.data
            if (!dataRow)
                continue;
            for (let prop in dataRow) {
                if (propsToSkip.includes(prop.toLowerCase()) || prop.startsWith('__'))
                    continue; //skip internal props (no headers)
                const dataType = (_b = (_a = dataRow[prop]) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.type;
                let data = '';
                if (dataType === 'button' || dataType === 'faicon' || dataType === 'icon' || dataType === 'modal')
                    data = ''; //button, icons etc. return empty string
                else
                    data = dataRow[prop].value + '';
                rowResult.push(data);
            }
            const res = rowResult.join(';');
            result.push(res);
        }
        const clipboardResult = result.join('\n');
        navigator.clipboard.writeText(clipboardResult);
    }
    set activeRows(rows) {
        if (this.selectionMode === 'none')
            return;
        PIC.getInstance.msg('Settings active rows', rows);
        //if setting empty then clear all active rows
        if (rows.length === 0) {
            __classPrivateFieldSet(this, _PICWebTable_activeRows_, [], "f");
        }
        //if setting same row return
        if (rows.length === 1 && this.activeRows.length === 1 && rows[0][0] === this.activeRows[0][0]) {
            return;
        }
        __classPrivateFieldSet(this, _PICWebTable_activeRows_, rows, "f");
        // this.scrollToRow = rows[0][0];
        this.requestUpdate();
    }
    get activeRows() {
        return __classPrivateFieldGet(this, _PICWebTable_activeRows_, "f");
    }
    getEnabledFilterRowInputs() {
        var _a;
        return Array.from((_a = this.filterRowInputs) !== null && _a !== void 0 ? _a : []).filter((inp) => !inp.disabled);
    }
    handleFilterInput(e, header) {
        var _a;
        let targetVal = e.target.value;
        this.selectedHeader = null;
        let defaultFilterType = ((_a = header.properties) === null || _a === void 0 ? void 0 : _a.customFilter) ? 'custom' : 'startsWith';
        if (!header.filter || header.filter === 'between' || header.filter === 'select')
            header.filter = defaultFilterType;
        if (['startsWith', 'contains', 'notStartsWith', 'notContains'].includes(header.filter) || e.target.checkValidity()) {
            this.addActiveFilter(targetVal, header, header.filter ? header.filter : defaultFilterType); //startsWith is default
        }
    }
    filterKeyHandler(e, header) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2;
        let target = e.target;
        switch (e.key) {
            case 'Enter': {
                if (target.classList.contains('filter-input')) { // handling for inputs in filter row
                    if (!['rangeFrom', 'rangeTo'].includes(target.id) && !target.classList.contains('select-item'))
                        this.toggleFilterContainer(e, header);
                }
                if (target.classList.contains('filter-item')) { // handling for filter types in filter container
                    let filterType = target.id;
                    if (filterType === 'between')
                        (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('#secondaryFilterContainer')) === null || _b === void 0 ? void 0 : _b.classList.add('visible');
                    if (filterType === 'select')
                        (_d = (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector('#selectContainer')) === null || _d === void 0 ? void 0 : _d.classList.add('visible');
                    this.handleFilterTypeSelection(e, filterType);
                    if (filterType !== 'between' && filterType !== 'select') {
                        (_f = (_e = this.shadowRoot) === null || _e === void 0 ? void 0 : _e.querySelector(`.filter-row[data-internal-index="${header.internalIndex}"] input`)) === null || _f === void 0 ? void 0 : _f.focus();
                    }
                    if (filterType === 'between')
                        (_h = (_g = this.shadowRoot) === null || _g === void 0 ? void 0 : _g.querySelector('#rangeFrom')) === null || _h === void 0 ? void 0 : _h.focus();
                    if (filterType === 'select')
                        (_k = (_j = this.shadowRoot) === null || _j === void 0 ? void 0 : _j.querySelector('#selectContainer .select-checkbox')) === null || _k === void 0 ? void 0 : _k.focus();
                }
                if (target.classList.contains('select-item') || target.classList.contains('select-checkbox')) {
                    let currBox = target instanceof PICCheckBox ? target : target.querySelector('pic-checkbox');
                    if (currBox)
                        currBox.inputField.click();
                }
                break;
            }
            case 'Escape': {
                if (this.selectedHeader)
                    (_m = (_l = this.shadowRoot) === null || _l === void 0 ? void 0 : _l.querySelector(`.filter-row[data-internal-index="${this.selectedHeader.internalIndex}"] input`)) === null || _m === void 0 ? void 0 : _m.focus(); // trying this
                this.selectedHeader = null;
                this.requestUpdate();
                break;
            }
            case 'Tab': {
                if (!e.shiftKey) {
                    if (target.classList.contains('filter-input')) {
                        if (target.id === 'rangeTo') {
                            //tabbing out of #rangeTo should focus on the filter row input again
                            e.preventDefault();
                            (_p = (_o = this.shadowRoot) === null || _o === void 0 ? void 0 : _o.querySelector(`.filter-row[data-internal-index="${header.internalIndex}"] input`)) === null || _p === void 0 ? void 0 : _p.focus();
                        }
                        else if (target.id === 'rangeFrom') {
                            e.preventDefault();
                            (_r = (_q = this.shadowRoot) === null || _q === void 0 ? void 0 : _q.querySelector('#rangeTo')) === null || _r === void 0 ? void 0 : _r.focus();
                        }
                        else if (target.closest('.filter-row')) {
                            const enabled = this.getEnabledFilterRowInputs();
                            const idx = enabled.indexOf(target);
                            if (idx >= 0 && idx < enabled.length - 1) {
                                e.preventDefault();
                                enabled[idx + 1].focus();
                            }
                            else if (idx === enabled.length - 1) {
                                e.preventDefault();
                                this.handleRowKeyDown(e);
                                this.gridTable.focus();
                            }
                        }
                    }
                }
                if (e.shiftKey) {
                    if (target.id === 'rangeTo') {
                        e.preventDefault();
                        (_t = (_s = this.shadowRoot) === null || _s === void 0 ? void 0 : _s.querySelector('#rangeFrom')) === null || _t === void 0 ? void 0 : _t.focus();
                    }
                    else if (target.closest('.filter-row')) {
                        const enabled = this.getEnabledFilterRowInputs();
                        const idx = enabled.indexOf(target);
                        if (idx === 0) {
                            e.preventDefault();
                            (_u = this.tableBtns[this.tableBtns.length - 1]) === null || _u === void 0 ? void 0 : _u.focus();
                        }
                        else if (idx > 0) {
                            e.preventDefault();
                            enabled[idx - 1].focus();
                        }
                    }
                }
                break;
            }
            case 'ArrowUp': {
                if (this.selectedHeader && this.filterRowIsVisible) {
                    let filterItems = (_v = this.shadowRoot) === null || _v === void 0 ? void 0 : _v.querySelectorAll('.filter-item');
                    if (filterItems && e.target.classList.contains('filter-item')) {
                        let currentIndex = Array.from(filterItems).indexOf(e.target);
                        if (currentIndex > 0) {
                            filterItems[currentIndex - 1].focus();
                        }
                        else {
                            (_x = (_w = this.shadowRoot) === null || _w === void 0 ? void 0 : _w.querySelector(`.filter-row[data-internal-index="${this.selectedHeader.internalIndex}"] input`)) === null || _x === void 0 ? void 0 : _x.focus();
                        }
                    }
                    else if (e.target.classList.contains('select-item') || e.target.classList.contains('select-checkbox')) {
                        e.preventDefault();
                        let checkboxes = (_y = this.shadowRoot) === null || _y === void 0 ? void 0 : _y.querySelectorAll('.select-checkbox');
                        let currBox = e.target instanceof PICCheckBox ? e.target : e.target.querySelector('pic-checkbox');
                        if (checkboxes) {
                            let currentIndex = Array.from(checkboxes).indexOf(currBox);
                            if (currentIndex > 0) {
                                checkboxes[currentIndex - 1].focus();
                            }
                            else
                                checkboxes[checkboxes.length - 1].focus();
                        }
                    }
                }
                break;
            }
            case 'ArrowDown': {
                if (this.selectedHeader && this.filterRowIsVisible) {
                    let filterItems = (_z = this.shadowRoot) === null || _z === void 0 ? void 0 : _z.querySelectorAll('.filter-item');
                    if (filterItems && e.target.classList.contains('filter-item')) {
                        let currentIndex = Array.from(filterItems).indexOf(e.target);
                        if (currentIndex < filterItems.length - 1) {
                            filterItems[currentIndex + 1].focus();
                        }
                        else {
                            (_1 = (_0 = this.shadowRoot) === null || _0 === void 0 ? void 0 : _0.querySelector(`.filter-row[data-internal-index="${this.selectedHeader.internalIndex}"] input`)) === null || _1 === void 0 ? void 0 : _1.focus();
                        }
                    }
                    else if (filterItems && e.target.classList.contains('filter-input') && !['rangeFrom', 'rangeTo'].includes(e.target.id)) {
                        filterItems[0].focus();
                    }
                    else if (e.target.classList.contains('select-item') || e.target.classList.contains('select-checkbox')) {
                        e.preventDefault();
                        let checkboxes = (_2 = this.shadowRoot) === null || _2 === void 0 ? void 0 : _2.querySelectorAll('.select-checkbox');
                        let currBox = e.target instanceof PICCheckBox ? e.target : e.target.querySelector('pic-checkbox');
                        if (checkboxes) {
                            let currentIndex = Array.from(checkboxes).indexOf(currBox);
                            if (currentIndex < checkboxes.length - 1) {
                                checkboxes[currentIndex + 1].focus();
                            }
                            else
                                checkboxes[0].focus();
                        }
                    }
                }
                break;
            }
            default:
                return;
        }
    }
    //rowIdentifier
    findNextRow(row) {
        return this.getRowPos(row, 1);
    }
    findPrevRow(row) {
        return this.getRowPos(row, -1);
    }
    getRowPos(row, movePos) {
        let nxtRow = this.visibleData[row[1] + movePos];
        if (!nxtRow)
            return row;
        return [nxtRow.internalIndex, row[1] + movePos];
    }
    getRowIdxFromInternal(idx) {
        return this.visibleData.findIndex(item => item.internalIndex === idx);
    }
    findRowsBetween(rowFrom, rowTo) {
        if (rowFrom[0] === rowTo[0])
            return [rowFrom, rowTo];
        let rows = [];
        let sortedRows = rowFrom[1] <= rowTo[1] ? [rowFrom, rowTo] : [rowTo, rowFrom];
        for (let i = sortedRows[0][1]; i <= sortedRows[1][1]; i++) {
            let row = this.visibleData[i];
            rows.push([row.internalIndex, i]);
        }
        return rows;
    }
    removeActiveRow(row) {
        if (typeof row === 'number') {
            this.activeRows = this.activeRows.filter((_, idx) => idx !== row);
            //this.activeRows.splice(row, 1) //splice doesnt trigger set
        }
        else {
            this.activeRows = this.activeRows.filter(row_ => row_[0] !== row[0]);
        }
    }
    handleRangeSelection(e) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        let from = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('#rangeFrom');
        let to = (_b = this.shadowRoot) === null || _b === void 0 ? void 0 : _b.querySelector('#rangeTo');
        let filterRowInput = (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector(`.filter-row[data-internal-index="${(_d = this.selectedHeader) === null || _d === void 0 ? void 0 : _d.internalIndex}"] input`);
        if (from.value.length > 0 && to.value.length > 0 && this.selectedHeader) {
            if (((_f = (_e = this.selectedHeader) === null || _e === void 0 ? void 0 : _e.properties) === null || _f === void 0 ? void 0 : _f.type) && ['date', 'datetime'].includes((_h = (_g = this.selectedHeader) === null || _g === void 0 ? void 0 : _g.properties) === null || _h === void 0 ? void 0 : _h.type)) {
                if (from.checkValidity() && to.checkValidity()) {
                    if (filterRowInput)
                        filterRowInput.value = '';
                    this.addActiveFilter(from.value + ' to ' + to.value, this.selectedHeader, 'between');
                }
            }
            if (((_k = (_j = this.selectedHeader) === null || _j === void 0 ? void 0 : _j.properties) === null || _k === void 0 ? void 0 : _k.type) === 'time') {
                let fromTime = from.value, toTime = to.value;
                if (from.value.split(':').length < 3) {
                    for (let index = from.value.split(':').length; index < 3; index++) {
                        fromTime += ':00';
                    }
                }
                if (to.value.split(':').length < 3) {
                    for (let index = to.value.split(':').length; index < 3; index++) {
                        toTime += ':00';
                    }
                }
                if (filterRowInput)
                    filterRowInput.value = '';
                this.addActiveFilter(fromTime + ' to ' + toTime, this.selectedHeader, 'between');
            }
            if (((_m = (_l = this.selectedHeader) === null || _l === void 0 ? void 0 : _l.properties) === null || _m === void 0 ? void 0 : _m.type) === 'number') {
                if (from.checkValidity() && to.checkValidity()) {
                    if (filterRowInput)
                        filterRowInput.value = '';
                    this.addActiveFilter(from.value + ' to ' + to.value, this.selectedHeader, 'between');
                }
            }
        }
        if (from.value.length === 0 && to.value.length === 0 && this.selectedHeader) {
            if (filterRowInput)
                filterRowInput.value = '';
            this.addActiveFilter('', this.selectedHeader, 'between');
        }
    }
    preventInvalidCharacters(e, header) {
        var _a, _b, _c;
        if (e.data) {
            if (e.data.length > 1) {
                // user is pasting in multiple characters - allow it and handle possible formatting errors in the respective input handlers
            }
            else {
                let type = (_a = header === null || header === void 0 ? void 0 : header.properties) === null || _a === void 0 ? void 0 : _a.type; // for input in filter row
                if (!type)
                    type = (_c = (_b = this.selectedHeader) === null || _b === void 0 ? void 0 : _b.properties) === null || _c === void 0 ? void 0 : _c.type; // for input in range selection (filter is 'between')
                let sep = ':'; // if time
                let format = parseFormat(PIC.getInstance.userConfig.dateFormat);
                if (type === 'datetime')
                    format = parseFormat(PIC.getInstance.userConfig.dateTimeFormat.split(' ')[0]);
                if (type === 'date' || type === 'datetime')
                    sep = format.sep;
                let reg = header ? new RegExp(`[0-9]|\\${sep}|\|`) : new RegExp(`[0-9]|\\${sep}`); // if date or time
                if (type === 'number') {
                    reg = header ? /[0-9,.|-]/ : /[0-9,.-]/;
                    if (!e.data.match(reg))
                        e.preventDefault();
                }
                if (type && ['date', 'time'].includes(type)) {
                    if (!e.data.match(reg))
                        e.preventDefault();
                }
                if (type === 'datetime') {
                    reg = header ? new RegExp(`[0-9]|\\${sep}|:| |\|`) : new RegExp(`[0-9]|\\${sep}|:| |`);
                    if (!e.data.match(reg))
                        e.preventDefault();
                }
            }
        }
    }
    handleFilterTypeSelection(e, fType) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if (!this.selectedHeader)
            return;
        let inp = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.filter-row[data-internal-index="${(_b = this.selectedHeader) === null || _b === void 0 ? void 0 : _b.internalIndex}"] input`);
        this.selectedHeader.filter = fType;
        if (fType === 'between') {
            (_d = (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector('#secondaryFilterContainer')) === null || _d === void 0 ? void 0 : _d.classList.add('visible');
            (_f = (_e = this.shadowRoot) === null || _e === void 0 ? void 0 : _e.querySelector('#selectContainer')) === null || _f === void 0 ? void 0 : _f.classList.remove('visible');
            let active = this.activeFilters.filters.get((_g = this.selectedHeader) === null || _g === void 0 ? void 0 : _g.value);
            (active && active.filterType === 'between') ? this.setRangeValues(active === null || active === void 0 ? void 0 : active.value) : this.setRangeValues();
            this.requestUpdate();
        }
        else if (fType === 'select') {
            (_j = (_h = this.shadowRoot) === null || _h === void 0 ? void 0 : _h.querySelector('#secondaryFilterContainer')) === null || _j === void 0 ? void 0 : _j.classList.remove('visible');
            (_l = (_k = this.shadowRoot) === null || _k === void 0 ? void 0 : _k.querySelector('#selectContainer')) === null || _l === void 0 ? void 0 : _l.classList.add('visible');
            this.requestUpdate();
            this.updateComplete.then(() => {
                let active = this.activeFilters.filters.get(this.selectedHeader.value);
                (active && active.filterType === 'select') ? this.setCheckValues(active === null || active === void 0 ? void 0 : active.value) : this.setCheckValues('all');
            });
        }
        else {
            // check if something is written in the corresponding input and if so, apply filter
            if (inp && inp.value.length > 0) {
                if (['startsWith', 'contains', 'notStartsWith', 'notContains', 'endsWith'].includes(fType) || inp.checkValidity()) {
                    this.addActiveFilter(inp.value, this.selectedHeader, fType);
                }
            }
            this.selectedHeader = null;
            this.requestUpdate();
        }
    }
    handleInvalidInput(e) {
        //TODO show error message to the user
    }
    getValidationPattern(dataType, header) {
        if (dataType === 'number') {
            if (header)
                return `^-?(\\d+([.,]\\d+)?)(\\|-?(\\d+([.,]\\d+)?))*$`; // if header, allow for OR (|)
            return `^-?\\d*[.,]?\\d+$`;
        }
        if (dataType === 'date') {
            let format = parseFormat(PIC.getInstance.userConfig.dateFormat);
            let reg = '^';
            let dateReg = '';
            format.posArr.forEach((pos, idx) => {
                if (pos === Position.Year && format.fullYear)
                    dateReg += `\\d{4}`;
                else
                    dateReg += `\\d{2}`;
                if (idx < format.posArr.length - 1)
                    dateReg += format.sep;
            });
            if (header)
                dateReg += `(\\|${dateReg})*`; // if header, allow for OR (|)
            reg += dateReg + '$';
            return reg;
        }
        if (dataType === 'time') {
            let format = PIC.getInstance.userConfig.timeFormat.split(':');
            let reg = '^';
            let timeReg = '';
            format.forEach((time, idx) => {
                timeReg += `\\d{2}`;
                if (idx < format.length - 1)
                    timeReg += ':';
            });
            if (header)
                timeReg += `(\\|${timeReg})*`; // if header, allow for OR (|)
            reg += timeReg + '$';
            return reg;
        }
        if (dataType === 'datetime') {
            let dateFormat = parseFormat(PIC.getInstance.userConfig.dateTimeFormat.split(' ')[0]);
            let timeFormat = PIC.getInstance.userConfig.dateTimeFormat.split(' ')[1].split(':');
            let reg = '^';
            let datetimeReg = '';
            dateFormat.posArr.forEach((pos, idx) => {
                if (pos === Position.Year && dateFormat.fullYear)
                    datetimeReg += `\\d{4}`;
                else
                    datetimeReg += `\\d{2}`;
                if (idx < dateFormat.posArr.length - 1)
                    datetimeReg += dateFormat.sep;
            });
            datetimeReg += ` `;
            timeFormat.forEach((time, idx) => {
                datetimeReg += `\\d{2}`;
                if (idx < timeFormat.length - 1)
                    datetimeReg += ':';
            });
            if (header)
                datetimeReg += `(\\|${datetimeReg})*`; // if header, allow for OR (|)
            reg += datetimeReg + '$';
            return reg;
        }
        return '.+';
    }
    toggleFilterContainer(e, header) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        e.preventDefault();
        if (e instanceof FocusEvent) { //when focusing out of the filter container or filter row input
            if (e.relatedTarget && e.relatedTarget.classList.contains('filter-item')) {
                if (e.relatedTarget.classList.contains('between')) {
                    (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('#selectContainer')) === null || _b === void 0 ? void 0 : _b.classList.remove('visible');
                }
                if (e.relatedTarget.classList.contains('select')) {
                    (_d = (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector('#secondaryFilterContainer')) === null || _d === void 0 ? void 0 : _d.classList.remove('visible');
                }
            }
            else {
                this.selectedHeader = null;
                this.requestUpdate();
            }
        }
        else {
            if (this.selectedHeader === header) {
                this.selectedHeader = null;
            }
            else {
                (_f = (_e = this.shadowRoot) === null || _e === void 0 ? void 0 : _e.querySelector('#selectContainer')) === null || _f === void 0 ? void 0 : _f.classList.remove('visible');
                (_h = (_g = this.shadowRoot) === null || _g === void 0 ? void 0 : _g.querySelector('#secondaryFilterContainer')) === null || _h === void 0 ? void 0 : _h.classList.remove('visible');
                let active = this.activeFilters.filters.get(header.value);
                if (active)
                    header.filter = active.filterType;
                this.selectedHeader = header;
            }
            this.requestUpdate();
        }
    }
    setRangeValues(filterValue) {
        var _a, _b, _c, _d;
        let from = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('#rangeFrom');
        let to = (_b = this.shadowRoot) === null || _b === void 0 ? void 0 : _b.querySelector('#rangeTo');
        from.value = '';
        to.value = '';
        if (filterValue) {
            from.value = (_c = filterValue.split(' to ')[0]) !== null && _c !== void 0 ? _c : '';
            to.value = (_d = filterValue.split(' to ')[1]) !== null && _d !== void 0 ? _d : '';
        }
    }
    setCheckValues(filterValue) {
        var _a, _b, _c;
        let allCheckboxes = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.select-checkbox:not(.toggle-all)');
        let toggleAllCheckbox = (_b = this.shadowRoot) === null || _b === void 0 ? void 0 : _b.querySelector('.select-checkbox.toggle-all');
        let checkedVals = (_c = filterValue === null || filterValue === void 0 ? void 0 : filterValue.split('|')) !== null && _c !== void 0 ? _c : [];
        allCheckboxes.forEach(checkbox => {
            if (filterValue === 'all' || checkedVals.includes(checkbox.id.split('filtermenu-checkbox-')[1])) {
                checkbox === null || checkbox === void 0 ? void 0 : checkbox.setValue('true');
            }
            else {
                checkbox === null || checkbox === void 0 ? void 0 : checkbox.setValue('false');
            }
        });
        if (Array.from(allCheckboxes).every(checkbox => checkbox.submitValue === 'true'))
            this.toggleSelectAllCheckbox('checked');
        else if (Array.from(allCheckboxes).every(checkbox => checkbox.submitValue === 'false'))
            this.toggleSelectAllCheckbox('unchecked');
        else
            this.toggleSelectAllCheckbox('indeterminate');
        toggleAllCheckbox.requestUpdate();
    }
    calcFilterWidthsFromHeaders(widthHeaders) {
        widthHeaders.forEach(hdr => {
            var _a;
            let fItem = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.filter-row[data-internal-index="${hdr.internalIndex}"]`);
            // if(fItem) fItem.style.maxWidth = '0px';
            // let hdrItem = Array.from(this.gridTableHeader).find(cell => (cell as unknown as HTMLElementHeaderWithItem).item.internalIndex === hdr.internalIndex);
            // if(fItem && hdrItem) {
            //     fItem.style.maxWidth = (hdrItem.getBoundingClientRect()?.width) + 'px';
            // }
            // else if(fItem) fItem.style.maxWidth = 'auto';
            if (fItem)
                fItem.style.maxWidth = 'auto';
        });
    }
    renderFilterRow() {
        return repeat(this.actuallyVisibleHeaders, (header) => header.internalIndex, (header, idx) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            let active = this.activeFilters.filters.get(header.value);
            if (header.type === 'subTable')
                return '';
            return html `
                ${(((_a = header.properties) === null || _a === void 0 ? void 0 : _a.type) && ['faicon', 'icon', 'button'].includes((_b = header.properties) === null || _b === void 0 ? void 0 : _b.type)) || ['removal', 'move', 'modal', 'checkbox', 'tree'].includes(header.type) ?
                html `
                    <div class="filter-row" style=${header.type === 'checkbox' ? 'z-index: 2' : ''}></div>
                    ` : html `
                    <div
                        class="filter-row"
                        style=${`min-width: 50px;`}
                        data-internal-index=${ifDefined(header.internalIndex)}
                        @contextmenu=${(e) => { var _a; if (!((_a = header.properties) === null || _a === void 0 ? void 0 : _a.disableFilter))
                this.toggleFilterContainer(e, header); }}
                        @click=${(e) => {
                var _a;
                if (!e.target.classList.contains('filter-icon') || ((_a = header.properties) === null || _a === void 0 ? void 0 : _a.disableFilter))
                    return;
                this.toggleFilterContainer(e, header);
            }}
                        >
                        <input
                            type="text"
                            autocomplete="off"
                            tabindex="-1"
                            class="pic-body2 filter-input"
                            inputmode=${(((_c = header.properties) === null || _c === void 0 ? void 0 : _c.type) === 'number' || header.type === 'number') ? 'decimal' : 'text'}
                            pattern=${this.getValidationPattern((_d = header.properties) === null || _d === void 0 ? void 0 : _d.type, header)}
                            placeholder=${((_e = header.properties) === null || _e === void 0 ? void 0 : _e.type) === 'date' ? PIC.getInstance.userConfig.dateFormat : ((_f = header.properties) === null || _f === void 0 ? void 0 : _f.type) === 'time' ? PIC.getInstance.userConfig.timeFormat : ((_g = header.properties) === null || _g === void 0 ? void 0 : _g.type) === 'datetime' ? PIC.getInstance.userConfig.dateTimeFormat : ''}
                            @input=${(e) => this.handleFilterInput(e, header)}
                            @keydown=${(e) => this.filterKeyHandler(e, header)}
                            @beforeinput=${(e) => this.preventInvalidCharacters(e, header)}
                            @blur=${(e) => this.toggleFilterContainer(e, header)}
                            ?disabled=${((_h = header.properties) === null || _h === void 0 ? void 0 : _h.disableFilter) ? true : false}
                        />
                        <div 
                            class=${`filter-icon ${active ? 'active' : ''} ${((_j = header.properties) === null || _j === void 0 ? void 0 : _j.disableFilter) ? 'disabled' : ''}`}
                        >
                        </div>
                    </div>
                    `}
            `;
        });
    }
    getFilterContainerStyle() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        let selectedBCR = (_c = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.filter-row[data-internal-index="${(_b = this.selectedHeader) === null || _b === void 0 ? void 0 : _b.internalIndex}"]`)) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
        let left = (selectedBCR === null || selectedBCR === void 0 ? void 0 : selectedBCR.left) + 'px';
        let right = 'auto';
        if (selectedBCR && window.innerWidth - selectedBCR.right < 200) {
            right = (window.innerWidth - ((_d = selectedBCR === null || selectedBCR === void 0 ? void 0 : selectedBCR.right) !== null && _d !== void 0 ? _d : 0)) + 'px';
            left = 'auto';
        }
        let filterCount = ((_f = (_e = this.selectedHeader) === null || _e === void 0 ? void 0 : _e.properties) === null || _f === void 0 ? void 0 : _f.type) && ['number', 'date', 'time', 'datetime'].includes((_h = (_g = this.selectedHeader) === null || _g === void 0 ? void 0 : _g.properties) === null || _h === void 0 ? void 0 : _h.type) ? 10 : 7;
        if ((_k = (_j = this.selectedHeader) === null || _j === void 0 ? void 0 : _j.properties) === null || _k === void 0 ? void 0 : _k.customFilter)
            filterCount += 1;
        let heightRequiredForFilters = filterCount * 40;
        let top;
        if (this.selectedHeader && selectedBCR) {
            let spaceBelowInput = window.innerHeight - selectedBCR.bottom;
            if (spaceBelowInput < heightRequiredForFilters) {
                top = (selectedBCR.top - heightRequiredForFilters) + 'px';
            }
            else {
                top = selectedBCR.bottom + 'px';
            }
        }
        else {
            top = '0';
        }
        return {
            'display': this.selectedHeader && this.filterRowIsVisible ? 'block' : 'none',
            'top': top,
            'left': this.selectedHeader && selectedBCR ? left : '0',
            'right': right,
        };
    }
    getSecondaryFilterContainerStyle(isSelect) {
        var _a, _b, _c, _d, _e, _f;
        let mainContainerBCR = (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('#filterContainer')) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        let betweenBCR = !isSelect ? (_d = (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelector('div.filter-item.between')) === null || _d === void 0 ? void 0 : _d.getBoundingClientRect() : (_f = (_e = this.shadowRoot) === null || _e === void 0 ? void 0 : _e.querySelector('div.filter-item.select')) === null || _f === void 0 ? void 0 : _f.getBoundingClientRect();
        let left, right;
        if (mainContainerBCR) {
            left = mainContainerBCR.right + 'px';
            right = 'auto';
            if (mainContainerBCR.right + 300 > window.innerWidth) {
                left = 'auto';
                right = (window.innerWidth - mainContainerBCR.left) + 'px';
            }
        }
        let returnObj = {
            'top': betweenBCR ? (betweenBCR === null || betweenBCR === void 0 ? void 0 : betweenBCR.top) + 'px' : '0',
            'left': left,
            'right': right,
        };
        if (!this.filterRowIsVisible || !this.selectedHeader) {
            Object.assign(returnObj, {
                'display': 'none',
            });
        }
        return returnObj;
    }
    getFilterTranslateTxt(key) {
        let objKey = PIC.getInstance.userConfig.userLang.replace('-', '').toLowerCase();
        if (!['engb', 'dadk', 'dede'].includes(objKey))
            objKey = 'engb';
        return langObj[objKey][key];
    }
    renderFilterContainer() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
        let fTypes = [];
        let customName = '';
        if ((_b = (_a = this.selectedHeader) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.customFilter) {
            let rawFilter = (_c = this.selectedHeader.properties) === null || _c === void 0 ? void 0 : _c.customFilter;
            //@ts-ignore 
            if (rawFilter)
                rawFilter = atob(rawFilter);
            let parsed = JSON.parse(rawFilter ? Array.isArray(rawFilter) ? '' : rawFilter : '');
            if (Array.isArray(parsed)) {
                let found = parsed.find((item) => item === null || item === void 0 ? void 0 : item.filterName);
                if (found)
                    customName = found.filterName;
            }
            fTypes.push('custom');
        }
        let selectVals = this.getColumnUniqueValues();
        let checkedVals = [];
        if (selectVals.length <= 50 && selectVals.length > 0) {
            fTypes.push('select');
            if ((_d = this.selectedHeader) === null || _d === void 0 ? void 0 : _d.value) {
                let filter = this.activeFilters.filters.get((_e = this.selectedHeader) === null || _e === void 0 ? void 0 : _e.value);
                if (filter && filter.filterType === 'select') {
                    checkedVals = filter.value.split('|');
                }
            }
        }
        fTypes.push('startsWith');
        if (((_g = (_f = this.selectedHeader) === null || _f === void 0 ? void 0 : _f.properties) === null || _g === void 0 ? void 0 : _g.type) && ['number', 'date', 'time', 'datetime'].includes((_j = (_h = this.selectedHeader) === null || _h === void 0 ? void 0 : _h.properties) === null || _j === void 0 ? void 0 : _j.type)) {
            fTypes = fTypes.concat(['endsWith', 'equals', 'notequals', 'contains', 'notContains', 'greaterThan', 'lessThan', 'between']);
        }
        else {
            fTypes = fTypes.concat(['endsWith', 'equals', 'notequals', 'contains', 'notContains']);
        }
        return html `
            <div id="filterContainer"
                class="pic-body2"
                style=${styleMap(this.getFilterContainerStyle())}
            >
            ${fTypes.map((fType) => {
            var _a;
            return html `
                    <div class="filter-item${((_a = this.selectedHeader) === null || _a === void 0 ? void 0 : _a.filter) === fType ? ' selected' : ''} ${fType}"
                    id=${fType}
                    tabindex="0"
                    @click=${(e) => this.handleFilterTypeSelection(e, fType)}
                    @keydown=${(e) => this.filterKeyHandler(e, this.selectedHeader)}
                    >${fType === 'custom' && customName.length > 0 ? customName : this.getFilterTranslateTxt(fType)}</div>
                `;
        })}
            </div>
            <div id="secondaryFilterContainer"
                class="pic-body2"
                style=${styleMap(this.getSecondaryFilterContainerStyle())}
            >
                <input 
                    id="rangeFrom"
                    tabindex="-1"
                    class="filter-input pic-body2"
                    inputmode="decimal"
                    type="text"
                    autocomplete="off"
                    pattern=${this.getValidationPattern((_l = (_k = this.selectedHeader) === null || _k === void 0 ? void 0 : _k.properties) === null || _l === void 0 ? void 0 : _l.type)}
                    placeholder=${((_o = (_m = this.selectedHeader) === null || _m === void 0 ? void 0 : _m.properties) === null || _o === void 0 ? void 0 : _o.type) === 'date' ? PIC.getInstance.userConfig.dateFormat : ((_q = (_p = this.selectedHeader) === null || _p === void 0 ? void 0 : _p.properties) === null || _q === void 0 ? void 0 : _q.type) === 'time' ? PIC.getInstance.userConfig.timeFormat : ''}
                    @input=${(e) => this.handleRangeSelection(e)}
                    @keydown=${(e) => this.filterKeyHandler(e, this.selectedHeader)}
                    @beforeinput=${(e) => this.preventInvalidCharacters(e)}
                />
                <p style="display: inline;">${this.getFilterTranslateTxt('and')}</p>
                <input 
                    id="rangeTo"
                    tabindex="-1"
                    class="filter-input pic-body2"
                    inputmode="decimal"
                    type="text"
                    autocomplete="off"
                    pattern=${this.getValidationPattern((_s = (_r = this.selectedHeader) === null || _r === void 0 ? void 0 : _r.properties) === null || _s === void 0 ? void 0 : _s.type)}
                    placeholder=${((_u = (_t = this.selectedHeader) === null || _t === void 0 ? void 0 : _t.properties) === null || _u === void 0 ? void 0 : _u.type) === 'date' ? PIC.getInstance.userConfig.dateFormat : ((_w = (_v = this.selectedHeader) === null || _v === void 0 ? void 0 : _v.properties) === null || _w === void 0 ? void 0 : _w.type) === 'time' ? PIC.getInstance.userConfig.timeFormat : ''}
                    @input=${(e) => this.handleRangeSelection(e)}
                    @keydown=${(e) => this.filterKeyHandler(e, this.selectedHeader)}
                    @beforeinput=${(e) => this.preventInvalidCharacters(e)}
                />
            </div>
            <div id="selectContainer"
            class="pic-body2"
            style=${styleMap(this.getSecondaryFilterContainerStyle(true))}>
                ${this.renderToggleAllCheckbox(selectVals.length)}
                ${selectVals.map((val) => {
            let filterCheckboxClickAction = new PICAction();
            filterCheckboxClickAction.type_ = 'internal';
            filterCheckboxClickAction.eventFnc = this.modifySelectFilter.bind(this, val);
            return html `
                        <div class="select-item" @keydown=${(e) => { if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key))
                this.filterKeyHandler(e, this.selectedHeader); }}>
                            <pic-checkbox 
                            class='checkbox-toggle select-checkbox'
                            .isinput__=${false} 
                            .id_=${'filtermenu-checkbox-' + val} 
                            .eType=${'default'} 
                            .density=${'high'}
                            .minFitContent=${false}
                            .width=${'40px'}
                            .value=${checkedVals.length > 0 && !checkedVals.includes(val) ? 'false' : 'true'}
                            .clickAction=${filterCheckboxClickAction}
                            >
                            </pic-checkbox>
                            <label style="margin-right: auto;" for=${'filtermenu-checkbox-' + val}>${val === '!*' ? this.getFilterTranslateTxt('blank') : val}</label>
                        </div>
                    `;
        })}
            </div>
        `;
    }
    renderToggleAllCheckbox(numberOfCheckboxes) {
        var _a, _b;
        let filterCheckboxClickAction = new PICAction();
        filterCheckboxClickAction.type_ = 'internal';
        filterCheckboxClickAction.eventFnc = this.modifySelectFilter.bind(this, 'toggleAll');
        let initVal = 'true';
        let indeterminate = false;
        let active = this.selectedHeader ? this.activeFilters.filters.get((_a = this.selectedHeader) === null || _a === void 0 ? void 0 : _a.value) : undefined;
        if (((_b = this.selectedHeader) === null || _b === void 0 ? void 0 : _b.filter) === 'select' && active) {
            initVal = 'false';
            indeterminate = numberOfCheckboxes > active.value.split('|').length;
        }
        return html `
            <div class="select-item" @keydown=${(e) => { if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key))
            this.filterKeyHandler(e, this.selectedHeader); }}>
                <pic-checkbox 
                class=${`checkbox-toggle select-checkbox toggle-all ${indeterminate ? 'indeterminate' : ''}`}
                .isinput__=${false} 
                .id_=${'filtermenu-checkbox-toggle-all'} 
                .eType=${'default'} 
                .density=${'high'}
                .minFitContent=${false}
                .width=${'40px'}
                .mode_=${'regular'}
                .value=${initVal}
                .clickAction=${filterCheckboxClickAction}
                >
                </pic-checkbox>
                <label style="margin-right: auto;" for=${'filtermenu-checkbox-toggle-all'}>${this.getFilterTranslateTxt('selectAll')}</label>
            </div>
        `;
    }
    modifySelectFilter(cellTxt) {
        var _a, _b, _c, _d, _e;
        let filterInput = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.filter-row[data-internal-index="${(_b = this.selectedHeader) === null || _b === void 0 ? void 0 : _b.internalIndex}"] input`);
        if (filterInput)
            filterInput.value = '';
        let allCheckboxes = (_c = this.shadowRoot) === null || _c === void 0 ? void 0 : _c.querySelectorAll('.select-checkbox:not(.toggle-all)');
        let toggleAllCheckbox = (_d = this.shadowRoot) === null || _d === void 0 ? void 0 : _d.querySelector('.select-checkbox.toggle-all');
        if (toggleAllCheckbox && allCheckboxes.length > 0) {
            if (cellTxt === 'toggleAll') {
                let submitVal = toggleAllCheckbox.submitValue;
                toggleAllCheckbox.classList.remove('indeterminate');
                if (submitVal == 'true') {
                    this.setCheckValues('all');
                    this.addActiveFilter('', this.selectedHeader);
                }
                else {
                    this.setCheckValues();
                    this.addActiveFilter('|', this.selectedHeader, 'select');
                }
            }
            else {
                let allChecked = Array.from(allCheckboxes).every(checkbox => checkbox.submitValue === 'true');
                if (allChecked && ((_e = this.selectedHeader) === null || _e === void 0 ? void 0 : _e.filter) === 'select') {
                    this.addActiveFilter('', this.selectedHeader); // this removes the filter
                    this.toggleSelectAllCheckbox('checked');
                }
                else {
                    let valsForFilter = Array.from(allCheckboxes).filter(checkbox => checkbox.submitValue === 'true').map(checkbox => checkbox.id.split('filtermenu-checkbox-')[1]).join('|');
                    if (Array.from(allCheckboxes).some(checkbox => checkbox.submitValue === 'true')) {
                        this.toggleSelectAllCheckbox('indeterminate');
                    }
                    else {
                        this.toggleSelectAllCheckbox('unchecked');
                        valsForFilter = '|';
                    }
                    this.addActiveFilter(valsForFilter, this.selectedHeader, 'select');
                }
            }
        }
    }
    toggleSelectAllCheckbox(mode) {
        var _a;
        let toggleAllCheckbox = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.select-checkbox.toggle-all');
        if (toggleAllCheckbox) {
            switch (mode) {
                case 'checked':
                    toggleAllCheckbox.setChecked(true);
                    toggleAllCheckbox.classList.remove('indeterminate');
                    break;
                case 'unchecked':
                    toggleAllCheckbox.setChecked(false);
                    toggleAllCheckbox.classList.remove('indeterminate');
                    break;
                case 'indeterminate':
                    toggleAllCheckbox.setChecked(false);
                    toggleAllCheckbox.classList.add('indeterminate');
                    break;
            }
            toggleAllCheckbox.requestUpdate();
        }
    }
    getColumnUniqueValues() {
        var _a, _b;
        let columnValues = this.selectedHeader ? this.data.data.map((row) => {
            var _a, _b, _c, _d, _e;
            let val = row[this.selectedHeader.value].value;
            if ((_a = this.selectedHeader.properties) === null || _a === void 0 ? void 0 : _a.type) {
                switch ((_b = this.selectedHeader.properties) === null || _b === void 0 ? void 0 : _b.type) {
                    case 'date':
                        return PIC.getInstance.userConfig.formatDate(val).replace(/<br>|<br\/>/gm, '\n');
                    case 'time':
                        return PIC.getInstance.userConfig.formatTime(val).replace(/<br>|<br\/>/gm, '\n');
                    case 'datetime':
                        return PIC.getInstance.userConfig.formatDateTime(new Date(val)).replace(/<br>|<br\/>/gm, '\n');
                    case 'button':
                        break;
                    default:
                        return String(val).replace(/<br>|<br\/>/gm, '\n');
                }
            }
            else
                switch (this.selectedHeader.type) {
                    case 'checkbox':
                        return row.checked ? 'true' : 'false';
                    case 'tree':
                    case 'removal':
                    case 'move':
                    case 'modal':
                    case 'subTable':
                        break;
                    case 'string':
                        if (((_c = row[this.selectedHeader.value].properties) === null || _c === void 0 ? void 0 : _c.type) && ['button', 'icon', 'faicon'].includes(row[this.selectedHeader.value].properties.type))
                            break;
                        else
                            return String(val);
                    case 'number':
                        if (((_d = this.selectedHeader.properties) === null || _d === void 0 ? void 0 : _d.numberFormatOptions) !== undefined)
                            return toUserLocaleFloat(Number(val), (_e = this.selectedHeader.properties) === null || _e === void 0 ? void 0 : _e.numberFormatOptions).replace(/<br>|<br\/>/gm, '\n');
                        else
                            return toUserLocaleFloat(Number(val), { minimumFractionDigits: 0, maximumFractionDigits: 3 }).replace(/<br>|<br\/>/gm, '\n');
                    default:
                        return String(val).replace(/<br>|<br\/>/gm, '\n');
                }
        }) : [];
        let uniqueValues = [];
        for (const value of columnValues) {
            if (value !== undefined && value !== null && !uniqueValues.includes(value)) {
                if (String(value).length === 0) {
                    if (!uniqueValues.includes('!*'))
                        uniqueValues.push('!*');
                }
                else
                    uniqueValues.push(value);
            }
        }
        if (this.selectedHeader) {
            if (['date', 'datetime'].includes((_a = this.selectedHeader.properties) === null || _a === void 0 ? void 0 : _a.type)) {
                uniqueValues.sort((a, b) => {
                    var _a, _b;
                    if (a === '!*')
                        return -1;
                    if (b === '!*')
                        return 1;
                    let aVal = this.getNumberFromDateString(a, (((_a = this.selectedHeader.properties) === null || _a === void 0 ? void 0 : _a.type) === 'date' ? 'date' : 'datetime'));
                    let bVal = this.getNumberFromDateString(b, (((_b = this.selectedHeader.properties) === null || _b === void 0 ? void 0 : _b.type) === 'date' ? 'date' : 'datetime'));
                    if (aVal !== undefined && bVal !== undefined)
                        return aVal - bVal;
                    return 0;
                });
            }
            else if (((_b = this.selectedHeader.properties) === null || _b === void 0 ? void 0 : _b.type) === 'time') {
                uniqueValues.sort((a, b) => {
                    if (a === '!*')
                        return -1;
                    if (b === '!*')
                        return 1;
                    let aParts = a.split(':');
                    let bParts = b.split(':');
                    let aVal = Number(aParts[0]) * 3600 + (aParts.length > 1 ? Number(aParts[1]) * 60 : 0) + (aParts.length > 2 ? Number(aParts[2]) : 0);
                    let bVal = Number(bParts[0]) * 3600 + (bParts.length > 1 ? Number(bParts[1]) * 60 : 0) + (bParts.length > 2 ? Number(bParts[2]) : 0);
                    if (aVal !== undefined && bVal !== undefined)
                        return aVal - bVal;
                    return 0;
                });
            }
            else if (this.selectedHeader.type === 'number') {
                uniqueValues.sort((a, b) => {
                    if (a === '!*')
                        return -1;
                    if (b === '!*')
                        return 1;
                    let aVal = parseLocaleNumber(a, PIC.getInstance.userConfig.numberLang);
                    let bVal = parseLocaleNumber(b, PIC.getInstance.userConfig.numberLang);
                    if (aVal !== undefined && bVal !== undefined)
                        return aVal - bVal;
                    return 0;
                });
            }
            else {
                uniqueValues.sort((a, b) => {
                    if (a === '!*')
                        return -1;
                    if (b === '!*')
                        return 1;
                    return a.localeCompare(b);
                });
            }
        }
        return uniqueValues;
    }
    getRowClass(iData) {
        var _a;
        let rowIdx = iData.internalIndex;
        let isActive = false;
        if (this.activeRows && this.activeBaseRow && this.activeEndRow) {
            if (rowIdx >= this.activeBaseRow[0] && rowIdx <= this.activeEndRow[0]) {
                isActive = true;
            }
            if (this.activeRows.find(row => row[0] === rowIdx)) {
                isActive = true;
            }
        }
        return {
            'grid-row': true,
            ['row-' + rowIdx]: true,
            'active-row': isActive && this.selectionMode !== 'none',
            'clickable': Boolean((_a = iData.__properties__) === null || _a === void 0 ? void 0 : _a.__clickEvent__),
            'alternate-bg': this.alternateLineColor ? true : false
        };
    }
    getRowStyle(row) {
        var _a, _b;
        // Mix a slice of the current theme's ink color (dark in light mode,
        // light in dark mode) into the row's own background, so hover/active
        // darken in light mode and lighten in dark mode instead of always
        // blending toward a hardcoded white.
        return {
            '--elm-bg-hover-color': ((_a = row.__properties__) === null || _a === void 0 ? void 0 : _a.bgHoverColor) ? (_b = row.__properties__) === null || _b === void 0 ? void 0 : _b.bgHoverColor : 'color-mix(in srgb, var(--table-row-cell-bgcolor), var(--pic-onSurfaceHighColor) 13%)',
            '--elm-bg-active-color': 'color-mix(in srgb, var(--table-row-cell-bgcolor), var(--pic-onSurfaceHighColor) 30%)'
        };
    }
    /**
     * Render table body. Rows / data
     * @param data
     * @returns
     */
    renderTableBody(data, offset) {
        if (this.dataLoadError)
            return html ``;
        let myData;
        if (typeof offset === 'number' && offset > -1) {
            myData = data.slice(offset, offset + this.rowsPerPage);
            //console.log('VisData', data, myData, offset, offset + this.rowsPerPage)
        }
        else if (this.useLazyRender && this.lazyPage[1] > 0) {
            myData = data.slice(this.lazyPage[0], this.lazyPage[1] + 1);
        }
        else
            myData = data;
        switch (this.rowDensity) {
            case 'high':
                this.cellCompactMode = 'compact';
                break;
            case 'medium':
                this.cellCompactMode = 'cozy';
                break;
            case 'low':
                this.cellCompactMode = 'roomy';
                break;
            default:
                if (this.compactMode)
                    this.cellCompactMode = 'compact';
                else
                    this.cellCompactMode = 'cozy';
                break;
        }
        let x = html `
			${repeat(myData, (data_) => data_.internalIndex, (data_, idx) => {
            var _a;
            if (((_a = data_.__properties__) === null || _a === void 0 ? void 0 : _a.focus) && this.selectionMode !== 'none') {
                if (!this.activeBaseRow)
                    this.activeBaseRow = [data_.internalIndex, idx];
                if (!this.activeEndRow)
                    this.activeEndRow = [data_.internalIndex, idx];
                if (this.selectionMode !== 'multi') {
                    if (this.activeRows.length < 1)
                        this.activeRows.push([data_.internalIndex, idx]);
                }
                else {
                    this.activeRows.push([data_.internalIndex, idx]);
                    this.activeEndRow = [data_.internalIndex, idx];
                    /* Removing focus attribute so rerender can change active / scrollto */
                    data_.__properties__.focus = false;
                    /* This will trigger another update */
                }
                this.scrollToRow = this.activeBaseRow;
            }
            return html `
					<div class=${classMap(this.getRowClass(data_))}
                    .item=${data_} style="${styleMap(this.getRowStyle(data_))}">
                        ${repeat(this.actuallyVisibleHeaders, (item, index) => this.renderCell(item, data_, index))}
					</div>
					${this.renderSubTables(data_)}
					`;
        })}
            ${this.renderAddNewRow()}
            ${this.renderGridFooter()}
        `;
        if (!this.savedFiltersApplied && this.saveFilters) {
            if (!this.filterRowIsVisible)
                this.toggleFilters();
            this.getSavedFilters();
            this.savedFiltersApplied = true;
        }
        return x;
    }
    renderSubTableTitle(iTitle) {
        return html `
        <hr>
        <p class="pic-subtitle1">${iTitle}</p>
        `;
    }
    renderAddNewRow() {
        if (this.editable && this.addRemoveRows && !this.readOnly) {
            return html `
            <div class="new-row" style="grid-column-start: span ${this.visibleHeaders.length};">
                <i class="add-new-row"></i>
            </div>
            `;
        }
        else
            return nothing;
    }
    renderSubTableHdr(headers) {
        let tooltipPlacement = ['bottom'];
        if (headers)
            return html `
            <div class="grid-header">
            ${headers.map(hdr => {
                var _a, _b, _c, _d;
                return html `
                <div title=${hdr.text} 
                style=${styleMap(this.styleAdder(this.getGridCellStyle(hdr, true), 'background-color', 'var(--table-header-cell-bgcolor)'))}
                .item=${hdr} 
                class=${classMap(this.getHeaderClass(hdr))}
                @click=${() => this.sortHeader(hdr)}> ${this.renderHeaderCell(hdr)} 
                <pic-tooltip class=${classMap(this.hideTooltip())} .allowedPlacements=${tooltipPlacement}>${(_d = (_b = (_a = hdr.properties) === null || _a === void 0 ? void 0 : _a.toolTip) !== null && _b !== void 0 ? _b : (_c = hdr.properties) === null || _c === void 0 ? void 0 : _c.hdrtitle) !== null && _d !== void 0 ? _d : hdr.text}</pic-tooltip>
            </div>`;
            })}
            </div>   
        `;
        else
            return html ``;
    }
    renderSubTableRow(headers, data) {
        if (data)
            return html `
        ${repeat(data, (data_) => data_.internalIndex, (data_, idx) => {
                return html `
                <div class="grid-row ${'row-' + data_.internalIndex}" .item=${data_} >
                    ${headers.map(h => html `${this.renderCell(h, data_)}`)}
                </div>
                `;
            })}
        `;
        else
            return html ``;
    }
    getSubTablesContainerStyle(treeIdx) {
        return {
            'grid-column-start': 1,
            'grid-column-end': this.actuallyVisibleHeaders.length - 1,
        };
    }
    getSubTableStyle(headers) {
        return {
            'grid-template-columns': 'repeat(' + headers.length + ',  minmax(15px, auto))'
        };
    }
    renderSubTables(curItem) {
        if (curItem.treeToggeled) {
            /* check for subtabels */
            let subTabels = this.data.headers.filter(hdr => hdr.type == 'subTable')
                .map(subTable => {
                return {
                    subTableName: subTable.value,
                    subTableData: curItem[subTable.value]
                };
            });
            if (subTabels && subTabels.length > 0) {
                return subTabels.map(subTable => {
                    var _a, _b;
                    let data = (_a = subTable.subTableData) === null || _a === void 0 ? void 0 : _a.data;
                    let headers = (_b = subTable.subTableData) === null || _b === void 0 ? void 0 : _b.headers;
                    if (!headers) {
                        PIC.getInstance.msg('sub row data issue', subTable, curItem);
                        return;
                    }
                    else {
                        let treeIdx = 0;
                        if (this.checkbox) {
                            treeIdx++;
                            if (!headers.find(hdr => hdr == checkboxHeader))
                                headers.unshift(checkboxHeader);
                        }
                        if (this.tree) {
                            treeIdx++;
                            if (!headers.find(hdr => hdr == treeHeader))
                                headers.unshift(treeHeader);
                        }
                        if (this.editable && this.addRemoveRows) {
                            treeIdx++;
                            if (!headers.find(hdr => hdr == removalHeader)) {
                                removalHeader.internalIndex = -(this.data.headers.length + 3);
                                headers.push(removalHeader);
                            }
                        }
                        if (this.dragndrop && !this.addRemoveRows) {
                            treeIdx++;
                            if (!headers.find(hdr => hdr == moveHeader)) {
                                moveHeader.internalIndex = -(this.data.headers.length + 3);
                                headers.push(moveHeader);
                            }
                        }
                        return html `
                        <div class='subTablesContainer' style=${styleMap(this.getSubTablesContainerStyle(treeIdx))}> 
                            ${this.renderSubTableTitle(subTable.subTableName == '__subTable__' ? '' : subTable.subTableName)}
                            <div class='subTable'  style=${styleMap(this.getSubTableStyle(headers))}>
                                ${this.renderSubTableHdr(headers)}
                                ${this.renderSubTableRow(headers, data)}
                            </div>
                            <hr>
                        </div>
                        `;
                    }
                });
            }
        }
    }
    getInputCellClass(header, data) {
        return {
            'inputCell': true,
            'pic-body2': true,
        };
    }
    getGridCellClass(header, data) {
        var _a, _b, _c, _d, _e;
        let addNumber = ((_a = data.properties) === null || _a === void 0 ? void 0 : _a.type) == 'number';
        addNumber = ((_b = header.properties) === null || _b === void 0 ? void 0 : _b.type) === 'number' /* || header.properties?.type == 'date' || header.properties?.type == 'time' */;
        let hasBorderProps = Boolean(((_c = data.properties) === null || _c === void 0 ? void 0 : _c.cellBorderColor) || ((_d = data.properties) === null || _d === void 0 ? void 0 : _d.cellBorderPixels));
        return {
            'grid-cell': true,
            'no-select': true,
            'pic-body2': true,
            // 'pic-body2': !addNumber,
            // 'pic-body2-mono': addNumber,
            'grid-sortable': this.getActingCellType(data, 'sortable', header),
            [this.cellCompactMode]: true,
            'clickable': header.type == 'modal' || Boolean((_e = data.properties) === null || _e === void 0 ? void 0 : _e.__clickEvent__),
            'cell-borders': hasBorderProps,
            // 'hide-overflow': data.properties?.fontproperty != 'wordwrap',
            // 'wrap-overflow': data.properties?.fontproperty == 'wordwrap'
        };
    }
    getGridCellSpanClass(header, data) {
        var _a, _b, _c, _d, _e, _f;
        let doWrap;
        if (Array.isArray((_a = header.properties) === null || _a === void 0 ? void 0 : _a.fontproperty))
            doWrap = ((_b = header.properties) === null || _b === void 0 ? void 0 : _b.fontproperty.find(prop => prop == 'wordwrap')) ? true : false;
        else if (data && Array.isArray((_c = data.properties) === null || _c === void 0 ? void 0 : _c.fontproperty))
            doWrap = ((_d = data.properties) === null || _d === void 0 ? void 0 : _d.fontproperty.find(prop => prop == 'wordwrap')) ? true : false;
        else if (this.autowidth)
            doWrap = true;
        else if (this.whitespace)
            doWrap = true;
        else
            doWrap = ((_e = header.properties) === null || _e === void 0 ? void 0 : _e.fontproperty) == 'wordwrap' || ((_f = data === null || data === void 0 ? void 0 : data.properties) === null || _f === void 0 ? void 0 : _f.fontproperty) == 'wordwrap';
        return {
            'hide-overflow': !doWrap,
            'wrap-overflow': doWrap
        };
    }
    getActingCellType(cell, type, header) {
        var _a, _b, _c, _d;
        if (!cell)
            return null;
        switch (type) {
            case 'align':
                break;
            case 'sortable': //Use table sort value, unless header specifically set to false
                return this.sort ? (((_a = header.properties) === null || _a === void 0 ? void 0 : _a.sortable) === false ? false : true) : false;
            case 'type': //Use cell type by default, or header if undefined, or string
                return (_b = cell.type) !== null && _b !== void 0 ? _b : ((_d = (_c = header.properties) === null || _c === void 0 ? void 0 : _c.type) !== null && _d !== void 0 ? _d : 'string');
            case 'width':
                break;
            default:
        }
    }
    fontProp2CSS_(iProp) {
        switch (iProp) {
            case 'bold':
                return { 'font-weight': 'bold' };
            case 'italic':
                return { 'font-style': 'italic' };
            case 'underline':
                return { 'text-decoration': 'underline' };
            default:
                return {};
        }
    }
    fontProp2CSS(iProp) {
        let styles = [];
        if (Array.isArray(iProp)) {
            iProp.map(prop => { styles.push(this.fontProp2CSS_(prop)); });
        }
        else
            styles.push(this.fontProp2CSS_(iProp));
        return styles.length > 0 ? styles.reduce((total, current) => total = Object.assign(Object.assign({}, total), current)) : {};
    }
    getHeaderTextStyle() {
        return this.highVisibilityHeaders ? { 'font-size': 'medium', 'font-weight': '600' } : {};
    }
    getGridCellStyle(header, isHeader, row, cell) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4;
        let styles = [];
        let perc = 0.87;
        let perc2 = 0.7;
        /* Headers */
        let headerBgColor = (_a = header.properties) === null || _a === void 0 ? void 0 : _a.bgcolor;
        if (!headerBgColor && this.alternateLineColor) {
            if (row) {
                let index = this.getRowIdxFromInternal(row.internalIndex);
                if (index % 2 === 0)
                    styles.push({ '--table-row-cell-bgcolor': this._alternateLineColor });
                else
                    styles.push({ '--table-row-cell-bgcolor': this._BGColor });
            }
        }
        if (headerBgColor) {
            styles.push({ '--table-row-cell-bgcolor': headerBgColor });
            styles.push({ '--elm-bg-hover-color': `rgb(from var(--table-row-cell-bgcolor) calc(r * ${perc}) calc(g * ${perc}) calc(b * ${perc}))` });
            styles.push({ '--elm-bg-active-color': `rgb(from var(--table-row-cell-bgcolor) calc(r * ${perc2}) calc(g * ${perc2}) calc(b * ${perc2}))` });
        }
        ;
        if ((_b = header.properties) === null || _b === void 0 ? void 0 : _b.color)
            styles.push({ '--table-row-cell-color': (_c = header.properties) === null || _c === void 0 ? void 0 : _c.color });
        if ((_d = header.properties) === null || _d === void 0 ? void 0 : _d.align) {
            styles.push({ 'text-align': (_e = header.properties) === null || _e === void 0 ? void 0 : _e.align });
            styles.push({ 'justify-content': (_f = header.properties) === null || _f === void 0 ? void 0 : _f.align });
        }
        if (this.cellCompactMode === 'compact' && isHeader && (header.value === ':checkbox' || header.value === ':tree'))
            styles.push({ 'padding': '0 5px 0 0' });
        if ((_g = header.properties) === null || _g === void 0 ? void 0 : _g.fontproperty)
            styles.push(this.fontProp2CSS((_h = header.properties) === null || _h === void 0 ? void 0 : _h.fontproperty));
        /* Compact header*/
        if (this.compactHeaderMode && isHeader && (header.value === ':checkbox' || header.value === ':tree'))
            styles.push({ 'padding': '0 2px 0 0' });
        if ((_j = header.properties) === null || _j === void 0 ? void 0 : _j.fontproperty)
            styles.push(this.fontProp2CSS((_k = header.properties) === null || _k === void 0 ? void 0 : _k.fontproperty));
        if (isHeader && ((_l = header.properties) === null || _l === void 0 ? void 0 : _l.width) && ['string', 'number', 'date', 'time', 'boolean', 'input'].includes(header.type))
            styles.push({ 'min-width': '50px' });
        /* Row */
        if (row) {
            let rowBgColor = (_m = row.__properties__) === null || _m === void 0 ? void 0 : _m.bgcolor;
            if (rowBgColor) {
                if (['NEW', 'READY', 'DEPARTED', 'DONE', 'SETTLED', 'CANCELED', 'INVALID'].includes(rowBgColor)) {
                    perc = 0.95;
                    perc2 = 0.85;
                    styles.push({ '--table-row-cell-bgcolor': `var(--table-status-${rowBgColor.toLowerCase()}-color)` });
                }
                else {
                    styles.push({ '--table-row-cell-bgcolor': rowBgColor });
                }
                styles.push({ '--elm-bg-hover-color': `rgb(from var(--table-row-cell-bgcolor) calc(r * ${perc}) calc(g * ${perc}) calc(b * ${perc}))` });
                styles.push({ '--elm-bg-active-color': `rgb(from var(--table-row-cell-bgcolor) calc(r * ${perc2}) calc(g * ${perc2}) calc(b * ${perc2}))` });
            }
            if ((_o = row.__properties__) === null || _o === void 0 ? void 0 : _o.color)
                styles.push({ '--table-row-cell-color': (_p = row.__properties__) === null || _p === void 0 ? void 0 : _p.color });
            if ((_q = row.__properties__) === null || _q === void 0 ? void 0 : _q.bgHoverColor)
                styles.push({ '--elm-bg-hover-color': row.__properties__.bgHoverColor });
        }
        /* Cell */
        if (cell) {
            let cellBgColor = (_r = cell.properties) === null || _r === void 0 ? void 0 : _r.bgcolor;
            if ((_s = cell.properties) === null || _s === void 0 ? void 0 : _s.align)
                styles.push({ 'text-align': (_t = cell.properties) === null || _t === void 0 ? void 0 : _t.align });
            if (cell.type === 'number') {
                if ((_u = cell.properties) === null || _u === void 0 ? void 0 : _u.align)
                    styles.push({ 'text-align': (_v = cell.properties) === null || _v === void 0 ? void 0 : _v.align });
                else if ((_w = header === null || header === void 0 ? void 0 : header.properties) === null || _w === void 0 ? void 0 : _w.align) {
                    //do nothing, just obey the header alignment
                }
                else
                    styles.push({ 'text-align': 'right' }); // right-align is default
            }
            if (cellBgColor) {
                if (['NEW', 'READY', 'DEPARTED', 'DONE', 'SETTLED', 'CANCELED', 'INVALID'].includes(cellBgColor)) {
                    perc = 0.95;
                    perc2 = 0.85;
                    styles.push({ '--table-row-cell-bgcolor': `var(--table-status-${cellBgColor.toLowerCase()}-color)` });
                }
                else {
                    styles.push({ '--table-row-cell-bgcolor': cellBgColor });
                }
                styles.push({ '--elm-bg-hover-color': `rgb(from var(--table-row-cell-bgcolor) calc(r * ${perc}) calc(g * ${perc}) calc(b * ${perc}))` });
                styles.push({ '--elm-bg-active-color': `rgb(from var(--table-row-cell-bgcolor) calc(r * ${perc2}) calc(g * ${perc2}) calc(b * ${perc2}))` });
            }
            if ((_x = cell.properties) === null || _x === void 0 ? void 0 : _x.color)
                styles.push({ '--table-row-cell-color': (_y = cell.properties) === null || _y === void 0 ? void 0 : _y.color });
            if ((_z = cell.properties) === null || _z === void 0 ? void 0 : _z.cellBorderPixels) {
                if ((typeof cell.properties.cellBorderPixels === 'number')) {
                    styles.push({ '--table-row-cell-border-width': ((_0 = cell.properties) === null || _0 === void 0 ? void 0 : _0.cellBorderPixels) + 'px' }); // if the value is simply a number
                }
                if ((typeof cell.properties.cellBorderPixels === 'string')) {
                    if (/^\d+/.test(cell.properties.cellBorderPixels)) {
                        if (/(px|em|rem|%|vh|vw|pt|cm|mm|in|pc|ex|ch|vmin|vmax)$/.test(cell.properties.cellBorderPixels)) {
                            styles.push({ '--table-row-cell-border-width': cell.properties.cellBorderPixels });
                        }
                        else {
                            styles.push({ '--table-row-cell-border-width': cell.properties.cellBorderPixels + 'px' });
                        }
                    }
                }
            }
            if ((_1 = cell.properties) === null || _1 === void 0 ? void 0 : _1.cellBorderColor)
                styles.push({ '--table-row-cell-border-color': (_2 = cell.properties) === null || _2 === void 0 ? void 0 : _2.cellBorderColor });
            if ((_3 = cell.properties) === null || _3 === void 0 ? void 0 : _3.fontproperty)
                styles.push(this.fontProp2CSS((_4 = cell.properties) === null || _4 === void 0 ? void 0 : _4.fontproperty));
        }
        return styles.length > 0 ? styles.reduce((total, current) => total = Object.assign(Object.assign({}, total), current)) : {};
    }
    renderCheckBoxCell(header, data) {
        let gridCheckBoxAction = new PICAction();
        gridCheckBoxAction.type_ = 'internal';
        gridCheckBoxAction.eventFnc = ((e) => {
            this.setCheckedRow(data, !data.checked);
            if (this.boundCanvas)
                this.sendCheckedRowsToBoundCanvas();
            this.requestUpdate();
        }).bind(this);
        return html `
            <div 
                style=${styleMap(this.getGridCellStyle(header, false, data))}
                class="grid-cell no-select ${this.cellCompactMode === 'compact' ? 'compact' : ''}">
            <pic-checkbox 
                class='checkbox-toggle' 
                tabindex=-1
                .id_=${'grid-checkbox-checkbox-' + data.internalIndex} 
                .isinput__=${false}
                .value=${data.checked ? 'true' : 'false'}
                .eType=${'default'} 
                .density=${'inline'} 
                .minFitContent=${false}
                .width=${'40px'}
                .clickAction=${gridCheckBoxAction}
                >
            </pic-checkbox>
        </div> 
        `;
    }
    renderTreeCell(header, data) {
        return html `
        <div 
            style=${styleMap(this.getGridCellStyle(header, false, data))}
            class="grid-cell no-select pic-body2 ${this.cellCompactMode === 'compact' ? 'compact' : ''}">
            <div class="grid-treeCaret" @click=${this.toggleTree}></div>  
        </div>
        `;
    }
    renderRemoveCell(header, data) {
        return html `
        <div 
            style=${styleMap(this.getGridCellStyle(header, false, data))}
            class="grid-cell no-select pic-body2 ${this.cellCompactMode === 'compact' ? 'compact' : ''}">
            <div class="grid-remove" @click=${() => this.deleteRow(data)}></div>
            <div class="grid-move"></div>
        </div>
        `;
    }
    rendermoveCell(header, data) {
        if (this.dragndrop) {
            return html `
            <div style=${styleMap(this.getGridCellStyle(header, false, data))}
                class="grid-cell no-select pic-body2 ${this.cellCompactMode === 'compact' ? 'compact' : ''}">
                <div class="grid-move"></div>
            </div> `;
        }
    }
    renderFaIconCell(header, data) {
        var _a, _b;
        let clickHandler = (event) => this.handleRowClick(event, data.internalIndex);
        let curCell = data[header.value];
        let curTxt = String(curCell.value);
        return html `
            <div 
                style=${styleMap(this.getGridCellStyle(header, false, data, curCell))} 
                class="grid-cell no-select pic-body2 ${this.cellCompactMode === 'compact' ? 'compact' : ''}"
                @click=${clickHandler}
                .item=${curCell}>
                <i  class=${classMap(this.classAdder(getFaIconClass(), curTxt, true))}
                    style=${styleMap(getIconAlign(curCell))}>
                </i>
                ${((_a = curCell.properties) === null || _a === void 0 ? void 0 : _a.toolTip) ? html `<pic-tooltip>${(_b = curCell.properties) === null || _b === void 0 ? void 0 : _b.toolTip}</pic-tooltip>` : ''}
            </div>
        `;
        function getIconAlign(cell) {
            var _a, _b;
            if (((_a = cell.properties) === null || _a === void 0 ? void 0 : _a.align) == 'left') {
                return { 'margin-left': '0.5em', 'margin-right': 'auto' };
            }
            else if (((_b = cell.properties) === null || _b === void 0 ? void 0 : _b.align) == 'right') {
                return { 'margin-left': 'auto', 'margin-right': '0.5em' };
            }
            else {
                return { 'margin': 'auto' };
            }
        }
        function getFaIconClass() {
            return {
                icon: true,
                fa: true,
                'fa-solid': true,
            };
        }
    }
    renderIconCell(header, data) {
        var _a, _b, _c, _d;
        let clickHandler = (event) => this.handleRowClick(event, data.internalIndex);
        let curCell = data[header.value];
        let curTxt = String(curCell.value);
        if (curTxt.toLowerCase().startsWith('/')) {
            if (curTxt.toLowerCase().includes('/svg/icons/iconpack.svg#'))
                return html `
                <div 
                    style=${styleMap(this.getGridCellStyle(header, false, data, curCell))} 
                    class="grid-cell no-select pic-body2 ${this.cellCompactMode === 'compact' ? 'compact' : ''}"
                    @click=${clickHandler}
                    .item=${curCell}>
                    <svg
                        style=${styleMap(getIconStyle(curCell))}
                        class="icon-cell"
                        src=${curTxt}>
                        <use class="pic-nohover" a="" href=${curTxt} xlink:href=${curTxt}></use>
                    </svg>
                    ${((_a = curCell.properties) === null || _a === void 0 ? void 0 : _a.toolTip) ? html `<pic-tooltip>${(_b = curCell.properties) === null || _b === void 0 ? void 0 : _b.toolTip}</pic-tooltip>` : ''}
                </div>
            `;
            else
                return html `
                <div 
                    style=${styleMap(this.getGridCellStyle(header, false, data, curCell))} 
                    class="grid-cell no-select pic-body2 ${this.cellCompactMode === 'compact' ? 'compact' : ''}"
                    @click=${clickHandler}
                    .item=${curCell}>
                    <img
                        style=${styleMap(getIconStyle(curCell))}
                        class="icon-cell"
                        src=${curTxt}>
                    ${((_c = curCell.properties) === null || _c === void 0 ? void 0 : _c.toolTip) ? html `<pic-tooltip>${(_d = curCell.properties) === null || _d === void 0 ? void 0 : _d.toolTip}</pic-tooltip>` : ''}
                </div>
            `;
        }
        function getIconStyle(cell) {
            var _a, _b, _c, _d, _e;
            let styles = [];
            if ((_a = cell.properties) === null || _a === void 0 ? void 0 : _a.width) {
                styles.push({ 'width': (_b = cell.properties) === null || _b === void 0 ? void 0 : _b.width });
                styles.push({ 'height': (_c = cell.properties) === null || _c === void 0 ? void 0 : _c.width });
            }
            if (((_d = cell.properties) === null || _d === void 0 ? void 0 : _d.align) == 'left') {
                styles.push({ 'margin-left': '0.5em' });
            }
            else if (((_e = cell.properties) === null || _e === void 0 ? void 0 : _e.align) == 'right') {
                styles.push({ 'margin-right': '0.5em' });
            }
            else {
                styles.push({ 'margin': 'auto' });
            }
            return styles.length > 0 ? styles.reduce((total, current) => total = Object.assign(Object.assign({}, total), current)) : {};
        }
    }
    renderButtonCell(header, data) {
        var _a, _b, _c, _d, _e;
        let clickHandler = (event) => this.handleRowClick(event, data.internalIndex);
        let curCell = data[header.value];
        let inpBtn = JSON.parse(String(curCell.value));
        let action = new PICAction();
        action.type_ = 'internal';
        if (inpBtn.Event) {
            action.eventFnc = (event) => {
                /* need to call webtable clickhandler first */
                clickHandler(event);
                doSubmit(inpBtn.Event);
            };
        }
        else if (inpBtn.loadPagePath) {
            action.eventFnc = (event) => {
                /* need to call webtable clickhandler first */
                clickHandler(event);
                loadPage(inpBtn.loadPagePath, inpBtn.loadPageParm, inpBtn.loadPageOpt);
            };
        }
        else {
            action.eventFnc = (event) => {
                clickHandler(event);
            };
        }
        return html `
            <div style=${styleMap(this.getGridCellStyle(header, false, data, curCell))} 
                class=${classMap(this.getGridCellClass(header, curCell))}
                .item=${curCell}
                >
                <pic-button 
                    .id=${inpBtn.Id}
                    density=${inpBtn.Density}
                    eType=${inpBtn.Etype}
                    width=${(_e = (_c = (_a = inpBtn.Width) !== null && _a !== void 0 ? _a : (_b = curCell.properties) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : (_d = header.properties) === null || _d === void 0 ? void 0 : _d.width) !== null && _e !== void 0 ? _e : '100%'}
                    .disabled_=${inpBtn.Disabled}
                    .label=${inpBtn.Label}
                    .action=${action}
                    >
                </pic-button>
            </div>
        `;
    }
    renderModalCell(header, data) {
        var _a, _b;
        let curCell = data[header.value];
        let curTxt = String(curCell.value);
        const prog2run = (_a = curCell.properties) === null || _a === void 0 ? void 0 : _a.prog2run;
        const params = (_b = curCell.properties) === null || _b === void 0 ? void 0 : _b.params;
        const openModalEvent = (event) => { prog2run && params ? loadPage(prog2run, params, { isNew: true }) : ''; };
        return this.renderDefaultCell(header, data, curCell, curTxt, openModalEvent);
    }
    renderNumberCell(header, data) {
        var _a;
        //Render numbers with either general lang fractions, or specific header fractions
        let curCell = data[header.value];
        let numberFormatOptions = (_a = header.properties) === null || _a === void 0 ? void 0 : _a.numberFormatOptions;
        let curTxt = typeof numberFormatOptions === 'undefined'
            ? toUserLocaleFloat(Number(curCell.value), { minimumFractionDigits: 0, maximumFractionDigits: 3 }) /* to not break existing solutions */
            : toUserLocaleFloat(Number(curCell.value), numberFormatOptions);
        return this.renderDefaultCell(header, data, curCell, curTxt);
    }
    renderStringCell(header, data) {
        let curCell = data[header.value];
        let curTxt = String(curCell.value);
        return this.renderDefaultCell(header, data, curCell, curTxt);
    }
    renderDateCell(header, data) {
        let curCell = data[header.value];
        let curTxt = PIC.getInstance.userConfig.formatDate(String(curCell.value));
        return this.renderDefaultCell(header, data, curCell, curTxt);
    }
    renderTimeCell(header, data) {
        let curCell = data[header.value];
        let curTxt = PIC.getInstance.userConfig.formatTime(curCell.value);
        return this.renderDefaultCell(header, data, curCell, curTxt);
    }
    mapDateTimeFormat(enumFormat) {
        if (!enumFormat)
            return undefined;
        const format = enumFormat.toUpperCase();
        // Handle predefined types
        const predefinedMap = {
            'DATE': 'date',
            'TIME': 'time',
            'DATETIME': 'datetime'
        };
        if (predefinedMap[format]) {
            return predefinedMap[format];
        }
        // Map backend enum formats to frontend format strings
        const formatMap = {
            // Individual date components
            'YYYY': 'YYYY',
            'MM': 'MM',
            'DD': 'dd',
            'YYYY_MM': 'YYYY-MM',
            'MM_DD': 'MM-dd',
            // Time formats
            'HH': 'HH',
            'MINUTES': 'mm',
            'SS': 'ss',
            'HH_MM': 'HH:mm',
            'MM_SS': 'mm:ss',
            'HH_MM_SS': 'HH:mm:ss',
        };
        return formatMap[format];
    }
    renderDateTimeCell(header, data) {
        var _a;
        let curCell = data[header.value];
        let cellFormat = this.mapDateTimeFormat((_a = curCell.properties) === null || _a === void 0 ? void 0 : _a.datetimeFormat);
        let dateObj = new Date(curCell.value);
        if (isNaN(dateObj.getTime())) {
            return this.renderDefaultCell(header, data, curCell, String(curCell.value || ''));
        }
        let curTxt;
        if (cellFormat === 'date') {
            curTxt = PIC.getInstance.userConfig.formatDate(dateObj);
        }
        else if (cellFormat === 'time') {
            let then = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 0, 0, 0);
            let timeInSeconds = (dateObj.getTime() - then.getTime()) / 1000;
            curTxt = PIC.getInstance.userConfig.formatTime(timeInSeconds);
        }
        else if (cellFormat && cellFormat !== 'datetime') {
            curTxt = PIC.getInstance.userConfig.translateDate(dateObj, cellFormat);
        }
        else {
            curTxt = PIC.getInstance.userConfig.formatDateTime(dateObj);
        }
        return this.renderDefaultCell(header, data, curCell, curTxt);
    }
    renderInputCell(header, data, curCell, curTxt, clickHandler) {
        var _a, _b;
        let tCellData = data[header.value];
        curCell = curCell ? curCell : tCellData;
        curTxt = curTxt ? curTxt : String(curCell.value);
        return html `
        <div style=${styleMap(this.getGridCellStyle(header, false, data, curCell))} 
            class=${classMap(this.getGridCellClass(header, curCell))}
            .item=${tCellData}
        >
        <input 
            class=${classMap(this.getInputCellClass(header, tCellData))} 
            id=${`inputCell-${data.internalIndex}${header.internalIndex}`} 
            type=${((_a = tCellData.properties) === null || _a === void 0 ? void 0 : _a.type) == 'number' || ((_b = header.properties) === null || _b === void 0 ? void 0 : _b.type) == 'number' ? 'number' : 'text'} 
            ?readonly=${this.readOnly}
            value=${curTxt.replace(/<br>|<br\/>/gm, '\n')}
            @change=${(e) => this.handleInputUpdate(e, header, tCellData)}
            >
        </div>
        `;
    }
    renderDefaultCell(header, data, curCell, curTxt, clickHandler) {
        var _a, _b, _c, _d;
        if (!clickHandler)
            clickHandler = (event) => this.handleRowClick(event, data.internalIndex);
        if (this.editable)
            return this.renderInputCell(header, data, curCell, curTxt, clickHandler);
        if (!((_a = header.properties) === null || _a === void 0 ? void 0 : _a.serializeNull) && curTxt === 'null')
            curTxt = '';
        let hasUserTooltip = Boolean((_b = curCell.properties) === null || _b === void 0 ? void 0 : _b.toolTip);
        return html `
            <div style=${styleMap(this.getGridCellStyle(header, false, data, curCell))} 
                class=${classMap(this.getGridCellClass(header, curCell))}
                @click=${clickHandler}
                .item=${curCell}
            >
                <span class=${classMap(this.getGridCellSpanClass(header, curCell))}
                    @mouseenter=${(event) => {
            let target = event.target;
            if (!hasUserTooltip && !target.hasAttribute('has-tooltip') && target.offsetWidth < target.scrollWidth) {
                let tooltipEl = document.createElement('pic-tooltip');
                tooltipEl.innerHTML = '<span>' + curTxt.replace(/<br>|<br\/>/gm, '\n') + '</span>';
                target.insertAdjacentElement('afterend', tooltipEl);
                target.setAttribute('has-tooltip', 'true');
                tooltipEl.show();
            }
            else if (!hasUserTooltip && target.hasAttribute('has-tooltip') && target.offsetWidth >= target.scrollWidth) {
                //remove if column has changed size to fit text
                let siblingTooltip = target.nextElementSibling;
                if (siblingTooltip && (siblingTooltip === null || siblingTooltip === void 0 ? void 0 : siblingTooltip.nodeName) === 'PIC-TOOLTIP') {
                    siblingTooltip.remove();
                }
            }
        }}
                    @mouseleave=${(event) => {
            let target = event.target;
            if (!hasUserTooltip && target.hasAttribute('has-tooltip')) {
                let siblingTooltip = target.nextElementSibling;
                if (siblingTooltip && (siblingTooltip === null || siblingTooltip === void 0 ? void 0 : siblingTooltip.nodeName) === 'PIC-TOOLTIP') {
                    siblingTooltip.hide();
                }
            }
        }}
                >${curTxt.replace(/<br>|<br\/>/gm, '\n')}</span>
            ${((_c = curCell.properties) === null || _c === void 0 ? void 0 : _c.toolTip) ? html `<pic-tooltip>${(_d = curCell.properties) === null || _d === void 0 ? void 0 : _d.toolTip}</pic-tooltip>` : ''}
            </div>
            `;
    }
    renderCell(header, data, index) {
        // if (!data.hasOwnProperty(header.value)) return '';
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if ((_a = header.properties) === null || _a === void 0 ? void 0 : _a.type) {
            if (((_b = header.properties) === null || _b === void 0 ? void 0 : _b.type) == 'date')
                return this.renderDateCell(header, data);
            else if (((_c = header.properties) === null || _c === void 0 ? void 0 : _c.type) == 'time')
                return this.renderTimeCell(header, data);
            else if (((_d = header.properties) === null || _d === void 0 ? void 0 : _d.type) == 'datetime')
                return this.renderDateTimeCell(header, data);
            else if (((_e = header.properties) === null || _e === void 0 ? void 0 : _e.type) == 'input')
                return this.renderInputCell(header, data);
            else if (((_f = header.properties) === null || _f === void 0 ? void 0 : _f.type) === 'icon')
                return this.renderIconCell(header, data);
            else if (((_g = header.properties) === null || _g === void 0 ? void 0 : _g.type) === 'faicon')
                return this.renderFaIconCell(header, data);
            else if (((_h = header.properties) === null || _h === void 0 ? void 0 : _h.type) === 'button')
                return this.renderButtonCell(header, data);
            else
                return this.renderStringCell(header, data);
        }
        else
            switch (header.type) {
                case 'checkbox':
                    return this.renderCheckBoxCell(header, data);
                case 'tree':
                    return this.renderTreeCell(header, data);
                case 'removal':
                    return this.renderRemoveCell(header, data);
                case 'move':
                    return this.rendermoveCell(header, data);
                case 'modal':
                    return this.renderModalCell(header, data);
                case 'subTable':
                    break;
                case 'string':
                    if (((_j = data[header.value].properties) === null || _j === void 0 ? void 0 : _j.type) == 'icon')
                        return this.renderIconCell(header, data);
                    else if (((_k = data[header.value].properties) === null || _k === void 0 ? void 0 : _k.type) == 'faicon') {
                        return this.renderFaIconCell(header, data);
                    }
                    else if (((_l = data[header.value].properties) === null || _l === void 0 ? void 0 : _l.type) == 'button')
                        return this.renderButtonCell(header, data);
                    else
                        return this.renderStringCell(header, data);
                case 'number':
                    return this.renderNumberCell(header, data);
                case 'boolean':
                    return this.renderStringCell(header, data);
                case 'input':
                    return this.renderInputCell(header, data);
                default:
                    return this.renderStringCell(header, data);
            }
    }
    renderEmptyDiv(numberOfDiv, divClass) {
        let divHolding = [];
        for (let i = 0; i < numberOfDiv; i++) {
            divHolding.push(divClass);
        }
        return divHolding.map(h => html `<div class=${h}></div>`);
    }
    renderGridFooter() {
        var _a;
        if (!this.sumfooter)
            return nothing;
        let hdrDiff = this.visibleHeaders.length;
        let selectedRows;
        if (this.checkbox && this.useCheckAsMarked) {
            let checkedItems = Array.from(this._checkboxAll).filter(c => c.submitValue == 'true');
            let checkedRows = checkedItems.map(x => x.closest('.grid-row'));
            selectedRows = checkedRows;
        }
        else {
            selectedRows = this.activeRows.map(x => { var _a; return (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`.row-${x[0]}`); });
        }
        let itemList = selectedRows.map(row => row === null || row === void 0 ? void 0 : row.item);
        let itemKeys = this.visibleHeaders.map(hdr => hdr.value);
        let summy = new Array(itemKeys.length).fill(NaN);
        let caseHandler = 'SKIP';
        let rowModes = [];
        //TODO: this is broken, will not count the correct number of cells (difference between data.headers, visibleHeaders && actuallyVisibleHeaders)
        for (let i = 0; i < this.visibleHeaders.length; i++) {
            if ((_a = this.visibleHeaders[i].properties) === null || _a === void 0 ? void 0 : _a.hdrsum) {
                rowModes.push({ idx: i, type: 'SUMALL' });
                caseHandler = caseHandler == 'SUMSELECTED' ? 'BOTH' : 'SUMALL';
                summy[i] = 0;
            }
            else if (this.visibleHeaders[i].type == 'number') {
                rowModes.push({ idx: i, type: 'SUMSELECTED' });
                caseHandler = caseHandler == 'SUMALL' ? 'BOTH' : 'SUMSELECTED';
                summy[i] = 0;
            }
        }
        let SC = rowModes.filter(v => { return v.type == 'SUMSELECTED'; });
        let SA = rowModes.filter(v => { return v.type == 'SUMALL'; });
        switch (caseHandler) {
            case 'SKIP':
                break;
            case 'SUMALL':
                for (let i = 0; i < this.visibleData.length; i++) {
                    rowModes.forEach(k => {
                        if (k.type == 'SUMALL') {
                            summy[k.idx] += Number(this.visibleData[i][this.visibleHeaders[k.idx].value].value);
                        }
                    });
                }
                break;
            case 'SUMSELECTED':
                if (SC.length > 0)
                    for (let i = 0; i < selectedRows.length; i++) {
                        SC.forEach(k => {
                            summy[k.idx] += Number(itemList[i][this.visibleHeaders[k.idx].value].value);
                        });
                    }
                break;
            case 'BOTH':
                for (let i = 0; i < this.visibleData.length; i++) {
                    SA.forEach(k => {
                        summy[k.idx] += Number(this.visibleData[i][this.visibleHeaders[k.idx].value].value);
                    });
                }
                if (SC.length > 0)
                    for (let i = 0; i < selectedRows.length; i++) {
                        SC.forEach(k => {
                            summy[k.idx] += Number(itemList[i][this.visibleHeaders[k.idx].value].value);
                        });
                    }
                break;
            default:
                break;
        }
        hdrDiff -= summy.length;
        if (summy.filter(x => !isNaN(x)).length == 0)
            return html ``;
        else
            return html `
			<div class="grid-footer">
				${this.renderEmptyDiv(hdrDiff, 'grid-cell')}
				${summy.map(x => {
                if (isNaN(Number(x)))
                    return this.renderEmptyDiv(1, 'grid-cell');
                else
                    return html `<div class='grid-cell pic-body2'>${x.toLocaleString('en').replace(/,/g, '.')}</div>`;
                // else return html`<div class='grid-cell pic-body2-mono'>${x.toLocaleString('en').replace(/,/g, '.')}</div>`
            })}
			</div>
		`;
    }
    jumpToPage(page) {
        let pageToJump;
        switch (page) {
            case 'first':
                pageToJump = 1;
                break;
            case 'last':
                pageToJump = this.maxPages;
                break;
            case 'next':
                pageToJump = this.currentPage + 1 > this.maxPages
                    ? pageToJump = this.currentPage
                    : this.currentPage + 1;
                break;
            case 'prev':
                pageToJump = this.currentPage === 1
                    ? pageToJump = this.currentPage
                    : this.currentPage - 1;
                break;
            default: pageToJump = page;
        }
        if (pageToJump === this.currentPage)
            return; //do nothing
        this.currentPage = pageToJump;
        this.requestUpdate();
    }
    getPaginationDiv() {
        return html `<div id="pagination-control">
        <i class="fa-solid fa-backward-fast" @click=${(e) => this.jumpToPage('first')}></i>
        <i class="fa-solid fa-backward-step" @click=${(e) => this.jumpToPage('prev')}></i>
        <div id="pagination-select-container"><select id="pagination-select" .selectedIndex=${this.currentPage - 1}  @change=${(e) => {
            let page = Number(e.target.value);
            if (page > 0)
                this.jumpToPage(page);
        }}>
            ${Array(Math.ceil(this.visibleData.length / this.rowsPerPage)).fill(undefined).map((x, i) => {
            return html `<option value=${i + 1} ?selected=${this.currentPage === i + 1} >${i + 1}</option>`;
        })}
        </select></div>
        <i class="fa-solid fa-forward-step" @click=${(e) => this.jumpToPage('next')}></i>
        <i class="fa-solid fa-forward-fast" @click=${(e) => this.jumpToPage('last')}></i>
        </div>`;
    }
    //FIXME implement actions
    renderInfoFooter() {
        var _a, _b, _c;
        if (!this.infofooter)
            return '';
        const dt = this.lastUpdated ? this.lastUpdated.toLocaleTimeString((_a = PIC.getInstance.userConfig.dateLang) !== null && _a !== void 0 ? _a : 'da-DK') : null;
        let hdrDiff = this.data.headers.length - 5;
        let trueVals = Array.from(this._checkboxAll).filter(c => c.submitValue == 'true').length;
        let arl = this.activeRows.length;
        let showRowData = this.pagination
            ? html `<div>Showing <b>${Math.min(((this.currentPage - 1) * this.rowsPerPage) + 1, this.visibleData.length)}-${Math.min(this.currentPage * this.rowsPerPage, this.visibleData.length)}</b> out of <b>${this.visibleData.length}</b> rows</div>`
            : html `<div>Showing <b>${this.visibleData.length}</b> out of <b>${this.data.data.length}</b> rows</div>`;
        const res = html `
			<div class="info-footer pic-body2"
            style=${this.isFirefox && this.gridTable && (((_b = this.gridTable) === null || _b === void 0 ? void 0 : _b.scrollWidth) > ((_c = this.gridTable) === null || _c === void 0 ? void 0 : _c.clientWidth)) ? 'margin-top: 6px;' : ''}>
				${this.checkbox
            ? this.showRowSelectedCount
                ? this.useCheckAsMarked //if checkboxes and useCheckAsMarked then show how many checkboxes are checked
                    ? html `<div><b>${trueVals}</b> Checked</div>`
                    : html `<div><b>${arl}</b> Marked</div>`
                : nothing
            //no checkboxes
            : this.showRowSelectedCount
                ? html `<div><b>${arl}</b> Marked</div>`
                : nothing}
				<!-- <div>
					<select class="pic-body2" id="footerAction" @change=${this.handleSelectAction}>
            			<option value="Action 1">Action 1</option>
            			<option value="Action 2">Action 2</option>
            			<option value="Action 3">Action 3</option>
          			</select>
				</div> -->
				${this.renderEmptyDiv(hdrDiff, '')}
				${this.hideUpdateTimestamp ? nothing : html `<div class="last-updated">Last Updated: <b>${dt}</b></div>`}
				${this.hideRowCount ? nothing : showRowData}
                ${this.pagination ? this.getPaginationDiv() : nothing}
			</div>
		`;
        return res;
    }
    //TODO implement click grouping
    renderTableGroups() {
        return html `
			<div class="tableGroups">

			</div>
		`;
    }
    /* **************** RENDER FUNCTIONALITY eg filtering ********************/
    /** //TODO default localeCompare Function + number
     * Sort the table rows according a compare function
     * @param compareFn Function that takes 2 TData elements and returns a comparable value
     * @returns true if update complete, false if no function passed
     * @deprecated (can be used, but default is now `sortDataBySortTable`)
     */
    sortData(compareFn) {
        if (!compareFn || typeof compareFn !== 'function')
            return false;
        this.visibleData.sort(compareFn);
        this.requestUpdate();
        return true;
    }
    sortDataBySortTable() {
        // let collator = new Intl.Collator('en-Gb', {sensitivity: 'base', numeric: true}).compare;
        let collator = PIC.getInstance.userConfig.dateLang ? new Intl.Collator(PIC.getInstance.userConfig.dateLang, { sensitivity: 'base', numeric: true }).compare : new Intl.Collator('en-Gb', { sensitivity: 'base', numeric: true }).compare;
        for (let i = this.sortTable.sortArray.length - 1; i > -1; i--) {
            let sortItem = this.sortTable.sortArray[i];
            const sortFunc = (a, b) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                let aCell = a[sortItem.headerRef];
                let bCell = b[sortItem.headerRef];
                if (sortItem.sortDir === 'ascending') {
                    return collator((_b = (_a = aCell.properties) === null || _a === void 0 ? void 0 : _a.sortValue) !== null && _b !== void 0 ? _b : (aCell.value + ''), (_d = (_c = bCell.properties) === null || _c === void 0 ? void 0 : _c.sortValue) !== null && _d !== void 0 ? _d : (bCell.value + ''));
                }
                else if (sortItem.sortDir === 'descending') {
                    return collator((_f = (_e = bCell.properties) === null || _e === void 0 ? void 0 : _e.sortValue) !== null && _f !== void 0 ? _f : (bCell.value + ''), (_h = (_g = aCell.properties) === null || _g === void 0 ? void 0 : _g.sortValue) !== null && _h !== void 0 ? _h : (aCell.value + ''));
                }
                else
                    return ((_j = a.internalIndex) !== null && _j !== void 0 ? _j : 0) - ((_k = b.internalIndex) !== null && _k !== void 0 ? _k : 0);
            };
            this.visibleData.sort(sortFunc);
        }
        this.requestUpdate();
        return true;
    }
    sortHeader(header) {
        var _a;
        if (!((_a = header.properties) === null || _a === void 0 ? void 0 : _a.sortable) || !this.sort)
            return;
        //Util.log('stable before', this.sortTable.sortArray)
        this.sortTable.addSort(header.value);
        //Util.log('stable after', this.sortTable.sortArray)
        //Util.log('cursort before', this.visibleHeaders.map(x => `${x.value}-${x.currSort}`))
        this.visibleHeaders.forEach(h => {
            let sortItem = this.sortTable.find(h.value);
            if (sortItem != null)
                h.currSort = sortItem.sortDir;
            else
                h.currSort = '';
        });
        //Util.log('cursort after', this.visibleHeaders.map(x => `${x.value}-${x.currSort}`))
        this.sortDataBySortTable();
    }
    addActiveFilter(value, header, filterType_, handleBlank_) {
        const addToFilter = () => {
            var _a;
            //FIXME will now reset visibledata each change, might be slow
            let resetVisibleData_ = true;
            let handleBlank = handleBlank_ !== null && handleBlank_ !== void 0 ? handleBlank_ : false;
            if (!header)
                this.activeFilters.main = value;
            else if (filterType_) {
                let hdrFilter = this.activeFilters.filters.get(header.value);
                resetVisibleData_ = hdrFilter ? true : false;
                this.activeFilters.filters.set(header.value, { value: value, filterType: filterType_ });
            }
            else
                this.activeFilters.filters.set(header.value, { value: value, filterType: (_a = header.filter) !== null && _a !== void 0 ? _a : 'startsWith' });
            this.dispatchEvent(new CustomEvent('filterchange', {
                detail: {
                    header: header ? header : null,
                    value: value,
                    resetVisibleData: resetVisibleData_,
                    handleBlank: handleBlank
                }
            }));
        };
        this.addDebouncedFunction(addToFilter, 300);
    }
    getSavedFilters() {
        if (PIC.getInstance.storage.getItem('webTableSavedFilters-' + this.id_, 'session') || PIC.getInstance.storage.getItem('webTable-SavedFilters-' + this.id_, 'internal')) {
            this.dispatchEvent(new CustomEvent('loadsavedfilters', {
                detail: { value: 'filters' }
            }));
        }
        ;
    }
    /**
     * Set the check value of a given table, also set in original table to counter
     * a reset that happens when fx all filters are removed.
     * It would reset to the original value it has in the table, instead of the latest
     * supplied user given value
     * @param row
     * @param iValue
     */
    setCheckedRow(row, iValue) {
        row.checked = iValue; //assume this.visibleData here...
        let dataRow = this.data.data.find(x => x.internalIndex === row.internalIndex);
        if (dataRow)
            dataRow.checked = row.checked;
    }
    setCheckbox(rows, value) {
        var _a;
        let curRow;
        let checkbox;
        if (Array.isArray(rows)) {
            for (const row of rows) {
                checkbox = this.getRowCheckBox(row[0]);
                if (!checkbox)
                    continue;
                curRow = this.visibleData[this.getRowIdxFromInternal(row[0])];
                this.setCheckedRow(curRow, !curRow.checked);
                checkbox.setChecked((_a = curRow.checked) !== null && _a !== void 0 ? _a : false);
            }
            this.requestUpdate();
        }
        else if (value !== null) {
            checkbox = this.getRowCheckBox(this.getRowIdxFromInternal(rows.internalIndex));
            checkbox === null || checkbox === void 0 ? void 0 : checkbox.setChecked(value);
            this.setCheckedRow(rows, value);
            this.requestUpdate();
        }
        if (this.boundCanvas) {
            this.sendCheckedRowsToBoundCanvas();
        }
    }
    sendPolyToBoundCanvas(polyLines) {
        if (!Array.isArray(polyLines))
            polyLines = [polyLines];
        let drawAction = new PICAction();
        drawAction.actionOptions = {
            type: 'command',
            id: 'drawAction',
            event: 'dispatchEvent',
            params: [],
            parms: '',
            eventFnc: () => { },
            cmdData: {
                id: this.boundCanvasId,
                cmd: 'picaction',
                event: 'update-static-data',
                data: {
                    id: this.id_,
                    lines: polyLines,
                    action: 'set'
                }
            }
        };
        drawAction.triggerEvent();
    }
    sendCheckedRowsToBoundCanvas() {
        const checkedRows = this.visibleData.filter(data => data.checked);
        const polyLines = checkedRows.map(x => {
            var _a, _b, _c;
            let geometry = (_a = x.__properties__) === null || _a === void 0 ? void 0 : _a['geometry']; //[[x,y],...]
            if (!geometry)
                return null;
            return {
                name: `from${this.id}`,
                color: (_c = (_b = x.__properties__) === null || _b === void 0 ? void 0 : _b['geometry_color']) !== null && _c !== void 0 ? _c : 'red',
                id: `from${this.id}`,
                coordinates: {
                    unit: 'mm',
                    coordinates: JSON.parse(geometry)
                }
            };
        }).filter(x => x != null);
        this.sendPolyToBoundCanvas(polyLines);
    }
    sendEditableFieldsToBoundCanvas() {
        let testCoords = [];
        this.visibleData.forEach(row => {
            var _a;
            let curCoord = [];
            for (let hdr = 0; hdr < this.visibleHeaders.length; hdr++) {
                let inputId = `#inputCell-${row.internalIndex}-${hdr + 3}`;
                let inputFld = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(inputId);
                if (inputFld)
                    curCoord.push(parseInt(inputFld.value)); // maybe change this to Number(etc)?
            }
            if (curCoord.length > 0)
                testCoords.push(curCoord);
        });
        let pl = {
            name: `from${this.id}`,
            color: this.boundCanvasColor,
            id: `from${this.id}`,
            coordinates: {
                unit: 'mm',
                coordinates: testCoords
            }
        };
        this.sendPolyToBoundCanvas(pl);
    }
    getRowCheckBox(idx) {
        var _a;
        return idx < 0 ? null : (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`#grid-checkbox-checkbox-${idx}`);
    }
    focus() {
    }
    validateSubmit() {
        return true;
    }
};
_PICWebTable_activeRows_ = new WeakMap();
__decorate([
    property({ attribute: false })
], PICWebTable.prototype, "responseTabledata", void 0);
__decorate([
    state()
], PICWebTable.prototype, "visibleHeaders", void 0);
__decorate([
    state()
], PICWebTable.prototype, "invisibleHeaders", void 0);
__decorate([
    state()
], PICWebTable.prototype, "visibleData", void 0);
__decorate([
    property({ type: String })
], PICWebTable.prototype, "tableTitle", void 0);
__decorate([
    state()
], PICWebTable.prototype, "lastUpdated", void 0);
__decorate([
    state()
], PICWebTable.prototype, "filterRowIsVisible", void 0);
__decorate([
    queryAll('.grid-cell > pic-checkbox:not(.grid-checkbox)')
], PICWebTable.prototype, "_checkboxAll", void 0);
__decorate([
    query('.info-filter > input')
], PICWebTable.prototype, "headerFilterMain", void 0);
__decorate([
    query('.tableFiltersButton')
], PICWebTable.prototype, "filtersToggleBtn", void 0);
__decorate([
    query('#filterContainer')
], PICWebTable.prototype, "filterContainer", void 0);
__decorate([
    queryAll('input.filterselectmenuiteminput')
], PICWebTable.prototype, "headerFilters", void 0);
__decorate([
    query('#tableContext')
], PICWebTable.prototype, "contextMenu", void 0);
__decorate([
    query('.grid-table')
], PICWebTable.prototype, "gridTable", void 0);
__decorate([
    query('.grid-body')
], PICWebTable.prototype, "gridBody", void 0);
__decorate([
    query('.lazy-scrollbar.thumb')
], PICWebTable.prototype, "lazyThumb", void 0);
__decorate([
    query('.lazy-hscroll')
], PICWebTable.prototype, "ffHScroll", void 0);
__decorate([
    query('.lazy-hscroll-thumb')
], PICWebTable.prototype, "ffHScrollThumb", void 0);
__decorate([
    queryAll('.grid-table .grid-header > *')
], PICWebTable.prototype, "gridTableHeader", void 0);
__decorate([
    query('.grid-table > .resize-overlay')
], PICWebTable.prototype, "overlay", void 0);
__decorate([
    queryAll('.filter-row input')
], PICWebTable.prototype, "filterRowInputs", void 0);
__decorate([
    queryAll('.info-header .tableBtn')
], PICWebTable.prototype, "tableBtns", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':checkbox' })
], PICWebTable.prototype, "checkbox", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':tree' })
], PICWebTable.prototype, "tree", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':dragndrop' })
], PICWebTable.prototype, "dragndrop", void 0);
__decorate([
    property({ attribute: ':filter' })
], PICWebTable.prototype, "filter", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':infofooter' })
], PICWebTable.prototype, "infofooter", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':sumfooter' })
], PICWebTable.prototype, "sumfooter", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':hideupdatetimestamp' })
], PICWebTable.prototype, "hideUpdateTimestamp", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':hiderowcount' })
], PICWebTable.prototype, "hideRowCount", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':infoheader' })
], PICWebTable.prototype, "infoheader", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':sort' })
], PICWebTable.prototype, "sort", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':src' })
], PICWebTable.prototype, "src", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':allownewrows' })
], PICWebTable.prototype, "allowNewRows", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':groups' })
], PICWebTable.prototype, "groups", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':search' })
], PICWebTable.prototype, "search", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':compact' })
], PICWebTable.prototype, "compactMode", void 0);
__decorate([
    property({ converter: stringDecoderLC, attribute: ':rowdensity' })
], PICWebTable.prototype, "rowDensity", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':compactheader' })
], PICWebTable.prototype, "compactHeaderMode", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':highvisibilityheaders' })
], PICWebTable.prototype, "highVisibilityHeaders", void 0);
__decorate([
    property({ converter: arrayDecoder, attribute: ':export' })
], PICWebTable.prototype, "export", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':dataRootName' })
], PICWebTable.prototype, "dataRootName", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':savefilters' })
], PICWebTable.prototype, "saveFilters", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':autowidth' })
], PICWebTable.prototype, "autowidth", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':selectionmode' })
], PICWebTable.prototype, "selectionMode", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':showfilters' })
], PICWebTable.prototype, "filterEnabled", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':showrowselectedcount' })
], PICWebTable.prototype, "showRowSelectedCount", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':use-check-as-marked' })
], PICWebTable.prototype, "useCheckAsMarked", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':savetoggledcolumns' })
], PICWebTable.prototype, "saveToggledColumns", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':userest' })
], PICWebTable.prototype, "useRest", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':resizecolumns' })
], PICWebTable.prototype, "resizeColumns", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':editable' })
], PICWebTable.prototype, "editable", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':readonly' })
], PICWebTable.prototype, "readOnly", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':addremoverows' })
], PICWebTable.prototype, "addRemoveRows", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':boundcanvasid' })
], PICWebTable.prototype, "boundCanvas", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':customFilter' })
], PICWebTable.prototype, "valuesFilters", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':filterType' })
], PICWebTable.prototype, "filterType", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':filterEnable' })
], PICWebTable.prototype, "filterEnable", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':data-type' })
], PICWebTable.prototype, "dataType", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':allowdblclick' })
], PICWebTable.prototype, "allowDblclick", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':hideheadertooltip' })
], PICWebTable.prototype, "hideheadertooltip", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':pagination' })
], PICWebTable.prototype, "pagination", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':whitespace' })
], PICWebTable.prototype, "whitespace", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':autofittable' })
], PICWebTable.prototype, "autoFitTable", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: ':islookup' })
], PICWebTable.prototype, "isLookUp", void 0);
__decorate([
    property({ type: String, attribute: 'minheight' })
], PICWebTable.prototype, "minheight", void 0);
__decorate([
    property({ type: Number, attribute: ':pagesize', converter(value) {
            return Number(value) > 0 && Number(value) < 2000 ? Number(value) : 100;
        }, })
], PICWebTable.prototype, "rowsPerPage", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: ':alternatelinecolor' })
], PICWebTable.prototype, "alternateLineColor", null);
__decorate([
    property({ converter: stringDecoder, attribute: ':scrollToRow' })
], PICWebTable.prototype, "scrollToRow", null);
__decorate([
    property({ converter: numberDecoder, attribute: ':refreshtimer' })
], PICWebTable.prototype, "refreshTimer", null);
__decorate([
    property({ type: String, attribute: 'height' })
], PICWebTable.prototype, "height", null);
PICWebTable = __decorate([
    customElement('pic-webtable')
], PICWebTable);
export { PICWebTable };
//# sourceMappingURL=PICWebTable.js.map